import { useCallback, useEffect, useMemo, useRef, useState, type DragEvent, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import QRCode from 'qrcode';
import {
  adminApi, api, ApiError, fmt, formatCustomFieldValues, lineOaApi, wristbandReceivedLabel, PAYMENT_PROVIDERS, WRISTBAND_COLORS,
  type AdminCouponDto, type AdminOrderDto, type AdminRevenueSeriesPointDto, type AdminStatsDto, type AdminTicketDto,
  type CheckinLogDto, type CheckinTokenDto, type CheckoutSettingsDto, type ComingSoonDto, type ComingSoonStateDto, type ContentDto, type ContentSection, type CouponInput, type DeliveryChannel, type EarlyBirdDto,
  type TicketDeliveryDto, type TicketDeliveryMode,
  type PreviewPassDto,
  type EventLineOaDto, type LineOaAuditEntryDto, type LineOaCheckDto, type LineOaStatusDto, type LineOaVerifyDto,
  type OrderStatus, type NotificationDeliveryDto, type PaymentAccountDto, type PaymentAccountInput, type PaymentProvider,
  type ResellerCommissionType, type ResellerPartnerInput, type ResellerReportRowDto,
  salesChannelLineRouteOf,
  type SalesChannelDailyDto, type SalesChannelDto, type SalesChannelLineRoute,
  type SalesChannelPlatform, type SalesChannelReportDto,
  type OfflineBatchDto,
  type OfflineCardDto,
  type OfflineCardSkipDto,
  type OfflineSummaryDto,
  type RevenueSeriesRange, type TicketCustomFieldDto, type TicketInput, type WristbandBinding, type WristbandColor, type WristbandDto,
} from '../api';
import ImageCropModal from '../components/ImageCropModal';
import QrScanner from '../components/QrScanner';
import RevenueTrendChart from '../components/RevenueTrendChart';
import { usePageTitle } from '../hooks/usePageTitle';
import { usePolling } from '../hooks/usePolling';
import { decodeQrFromImageFile } from '../lib/qrDecode';
import { parseThaiMerchantQr } from '../lib/thaiMerchantQr';
import ConcertCalendar from './ConcertCalendar';

type MainTab = 'overview' | 'review' | 'checkin' | 'staff' | 'tickets' | 'wristbands' | 'payments' | 'delivery' | 'lineoa' | 'channels' | 'content' | 'calendar';
/**
 * 'checked_in' isn't a real order status — it's a client-side filter for status=paid + checkedInAt
 * set. 'problem' isn't a status either — it's the server's `bucket=problem` filter (rejected orders
 * + awaiting_verification orders with a definitive-negative latest slip check).
 */
type ReviewSubTab = 'awaiting_verification' | 'problem' | 'paid' | 'checked_in' | 'pending_payment' | 'expired' | '';

const NAV_SALES: { key: MainTab; label: string; icon: ReactNode }[] = [
  { key: 'overview', label: 'ภาพรวม', icon: <IconGrid /> },
  { key: 'review', label: 'ตรวจสอบสลิป', icon: <IconClock /> },
];
const NAV_OPS: { key: MainTab; label: string; icon: ReactNode }[] = [
  { key: 'checkin', label: 'เช็กอินหน้างาน', icon: <IconGate /> },
  { key: 'staff', label: 'ผู้ใช้ & Token', icon: <IconKey /> },
  { key: 'tickets', label: 'จัดการตั๋ว', icon: <IconTicket /> },
  { key: 'wristbands', label: 'Wristband', icon: <IconWristband /> },
  { key: 'payments', label: 'บัญชีรับเงิน', icon: <IconWallet /> },
  { key: 'delivery', label: 'การส่งมอบตั๋ว', icon: <IconSend /> },
  { key: 'lineoa', label: 'LINE OA ของผู้จัด', icon: <IconUsers /> },
  { key: 'channels', label: 'ช่องทางการขาย', icon: <IconSend /> },
  { key: 'content', label: 'หน้า Landing Page', icon: <IconMonitor /> },
  { key: 'calendar', label: 'ปฏิทินคอนเสิร์ต', icon: <IconList /> },
];

const REVIEW_SUB_TABS: { key: ReviewSubTab; label: string }[] = [
  { key: 'awaiting_verification', label: 'รอตรวจสลิป' },
  { key: 'problem', label: 'สลิปมีปัญหา' },
  { key: 'paid', label: 'ชำระแล้ว' },
  { key: 'checked_in', label: 'รับ Wristband แล้ว' },
  { key: 'pending_payment', label: 'รอโอน' },
  { key: 'expired', label: 'หมดเวลา' },
  { key: '', label: 'ทั้งหมด' },
];

/** `checked_in` and `problem` aren't real order statuses — they translate to `checkedIn`/`bucket` query params instead. */
function reviewSubTabParams(subTab: ReviewSubTab): { status?: string; checkedIn?: boolean; bucket?: 'problem' } {
  if (subTab === 'checked_in') return { checkedIn: true };
  if (subTab === 'problem') return { bucket: 'problem' };
  return { status: subTab || undefined };
}

const PAGE_META: Record<MainTab, { title: string; sub: string; width: string }> = {
  overview: { title: 'ภาพรวม', sub: 'สรุปยอดขายและสถานะออร์เดอร์', width: 'w-wide' },
  review: { title: 'ตรวจสอบสลิป', sub: 'ตรวจสอบสลิปโอนเงินและจัดการออร์เดอร์ตามสถานะ', width: 'w-normal' },
  checkin: { title: 'เช็กอินหน้างาน', sub: 'สแกน QR หรือกรอกรหัสบัตรเพื่อยืนยันเข้างาน', width: 'w-narrow' },
  staff: { title: 'ผู้ใช้ & Token', sub: 'สร้าง Token ให้พนักงานเช็กอิน และดูสถิติการเช็กอินของแต่ละคน', width: 'w-normal' },
  tickets: { title: 'จัดการตั๋ว', sub: 'ราคา โควตา และการเปิด/ปิดการขายแต่ละประเภท', width: 'w-normal' },
  wristbands: { title: 'Wristband', sub: 'สร้างริสแบนด์และผูกกับประเภทตั๋ว', width: 'w-normal' },
  payments: { title: 'บัญชีรับเงิน', sub: 'บัญชีที่ใช้สร้าง QR รับเงินและตรวจสลิปของงานนี้', width: 'w-normal' },
  delivery: { title: 'การส่งมอบตั๋ว', sub: 'ผู้ซื้อจะเห็นตั๋วเมื่อไหร่ และระบบส่งอะไรตามไปให้บ้างหลังชำระเงิน', width: 'w-normal' },
  lineoa: { title: 'LINE OA ของผู้จัด', sub: 'ปุ่มเพิ่มเพื่อน OA ของผู้จัด และการเชื่อมต่อ LINE ของงานนี้', width: 'w-normal' },
  channels: { title: 'ช่องทางการขาย', sub: 'สร้างลิงก์ให้ผู้ลงคอนเทนต์แต่ละคน แล้วดูว่าใครพาคนมาซื้อ', width: 'w-normal' },
  content: { title: 'หน้า Landing Page', sub: 'แก้ไขข้อมูลที่แสดงบนหน้าขายบัตร', width: 'w-md' },
  calendar: { title: 'ปฏิทินคอนเสิร์ต', sub: 'ดูตารางงานคอนเสิร์ตอื่นเพื่อเลี่ยงวันชนกัน (ข้อมูลภายใน ไม่เปิดสาธารณะ)', width: 'w-wide' },
};

const STATUS_LABEL: Record<string, string> = {
  pending_payment: 'รอโอน',
  awaiting_verification: 'รอตรวจสลิป',
  paid: 'ชำระแล้ว',
  expired: 'หมดเวลา',
  cancelled: 'ยกเลิก',
  rejected: 'ปฏิเสธ',
};

/**
 * TICK-137 — a `duplicate` needs three different sentences, not one.
 *
 * The verdict alone never told staff what to check. "ระบบตรวจซ้ำเอง" and "อาจมีคนใช้สลิปนี้ไปแล้ว"
 * are opposite situations that used to render identically, and the wrong one of them cost a paying
 * customer their ticket. Each line says what happened and what to do about it.
 */
const DUPLICATE_REASON_LABEL: Record<string, string> = {
  cross_order: 'สลิปซ้ำ — ใบเสร็จนี้ถูกใช้กับออเดอร์อื่นในระบบแล้ว',
  self_inflicted: 'ระบบตรวจสลิปใบนี้ซ้ำเอง (ครั้งก่อนไม่ได้คำตอบกลับมา) — ผู้ให้บริการจึงนับเป็นซ้ำ ไม่ใช่ลูกค้าส่งซ้ำ',
  unattributable: 'ผู้ให้บริการเคยเห็นสลิปนี้มาก่อน และไม่ได้เกิดจากการตรวจซ้ำของระบบ — ตรวจกับลูกค้าก่อนอนุมัติ',
};

/** Why an order is stuck on `awaiting_verification` — mirrors Superadmin's SlipStatusBadge labels. */
const SLIP_VERIFICATION_REASON_LABEL: Record<string, string> = {
  duplicate: 'ผู้ให้บริการแจ้งว่าเคยเห็นสลิปนี้แล้ว',
  mismatch_amount: 'ยอดเงินในสลิปไม่ตรงกับยอดที่ต้องชำระ',
  mismatch_receiver: 'บัญชีผู้รับในสลิปไม่ตรงกับบัญชีของงาน',
  invalid_slip: 'สลิปไม่ถูกต้อง / อ่านไม่ได้',
  error: 'ผู้ให้บริการตรวจสลิปไม่ตอบกลับ / เกิดข้อผิดพลาด',
  quota_exceeded: 'โควต้าตรวจสลิปของผู้ให้บริการเต็ม',
  pending: 'ผู้ให้บริการยังไม่คืนผลตรวจ',
};

/**
 * The confirm dialog for reject used one generic message regardless of why the order is stuck —
 * for a genuinely unrecoverable reason (duplicate slip, wrong receiver) that's fine, but for a
 * reason that could just be a bad vendor read (mismatch_amount, invalid_slip) a blind "reject and
 * release the wristband" is the wrong first move: re-verifying is free and reversible, releasing
 * the wristband to the pool is not (someone else can claim it before the mistake is caught).
 */
function rejectConfirmMessage(v: { status: string } | null | undefined): string {
  const reason = v ? SLIP_VERIFICATION_REASON_LABEL[v.status] : undefined;
  if (v?.status === 'duplicate' || v?.status === 'mismatch_receiver') {
    return `${reason} — ปฏิเสธและปล่อยบัตรกลับเข้าระบบ?`;
  }
  if (v?.status === 'mismatch_amount' || v?.status === 'invalid_slip') {
    return `ระบบอ่านสลิปว่า "${reason}" — แนะนำให้กด "รีตรวจสลิป" ก่อน เผื่อผลตรวจเดิมคลาดเคลื่อน\n\nยืนยันปฏิเสธและปล่อยบัตรกลับเข้าระบบ?`;
  }
  return 'ปฏิเสธสลิปนี้และปล่อยบัตรกลับเข้าระบบ?';
}

/**
 * Approve is only safe when the vendor genuinely hasn't answered yet (no attempt, or a
 * transport-level error/timeout/quota issue) — a definitive negative verdict like
 * `mismatch_receiver` means the vendor DID answer, and an admin blindly approving that is exactly
 * the hole this gate closes. Reject stays available regardless, since it's never the risky action.
 * `vendor: 'manual'` (RECEIVER_UNRESOLVED — no expected account configured to check against) is a
 * config gap, not a vendor-confirmed fraud signal, so it doesn't block the button either.
 *
 * TICK-137 — `duplicate` is the exception the 11 Sep 2026 incident was made of. A vendor call of
 * ours that timed out is already in the vendor's duplicate ledger, so our own retry comes back
 * `duplicate` without anyone having reused anything. The server now distinguishes the two and says
 * which this is in `duplicateIsCrossOrder`; gate on its answer rather than on a second copy of the
 * rule. That second copy is why fixing the server guard alone did not help: the button staff needed
 * was still never rendered. Fails closed — anything but an explicit `false` keeps it hidden.
 */
function slipVerificationAllowsManualAction(
  v: { vendor: string; status: string; duplicateProvenance?: string } | null | undefined,
): boolean {
  if (!v || v.vendor === 'manual') return true;
  // `unattributable` is approvable too — it is held, not condemned — but the reason line below
  // tells staff to check the slip with the customer first. Fails closed on an unknown provenance.
  if (v.status === 'duplicate') {
    return v.duplicateProvenance === 'self_inflicted' || v.duplicateProvenance === 'unattributable';
  }
  return v.status === 'error' || v.status === 'quota_exceeded' || v.status === 'pending';
}

/**
 * Re-verify only ever asks the vendor again — it can't approve anything on its own, so unlike
 * `slipVerificationAllowsManualAction` it has nothing to gate against a stale/wrong verdict. Gating
 * it the same way as approve was itself the bug: a definitive negative like `mismatch_amount` is
 * exactly the case where re-asking the vendor matters most, since that verdict can be stale (e.g.
 * computed by verification logic since fixed) with no other way for staff to recover the order.
 */
function slipVerificationAllowsReverify(hasSlip: boolean): boolean {
  return hasSlip;
}

/** Mirrors the .status-chip color groups in styles.css so the overview charts read as the same system. */
const STATUS_COLOR: Record<string, string> = {
  paid: '#9fdcb2',
  awaiting_verification: '#e8d49a',
  expired: '#d99a9a',
  cancelled: '#d99a9a',
  rejected: '#d99a9a',
};

/* ---------- icons ---------- */
function IconGrid({ size = 17 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="7" rx="1"></rect><rect x="14" y="3" width="7" height="7" rx="1"></rect>
      <rect x="14" y="14" width="7" height="7" rx="1"></rect><rect x="3" y="14" width="7" height="7" rx="1"></rect>
    </svg>
  );
}
function IconClock({ size = 17 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="9"></circle><polyline points="12 7 12 12 15 14"></polyline>
    </svg>
  );
}
function IconGate({ size = 17 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 7V5a2 2 0 0 1 2-2h2"></path><path d="M17 3h2a2 2 0 0 1 2 2v2"></path>
      <path d="M21 17v2a2 2 0 0 1-2 2h-2"></path><path d="M7 21H5a2 2 0 0 1-2-2v-2"></path>
      <line x1="7" y1="12" x2="17" y2="12"></line>
    </svg>
  );
}
function IconTicket({ size = 17 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2Z"></path>
      <path d="M13 5v14" strokeDasharray="2 3"></path>
    </svg>
  );
}
function IconMonitor({ size = 17 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="3" width="20" height="14" rx="2"></rect><line x1="8" y1="21" x2="16" y2="21"></line>
      <line x1="12" y1="17" x2="12" y2="21"></line>
    </svg>
  );
}
function IconWristband({ size = 17 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
      <path d="M7 8c-2.5 1-4 2.6-4 4s1.5 3 4 4"></path>
      <path d="M17 8c2.5 1 4 2.6 4 4s-1.5 3-4 4"></path>
      <rect x="7" y="6" width="10" height="12" rx="2"></rect>
      <line x1="10" y1="6" x2="10" y2="18"></line>
      <line x1="14" y1="6" x2="14" y2="18"></line>
    </svg>
  );
}
function IconWallet({ size = 17 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 12V7a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-5Z"></path>
      <path d="M16 12h4"></path>
      <circle cx="17" cy="12" r="0.8" fill="currentColor" stroke="none"></circle>
    </svg>
  );
}
function IconLogout({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path><polyline points="16 17 21 12 16 7"></polyline>
      <line x1="21" y1="12" x2="9" y2="12"></line>
    </svg>
  );
}
function IconSearch({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line>
    </svg>
  );
}
function IconDownload({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline>
      <line x1="12" y1="15" x2="12" y2="3"></line>
    </svg>
  );
}
function IconEye({ size = 17 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z"></path><circle cx="12" cy="12" r="3"></circle>
    </svg>
  );
}
function IconEyeOff({ size = 17 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9.9 5.1A10.4 10.4 0 0 1 12 5c6.5 0 10 7 10 7a17 17 0 0 1-3.2 4.1M6.6 6.6C4 8.3 2 12 2 12s3.5 7 10 7a10 10 0 0 0 4.2-.9"></path>
      <path d="M9.9 14.1a3 3 0 0 0 4.2-4.2"></path><line x1="2" y1="2" x2="22" y2="22"></line>
    </svg>
  );
}
function IconHand({ size = 17 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
      <path d="M8 12V6a1.5 1.5 0 0 1 3 0v5"></path>
      <path d="M11 11V4.5a1.5 1.5 0 0 1 3 0V11"></path>
      <path d="M14 11.5V5.5a1.5 1.5 0 0 1 3 0V12"></path>
      <path d="M17 12V9a1.5 1.5 0 0 1 3 0v6c0 3.9-3.1 7-7 7h-2c-2.2 0-3.5-.8-4.8-2.4l-3-3.7a1.4 1.4 0 0 1 2-2L8 14"></path>
    </svg>
  );
}
function IconX({ size = 17 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line>
    </svg>
  );
}
function IconCheck({ size = 17 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12"></polyline>
    </svg>
  );
}
function IconInfo({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"></circle><line x1="12" y1="16" x2="12" y2="12"></line><line x1="12" y1="8" x2="12.01" y2="8"></line>
    </svg>
  );
}
function IconList({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
      <line x1="8" y1="6" x2="21" y2="6"></line><line x1="8" y1="12" x2="21" y2="12"></line><line x1="8" y1="18" x2="21" y2="18"></line>
      <line x1="3" y1="6" x2="3.01" y2="6"></line><line x1="3" y1="12" x2="3.01" y2="12"></line><line x1="3" y1="18" x2="3.01" y2="18"></line>
    </svg>
  );
}
function IconUsers({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 18V5l12-2v13"></path><circle cx="6" cy="18" r="3"></circle><circle cx="18" cy="16" r="3"></circle>
    </svg>
  );
}
function IconKey({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="7.5" cy="15.5" r="4.5"></circle><path d="m10.5 12.5 8-8"></path><path d="m16 7 2 2"></path><path d="m19 4 2 2"></path>
    </svg>
  );
}
function IconSections({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="7" rx="1"></rect><rect x="3" y="14" width="18" height="7" rx="1"></rect>
    </svg>
  );
}
function IconTrash({ size = 15 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 6h18"></path><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
    </svg>
  );
}
function IconPlus({ size = 15 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line>
    </svg>
  );
}
function IconImageOff({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="2"></rect><circle cx="9" cy="9" r="2"></circle>
      <path d="m21 15-3.1-3.1a2 2 0 0 0-2.8 0L6 21"></path><line x1="3" y1="3" x2="21" y2="21"></line>
    </svg>
  );
}
function IconPhoto({ size = 26 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="2"></rect><circle cx="9" cy="9" r="2"></circle>
      <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"></path>
    </svg>
  );
}
function IconCrop({ size = 15 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 2v14a2 2 0 0 0 2 2h14"></path><path d="M18 22V8a2 2 0 0 0-2-2H2"></path>
    </svg>
  );
}
function IconGrip({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
      <circle cx="9" cy="6" r="1.4"></circle><circle cx="9" cy="12" r="1.4"></circle><circle cx="9" cy="18" r="1.4"></circle>
      <circle cx="15" cy="6" r="1.4"></circle><circle cx="15" cy="12" r="1.4"></circle><circle cx="15" cy="18" r="1.4"></circle>
    </svg>
  );
}
function IconChevronUp({ size = 14 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="18 15 12 9 6 15"></polyline>
    </svg>
  );
}
function IconChevronDown({ size = 14 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="6 9 12 15 18 9"></polyline>
    </svg>
  );
}
function IconCopy({ size = 14 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="9" y="9" width="13" height="13" rx="2"></rect><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
    </svg>
  );
}
function IconExternalLink({ size = 14 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
      <polyline points="15 3 21 3 21 9"></polyline><line x1="10" y1="14" x2="21" y2="3"></line>
    </svg>
  );
}

/** TICK-045 — why a resend could not be attempted, in the operator's language. */
const RESEND_SKIP_REASON: Record<string, string> = {
  NO_RECIPIENT: 'ออเดอร์นี้ไม่มีอีเมล/เบอร์ที่จะส่งไป',
  CHANNEL_DISABLED: 'ช่องทางนี้ถูกปิดอยู่ในระบบ',
  ALREADY_QUEUED: 'กำลังรอส่งอยู่แล้ว ไม่ต้องกดซ้ำ',
};

function IconSend({ size = 15 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 2 11 13" /><path d="M22 2 15 22l-4-9-9-4Z" />
    </svg>
  );
}

function IconRefresh({ size = 15 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="23 4 23 10 17 10"></polyline><polyline points="1 20 1 14 7 14"></polyline>
      <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
    </svg>
  );
}

/* ---------- shared page shell ---------- */
function PageHead({ tab, right }: { tab: MainTab; right?: ReactNode }) {
  const meta = PAGE_META[tab];
  return (
    <div className="admin-page-head">
      <div>
        <h1>{meta.title}</h1>
        <div className="sub">{meta.sub}</div>
      </div>
      {right}
    </div>
  );
}

function Group({ icon, title, sub, right, children }: {
  icon: ReactNode; title: string; sub: string;
  /** Optional status slot on the head's trailing edge — a badge, not a control. */
  right?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="admin-group">
      <div className="admin-group-head">
        <div className="admin-group-icon">{icon}</div>
        <div style={{ flex: 1, minWidth: 0 }}><div className="admin-group-title">{title}</div><div className="admin-group-sub">{sub}</div></div>
        {right}
      </div>
      <div className="admin-group-body">{children}</div>
    </section>
  );
}

/* ---------- overview ---------- */
function StatTile({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="admin-substat-card">
      <div className="label">{label}</div>
      <div className="value">{value}</div>
      {sub && <div style={{ fontSize: 12, color: 'var(--dim)', marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

function ZoneBar({ zone }: { zone: AdminStatsDto['zones'][number] }) {
  const pct = zone.capacity > 0 ? Math.min(100, Math.round((zone.sold / zone.capacity) * 100)) : 0;
  return (
    <div className="zone-bar-row">
      <div className="zone-bar-head">
        <span>
          {zone.nameTh || zone.nameEn} <span style={{ color: 'var(--dim)' }}>({zone.id})</span>
          {pct >= 85 && <span className="zone-chip hot">ใกล้เต็ม</span>}
          {pct >= 50 && pct < 85 && <span className="zone-chip brisk">ขายดี</span>}
        </span>
        <span style={{ color: 'var(--muted)' }}>
          {zone.sold} / {zone.capacity} · {pct}% · {fmt(zone.revenue)}
        </span>
      </div>
      <svg viewBox="0 0 100 8" preserveAspectRatio="none" className="zone-bar-svg">
        <rect x="0" y="0" width="100" height="8" rx="4" fill="var(--panel-border)" />
        <rect x="0" y="0" width={pct} height="8" rx="4" fill="var(--fg)" />
      </svg>
    </div>
  );
}

function StatsPanel({ stats, error }: { stats: AdminStatsDto | null; error: string }) {
  if (error && !stats) return <div className="form-error">{error}</div>;
  if (!stats) return <div style={{ color: 'var(--muted)', fontSize: 14 }}>กำลังโหลด…</div>;

  const totalSold = stats.zones.reduce((s, z) => s + z.sold, 0);
  const totalCapacity = stats.zones.reduce((s, z) => s + z.capacity, 0);
  const soldPct = totalCapacity > 0 ? (totalSold / totalCapacity) * 100 : 0;
  const checkinPct = stats.checkin.totalPaid > 0 ? (stats.checkin.checkedIn / stats.checkin.totalPaid) * 100 : 0;
  const avgPerTicket = totalSold > 0 ? stats.revenuePaid / totalSold : 0;
  const statusEntries = Object.entries(stats.ordersByStatus).filter(([status, cnt]) => status !== 'pending_payment' && cnt > 0);
  const totalOrders = statusEntries.reduce((sum, [, cnt]) => sum + cnt, 0);
  const zonesByRevenue = [...stats.zones].sort((a, b) => b.revenue - a.revenue);
  const maxZoneRevenue = Math.max(1, ...stats.zones.map((z) => z.revenue));

  const donutR = 54;
  const donutC = 2 * Math.PI * donutR;
  const donutOffset = donutC * (1 - soldPct / 100);

  return (
    <>
      {error && <div className="form-error">{error}</div>}
      <div className="admin-revenue-grid">
        <div className="admin-revenue-card">
          <div className="label">รายได้รวม (ชำระแล้ว)</div>
          <div className="value">{fmt(stats.revenuePaid)}</div>
          {totalSold > 0 && <div className="sub">{totalSold.toLocaleString()} ใบ · เฉลี่ย {fmt(Math.round(avgPerTicket))}/ใบ</div>}
        </div>
        <div className="admin-revenue-card">
          <div className="label">รายได้วันนี้</div>
          <div className="value">{fmt(stats.today.revenue)}</div>
          <div className="sub">{stats.today.ordersPaid} ออร์เดอร์</div>
        </div>
        <div className="admin-revenue-card">
          <div className="label">ตั๋วขายแล้ว</div>
          <div className="value">{totalSold.toLocaleString()} <span style={{ fontSize: 15, color: 'var(--dim)', fontWeight: 500 }}>/ {totalCapacity.toLocaleString()}</span></div>
          <div className="sub">{totalCapacity > 0 ? `${soldPct.toFixed(1)}% ของความจุทั้งหมด` : 'ยังไม่มีบัตร'}</div>
        </div>
        <div className="admin-revenue-card">
          <div className="label">เช็กอินแล้ว</div>
          <div className="value">{stats.checkin.checkedIn.toLocaleString()} <span style={{ fontSize: 15, color: 'var(--dim)', fontWeight: 500 }}>/ {stats.checkin.totalPaid.toLocaleString()}</span></div>
          <div className="sub">{stats.checkin.totalPaid > 0 ? `${checkinPct.toFixed(1)}% ของตั๋วที่ชำระแล้ว` : '—'}</div>
        </div>
      </div>

      <div className="stats-split-grid">
        <div className="admin-group">
          <div className="admin-group-body">
            <div className="stats-section-label">สัดส่วนตั๋วที่ขายแล้ว</div>
            {stats.zones.length === 0 ? (
              <div style={{ color: 'var(--muted)', fontSize: 14 }}>ยังไม่มีบัตร</div>
            ) : (
              <>
                <div className="stats-donut-row">
                  <div className="stats-donut">
                    <svg viewBox="0 0 132 132" width="132" height="132">
                      <circle cx="66" cy="66" r={donutR} fill="none" stroke="var(--panel-border)" strokeWidth="16" />
                      <circle
                        cx="66" cy="66" r={donutR} fill="none" stroke="var(--fg)" strokeWidth="16"
                        strokeDasharray={donutC} strokeDashoffset={donutOffset} strokeLinecap="round"
                        transform="rotate(-90 66 66)"
                      />
                    </svg>
                    <div className="stats-donut-center">
                      <div className="pct">{soldPct.toFixed(1)}%</div>
                      <div className="lbl">ขายแล้ว</div>
                    </div>
                  </div>
                  <div className="stats-donut-legend">
                    <div className="row"><span className="swatch" style={{ background: 'var(--fg)' }} />ขายแล้ว<span className="val">{totalSold.toLocaleString()}</span></div>
                    <div className="row"><span className="swatch" style={{ background: 'var(--panel-border)' }} />คงเหลือ<span className="val">{(totalCapacity - totalSold).toLocaleString()}</span></div>
                    <div className="row"><span className="swatch" style={{ background: 'transparent' }} />ความจุรวม<span className="val">{totalCapacity.toLocaleString()}</span></div>
                  </div>
                </div>
                <div className="stats-zone-list">
                  {stats.zones.map((z) => <ZoneBar key={z.id} zone={z} />)}
                </div>
              </>
            )}
          </div>
        </div>

        <div className="admin-group">
          <div className="admin-group-body">
            <div className="stats-section-label">
              สถานะออร์เดอร์ <span className="stats-section-meta">รวม {totalOrders}</span>
            </div>
            {totalOrders === 0 ? (
              <div style={{ color: 'var(--muted)', fontSize: 14 }}>ยังไม่มีออร์เดอร์</div>
            ) : (
              <>
                <div className="stats-stack-bar">
                  {statusEntries.map(([status, cnt]) => (
                    <div key={status} style={{ width: `${(cnt / totalOrders) * 100}%`, background: STATUS_COLOR[status] ?? 'var(--dim)' }} />
                  ))}
                </div>
                <div className="stats-status-list">
                  {statusEntries.map(([status, cnt]) => (
                    <div className="row" key={status}>
                      <span className="swatch" style={{ background: STATUS_COLOR[status] ?? 'var(--dim)' }} />
                      <span className="name">{STATUS_LABEL[status] ?? status}</span>
                      <span className="pct">{((cnt / totalOrders) * 100).toFixed(1)}%</span>
                      <span className="val">{cnt}</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {stats.zones.length > 0 && (
        <div className="admin-group">
          <div className="admin-group-body">
            <div className="stats-section-label">
              รายได้ต่อโซน <span className="stats-section-meta">เรียงจากมากไปน้อย</span>
            </div>
            <div className="stats-rev-chart">
              {zonesByRevenue.map((z) => (
                <div className="stats-rev-row" key={z.id}>
                  <div className="rname">{z.nameTh || z.nameEn}<span className="rsub">{z.sold.toLocaleString()} ใบ</span></div>
                  <div className="rev-track"><div className="rev-fill" style={{ width: `${(z.revenue / maxZoneRevenue) * 100}%` }} /></div>
                  <div className="rev-amt">{fmt(z.revenue)}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

const REVENUE_RANGE_OPTIONS: { key: RevenueSeriesRange; label: string }[] = [
  { key: '7d', label: '7 วัน' },
  { key: '14d', label: '14 วัน' },
  { key: '30d', label: '1 เดือน' },
  { key: 'all', label: 'ทั้งหมด' },
];

function RevenueTrendCard({ token, eventSlug }: { token: string; eventSlug?: string }) {
  const [range, setRange] = useState<RevenueSeriesRange>('7d');
  const [series, setSeries] = useState<AdminRevenueSeriesPointDto[] | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setSeries(null);
    adminApi.getRevenueSeries(token, range, eventSlug)
      .then((data) => { if (!cancelled) { setSeries(data); setError(''); } })
      .catch(() => { if (!cancelled) setError('โหลดแนวโน้มรายได้ไม่สำเร็จ'); });
    return () => { cancelled = true; };
  }, [token, range, eventSlug]);

  return (
    <div className="admin-group">
      <div className="admin-group-body">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 18, flexWrap: 'wrap' }}>
          <div className="stats-section-label" style={{ marginBottom: 0 }}>แนวโน้มรายได้</div>
          <div style={{ display: 'flex', gap: 6 }}>
            {REVENUE_RANGE_OPTIONS.map((opt) => (
              <button
                key={opt.key}
                type="button"
                className="admin-range-btn"
                aria-pressed={range === opt.key}
                onClick={() => setRange(opt.key)}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
        <RevenueTrendChart series={series} error={error} />
      </div>
    </div>
  );
}

/* ---------- active URLs ---------- */
function UrlsPanel({ eventSlug }: { eventSlug?: string }) {
  const [copied, setCopied] = useState<string | null>(null);
  const origin = window.location.origin;
  const prefix = eventSlug ? `/e/${eventSlug}` : '';
  const rows = [
    { key: 'landing', label: 'Landing Page', path: prefix || '/' },
    { key: 'admin', label: 'Admin', path: `${prefix}/admin` },
    { key: 'checkin', label: 'Check-in', path: `${prefix}/staff/checkin` },
    { key: 'pos', label: 'จุดจำหน่าย', path: `${prefix}/staff/pos` },
    { key: 'sales', label: 'ฝ่ายขาย', path: `${prefix}/admin` },
  ];

  const copy = (key: string, value: string) => {
    navigator.clipboard.writeText(value).then(() => {
      setCopied(key);
      setTimeout(() => setCopied((c) => (c === key ? null : c)), 1500);
    });
  };

  return (
    <Group icon={<IconExternalLink />} title="URL ที่ใช้งานอยู่" sub="ลิงก์เต็มของอีเวนต์นี้ — คัดลอกหรือเปิดได้ทันที">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {rows.map((r) => {
          const full = `${origin}${r.path}`;
          return (
            <div key={r.key} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{
                minWidth: 92, flex: 'none', fontSize: 11, color: 'var(--dim)',
                textTransform: 'uppercase', letterSpacing: '.06em', fontFamily: 'var(--mono)',
              }}>
                {r.label}
              </div>
              <a
                href={r.path}
                target="_blank"
                rel="noreferrer"
                title={full}
                style={{
                  flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  fontFamily: 'var(--mono)', fontSize: 12.5, color: 'var(--fg)',
                }}
              >
                {full}
              </a>
              <button
                type="button"
                className="admin-review-icon-btn"
                style={{ width: 32, height: 32, flex: 'none' }}
                title="คัดลอกลิงก์"
                onClick={() => copy(r.key, full)}
              >
                {copied === r.key ? <IconCheck size={14} /> : <IconCopy size={14} />}
              </button>
              <a
                href={r.path}
                target="_blank"
                rel="noreferrer"
                className="admin-review-icon-btn"
                style={{ width: 32, height: 32, flex: 'none', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
                title="เปิดลิงก์"
              >
                <IconExternalLink size={13} />
              </a>
            </div>
          );
        })}
      </div>
    </Group>
  );
}

/* ---------- review (slip check) ---------- */
function SlipImage({ token, orderId, eventSlug }: { token: string; orderId: string; eventSlug?: string }) {
  const [url, setUrl] = useState('');
  useEffect(() => {
    let objectUrl = '';
    adminApi
      .fetchSlip(token, orderId, eventSlug)
      .then((u) => {
        objectUrl = u;
        setUrl(u);
      })
      .catch(() => setUrl(''));
    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [token, orderId, eventSlug]);
  if (!url) return null;
  return (
    <div className="admin-slip">
      <img src={url} alt="สลิปโอนเงิน" />
    </div>
  );
}

/** Slip stays out of sight (and unfetched) once an order leaves "รอตรวจสลิป" — this reveals it on demand. */
function SlipToggle({ token, orderId, eventSlug }: { token: string; orderId: string; eventSlug?: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ marginTop: 8 }}>
      <button className="btn-link" onClick={() => setOpen((v) => !v)}>
        {open ? 'ซ่อนสลิป' : 'ดูสลิป'}
      </button>
      {open && <SlipImage token={token} orderId={orderId} eventSlug={eventSlug} />}
    </div>
  );
}

/** Delivery evidence is intentionally limited to the masked, event-scoped ledger returned by the API. */
function DeliveryHistory({ rows }: { rows: NotificationDeliveryDto[] }) {
  if (rows.length === 0) return null;

  return (
    <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid var(--border)' }}>
      <div className="mono-label" style={{ marginBottom: 8 }}>TICKET DELIVERY</div>
      <div style={{ display: 'grid', gap: 7 }}>
        {rows.map((item) => (
          <div key={item.id} style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontSize: 12.5 }}>
            <span className={`status-chip ${deliveryStatusClass(item.status)}`}>{deliveryStatusLabel(item.status)}</span>
            {/* TICK-060: `line` is a real channel now — an unmapped value must not render as "Email". */}
            <span style={{ fontWeight: 600 }}>{DELIVERY_CHANNEL_LABELS[item.channel] ?? item.channel}</span>
            <span style={{ color: 'var(--muted)', fontFamily: 'var(--mono)', fontSize: 11 }}>{item.recipientMasked}</span>
            <span style={{ color: 'var(--dim)', fontSize: 11 }}>{item.provider}</span>
            <span style={{ color: 'var(--dim)', fontSize: 11 }}>
              {formatDeliveryTime(item.lastProviderTime ?? item.lastReceivedAt ?? item.createdAt)}
            </span>
            {item.lastErrorCode && <span style={{ color: 'var(--danger)', fontSize: 11 }}>({item.lastErrorCode})</span>}
          </div>
        ))}
      </div>
    </div>
  );
}

function deliveryStatusClass(status: string): string {
  const value = status.toLowerCase();
  if (value.includes('deliver') || value === 'accepted' || value === 'email.sent') return 'paid';
  if (value.includes('bounce') || value.includes('complain') || value.includes('fail') || value.includes('reject')) return 'rejected';
  return 'pending_payment';
}

const DELIVERY_CHANNEL_LABELS: Record<string, string> = {
  email: 'Email', sms: 'SMS', line: 'LINE',
};

function deliveryStatusLabel(status: string): string {
  const value = status.toLowerCase();
  if (value.includes('deliver')) return 'ส่งถึงแล้ว';
  if (value === 'accepted' || value === 'email.sent') return 'ผู้ให้บริการรับแล้ว';
  if (value.includes('bounce')) return 'ตีกลับ';
  if (value.includes('complain')) return 'ร้องเรียน';
  if (value.includes('fail') || value.includes('reject')) return 'ส่งไม่สำเร็จ';
  if (value === 'queued') return 'รอส่ง';
  return status;
}

function formatDeliveryTime(value: string): string {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString('th-TH');
}

/* ---------- check-in ---------- */
type CheckinResultKind = 'ok' | 'warn' | 'error';
const CHECKIN_RESULT_COLOR: Record<CheckinResultKind, string> = { ok: '#9fdcb2', warn: '#d9b36a', error: '#d99a9a' };

function CheckinPanel({ token, eventSlug }: { token: string; eventSlug?: string }) {
  const [input, setInput] = useState('');
  const [scanning, setScanning] = useState(false);
  const [pending, setPending] = useState<{ payload: string; order: AdminOrderDto } | null>(null);
  /** TICK-062 (R8) — required before a group order can be admitted; see the note beside the button. */
  const [groupAcknowledged, setGroupAcknowledged] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [result, setResult] = useState<{ kind: CheckinResultKind; text: string } | null>(null);
  const busy = useRef(false);

  const doLookup = useCallback(
    async (raw: string) => {
      const payload = raw.trim();
      if (!payload || busy.current) return;
      busy.current = true;
      setResult(null);
      try {
        const r = await adminApi.checkinLookup(token, payload, eventSlug);
        if (r.unverified) {
          setResult({ kind: 'warn', text: `⏳ ${r.code} — ${r.name || '—'} · ยังไม่ยืนยันการชำระเงิน (Unverified) — รอ admin ตรวจสลิปก่อน` });
        } else if (r.alreadyCheckedIn) {
          setResult({ kind: 'error', text: `⚠ ${r.code} — ${wristbandReceivedLabel(r.checkedInAt!)}` });
        } else {
          setPending({ payload, order: r });
          setGroupAcknowledged(false);
        }
        setInput('');
      } catch (err) {
        setResult({ kind: 'error', text: err instanceof ApiError ? `✕ ${err.message}` : '✕ ค้นหาไม่สำเร็จ' });
      } finally {
        setTimeout(() => (busy.current = false), 300);
      }
    },
    [token, eventSlug],
  );

  const confirmPassed = useCallback(async () => {
    if (!pending || confirming) return;
    setConfirming(true);
    try {
      const r = await adminApi.checkinConfirm(token, pending.payload, pending.order.entries, eventSlug);
      if (r.alreadyCheckedIn) {
        setResult({ kind: 'error', text: `⚠ ${r.code} — ${wristbandReceivedLabel(r.checkedInAt!)}` });
      } else {
        const linesText = r.lines.map((l) => l.label).join(' · ');
        setResult({ kind: 'ok', text: `✓ ${r.code} — ${r.name || '—'} · ${linesText} · เข้างานได้ ${r.entries} คน` });
      }
      setPending(null);
      setGroupAcknowledged(false);
    } catch (err) {
      setResult({ kind: 'error', text: err instanceof ApiError ? `✕ ${err.message}` : '✕ เช็กอินไม่สำเร็จ' });
    } finally {
      setConfirming(false);
    }
  }, [pending, confirming, token, eventSlug]);

  if (pending) {
    return (
      <>
        <div style={{ background: 'var(--panel)', border: '1px solid var(--line)', borderRadius: 12, padding: 20 }}>
          <div style={{ fontFamily: 'var(--mono)', fontSize: 11, letterSpacing: '.14em', color: 'var(--dim)', marginBottom: 12, textTransform: 'uppercase' }}>
            ตรวจสอบก่อนแลกริสแบนด์
          </div>
          <div style={{ fontSize: 20, fontWeight: 700 }}>{pending.order.code}</div>
          <div style={{ fontSize: 15, marginTop: 6 }}>{pending.order.name || '—'}</div>
          <div style={{ fontSize: 13.5, color: 'var(--muted)', marginTop: 2 }}>
            {pending.order.phone || '—'}{pending.order.email ? ` · ${pending.order.email}` : ''}
          </div>
          <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 6 }}>
            {pending.order.lines.map((l) => {
              const customText = formatCustomFieldValues(l.customFieldValues);
              return (
                <div key={l.ticketId}>
                  <div style={{ fontSize: 15, fontWeight: 600 }}>{l.label}</div>
                  {customText && (
                    <div style={{ fontSize: 12.5, color: 'var(--dim)', fontFamily: 'var(--mono)', marginTop: 2 }}>{customText}</div>
                  )}
                </div>
              );
            })}
          </div>
          <div style={{ fontSize: 13.5, color: 'var(--muted)', marginTop: 10, fontFamily: 'var(--mono)' }}>
            รวม {pending.order.qty} ใบ · เข้างานได้ {pending.order.entries} คน
          </div>
          {pending.order.wristbands.length > 0 && (
            <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--line)' }}>
              <div style={{ fontSize: 12, color: 'var(--dim)', marginBottom: 6 }}>ต้องแจก Wristband</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {pending.order.wristbands.map((w) => (
                  <div key={w.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 15, fontWeight: 600 }}>
                    <span>{w.name} <span style={{ fontSize: 12, fontWeight: 400, color: 'var(--dim)' }}>({w.color})</span></span>
                    <span>× {w.qty}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {/*
            TICK-062 (R8): the same whole-order acknowledgement the check-in app shows. Admin is a
            convenience surface for the same physical act — if it were the lax way to confirm, the
            contract would only hold for whoever happened to use the other screen.
          */}
          <div className="checkin-whole-order" role="note">
            <strong>เช็กอินทั้งคำสั่งซื้อครั้งเดียว</strong>
            <span>
              QR นี้ใช้ได้ครั้งเดียวสำหรับทั้งคำสั่งซื้อ — ยืนยันแล้วจะใช้ซ้ำไม่ได้
              และไม่มีการเช็กอินแยกบางส่วน หากมาไม่ครบ กรุณาให้รอจนครบก่อน
            </span>
            {pending.order.entries > 1 && (
              <label className="checkin-ack">
                <input
                  type="checkbox"
                  checked={groupAcknowledged}
                  onChange={(e) => setGroupAcknowledged(e.target.checked)}
                />
                <span>ยืนยันว่ามาครบ {pending.order.entries} คน และมอบ Wristband ครบแล้ว</span>
              </label>
            )}
          </div>
          <div className="admin-actions" style={{ marginTop: 18 }}>
            <button
              className="btn-primary"
              style={{ padding: '11px 22px' }}
              disabled={confirming || (pending.order.entries > 1 && !groupAcknowledged)}
              onClick={confirmPassed}
            >
              {confirming
                ? 'กำลังบันทึก…'
                : `✓ ยืนยันมาครบ ${pending.order.entries} คน และมอบ Wristband แล้ว`}
            </button>
            <button className="btn-ghost" disabled={confirming} onClick={() => { setPending(null); setGroupAcknowledged(false); }}>ยกเลิก</button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="admin-search-row">
        <div className="admin-search-wrap">
          <IconSearch />
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && doLookup(input)}
            placeholder="กรอกรหัสบัตร เช่น MF26-XXXXX"
          />
        </div>
        <button className="btn-primary" style={{ padding: '13px 22px' }} onClick={() => doLookup(input)}>
          ค้นหา
        </button>
      </div>

      {scanning ? (
        <div>
          <QrScanner onDetected={doLookup} />
          <button className="btn-ghost" style={{ marginTop: 12 }} onClick={() => setScanning(false)}>
            ปิดกล้อง
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => { setResult(null); setScanning(true); }}
          style={{
            border: '1px dashed #333', borderRadius: 16, padding: '56px 20px', display: 'flex',
            flexDirection: 'column', alignItems: 'center', gap: 14, textAlign: 'center', color: 'var(--muted)',
            background: 'transparent', cursor: 'pointer', width: '100%',
          }}
        >
          <IconGate size={46} />
          <div style={{ fontSize: 14, color: 'var(--fg)' }}>แตะเพื่อเปิดกล้องสแกน QR</div>
          <div style={{ fontSize: 12 }}>รองรับ Wristband และ E-ticket</div>
        </button>
      )}

      {result && (
        <div className="checkin-result" style={{ fontSize: 15, color: CHECKIN_RESULT_COLOR[result.kind] }}>
          {result.text}
        </div>
      )}

      <div>
        <div style={{ fontFamily: 'var(--mono)', fontSize: 11, letterSpacing: '.14em', color: 'var(--dim)', marginBottom: 12, textTransform: 'uppercase' }}>
          เช็กอินล่าสุด
        </div>
        {!result ? (
          <div className="admin-empty" style={{ background: 'var(--panel)', border: '1px solid var(--line)', borderRadius: 12, padding: '40px 20px' }}>
            ยังไม่มีการเช็กอิน
          </div>
        ) : (
          <div style={{ background: 'var(--panel)', border: '1px solid var(--line)', borderRadius: 12, padding: '16px 18px', fontSize: 13.5, color: CHECKIN_RESULT_COLOR[result.kind] }}>
            {result.text}
          </div>
        )}
      </div>
    </>
  );
}

/* ---------- tickets ---------- */
const BLANK: TicketInput = {
  nameEn: '', nameTh: '', priceRegular: 0, priceEarly: 0, capacity: 0, seats: 1, sort: 0,
  visible: true, promotion: false, offline: false, serialPrefix: null, customFields: [],
};

let cfKeySeq = 0;
const blankCustomField = (): TicketCustomFieldDto => ({
  key: `field_${++cfKeySeq}`, labelEn: '', labelTh: '', type: 'text', required: false,
});

/** Add/remove/edit the custom fields (e.g. "license plate") a ticket type asks the buyer for at checkout. */
function CustomFieldsEditor({ fields, onChange }: { fields: TicketCustomFieldDto[]; onChange: (next: TicketCustomFieldDto[]) => void }) {
  const update = (i: number, patch: Partial<TicketCustomFieldDto>) =>
    onChange(fields.map((f, j) => (j === i ? { ...f, ...patch } : f)));
  return (
    <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--line)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <div className="card-step" style={{ marginBottom: 0 }}>Custom fields — ข้อมูลเพิ่มเติมตอนซื้อ</div>
        <button
          type="button"
          className="admin-review-icon-btn"
          style={{ width: 32, height: 32 }}
          title="เพิ่ม custom field"
          onClick={() => onChange([...fields, blankCustomField()])}
        >
          <IconPlus size={15} />
        </button>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {fields.map((f, i) => (
          <div key={i} style={{ background: 'var(--bg)', border: '1px solid var(--line)', borderRadius: 8, padding: '12px 12px 10px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr 1fr 0.8fr', gap: 10 }}>
              <label style={{ fontSize: 10.5, fontFamily: 'var(--mono)', letterSpacing: '.04em', color: 'var(--dim)', textTransform: 'uppercase' }}>
                Key <span style={{ color: '#ff9a3d' }}>*</span>
                <input
                  value={f.key}
                  onChange={(e) => update(i, { key: e.target.value.trim().toLowerCase().replace(/[^a-z0-9_]/g, '_') })}
                  style={!f.key.trim() ? { borderColor: '#e08b8b' } : undefined}
                />
              </label>
              <label style={{ fontSize: 10.5, fontFamily: 'var(--mono)', letterSpacing: '.04em', color: 'var(--dim)', textTransform: 'uppercase' }}>
                Label (TH) <span style={{ color: '#ff9a3d' }}>*</span>
                <input
                  value={f.labelTh}
                  onChange={(e) => update(i, { labelTh: e.target.value })}
                  placeholder="เช่น ทะเบียนรถ"
                  style={!f.labelTh.trim() ? { borderColor: '#e08b8b' } : undefined}
                />
              </label>
              <label style={{ fontSize: 10.5, fontFamily: 'var(--mono)', letterSpacing: '.04em', color: 'var(--dim)', textTransform: 'uppercase' }}>
                Label (EN) <span style={{ color: '#ff9a3d' }}>*</span>
                <input
                  value={f.labelEn}
                  onChange={(e) => update(i, { labelEn: e.target.value })}
                  placeholder="e.g. License plate"
                  style={!f.labelEn.trim() ? { borderColor: '#e08b8b' } : undefined}
                />
              </label>
              <label style={{ fontSize: 10.5, fontFamily: 'var(--mono)', letterSpacing: '.04em', color: 'var(--dim)', textTransform: 'uppercase' }}>
                Type
                <select value={f.type} onChange={(e) => update(i, { type: e.target.value as TicketCustomFieldDto['type'] })}>
                  <option value="text">Text</option>
                  <option value="number">Number</option>
                  <option value="select">Select</option>
                </select>
              </label>
            </div>
            {f.type === 'select' && (
              <label style={{ display: 'block', marginTop: 10, fontSize: 11.5, color: 'var(--muted)' }}>
                ตัวเลือก (คั่นด้วย ,)
                <input
                  value={(f.options ?? []).join(', ')}
                  onChange={(e) => update(i, { options: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) })}
                  placeholder="กรุงเทพมหานคร, เชียงใหม่, ขอนแก่น"
                />
              </label>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 10 }}>
              <label className="ticket-check" style={{ margin: 0 }}>
                <input type="checkbox" checked={f.required} onChange={(e) => update(i, { required: e.target.checked })} />
                บังคับกรอก (required)
              </label>
              <button
                type="button"
                className="admin-review-icon-btn reject"
                style={{ width: 30, height: 30 }}
                title="ลบฟิลด์นี้"
                onClick={() => onChange(fields.filter((_, j) => j !== i))}
              >
                <IconTrash size={14} />
              </button>
            </div>
          </div>
        ))}
        {fields.length === 0 && (
          <div style={{ fontSize: 11.5, color: 'var(--dim)' }}>ยังไม่มี custom field — ตั๋วประเภทนี้ใช้ฟอร์มซื้อมาตรฐาน</div>
        )}
      </div>
    </div>
  );
}

/** Plain-digit input while focused; comma-grouped (thousands) once it loses focus. */
function PriceInput({ value, onChange, disabled }: { value: number; onChange: (n: number) => void; disabled?: boolean }) {
  const [focused, setFocused] = useState(false);
  const [raw, setRaw] = useState(String(value || ''));
  useEffect(() => { if (!focused) setRaw(String(value || '')); }, [value, focused]);
  return (
    <input
      type="text"
      inputMode="numeric"
      disabled={disabled}
      value={focused ? raw : (value ? value.toLocaleString('en-US') : '')}
      onFocus={() => { setFocused(true); setRaw(String(value || '')); }}
      onChange={(e) => {
        const digits = e.target.value.replace(/[^0-9]/g, '');
        setRaw(digits);
        onChange(Math.max(0, Math.round(Number(digits) || 0)));
      }}
      onBlur={() => setFocused(false)}
    />
  );
}

function TicketRow({
  ticket, token, eventSlug, onChanged, dragging, onDragStart, onDragOver, onDrop, onDragEnd, earlyBirdEnabled,
  wristbands, draft, onDraftChange, wbRows, onWbRowsChange,
}: {
  ticket: AdminTicketDto;
  token: string;
  eventSlug?: string;
  onChanged: () => void;
  dragging: boolean;
  onDragStart: () => void;
  onDragOver: (e: DragEvent) => void;
  onDrop: (e: DragEvent) => void;
  onDragEnd: () => void;
  earlyBirdEnabled: boolean;
  wristbands: WristbandDto[];
  draft: TicketDraftFields;
  onDraftChange: (next: TicketDraftFields) => void;
  wbRows: WristbandBinding[];
  onWbRowsChange: (next: WristbandBinding[]) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const wbUsedIds = new Set(wbRows.map((r) => r.wristbandId));
  const wbAvailableToAdd = wristbands.filter((w) => !wbUsedIds.has(w.id));

  const num = (v: string) => Math.max(0, Math.round(Number(v) || 0));
  const pct = ticket.capacity > 0 ? Math.min(100, Math.round((ticket.sold / ticket.capacity) * 100)) : 0;
  const statusLabel = ticket.closed ? 'ปิดขาย' : ticket.soldOut ? 'เต็ม' : 'กำลังขาย';
  const statusColor = ticket.closed ? '#e08b8b' : ticket.soldOut ? '#d9b36a' : '#8fca9a';
  const statusBg = ticket.closed ? '#241616' : ticket.soldOut ? '#241f16' : '#16241a';
  const statusBorder = ticket.closed ? '#4a2626' : ticket.soldOut ? '#4a3f24' : '#244a2c';

  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setErr('');
    try {
      await fn();
      onChanged();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'ทำรายการไม่สำเร็จ');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="admin-group"
      style={{ opacity: dragging ? 0.4 : 1, marginBottom: 12 }}
      onDragOver={onDragOver}
      onDrop={onDrop}
    >
      <div style={{ display: 'flex', gap: 14, alignItems: 'center', padding: '16px 20px', flexWrap: 'wrap' }}>
        <span className="drag-handle" draggable onDragStart={onDragStart} onDragEnd={onDragEnd} title="ลากเพื่อจัดลำดับ">⠿</span>
        <span style={{ fontSize: 11.5, padding: '4px 10px', borderRadius: 7, fontWeight: 500, background: statusBg, border: `1px solid ${statusBorder}`, color: statusColor }}>
          {statusLabel}
        </span>
        <div style={{ flex: '1 1 200px', minWidth: 0 }}>
          <div style={{ fontSize: 13.5, fontWeight: 500 }}>{ticket.nameEn || ticket.id}</div>
          <div style={{ fontSize: 11.5, color: 'var(--dim)', marginTop: 3 }}>{ticket.nameTh}</div>
        </div>
        <div style={{ fontFamily: 'var(--mono)', fontSize: 13, flex: '0 0 90px', color: 'var(--muted)' }}>
          {ticket.capacity.toLocaleString()} ใบ
        </div>
        <div style={{ flex: '1 1 140px', maxWidth: 160 }}>
          <div style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--muted)' }}>
            {ticket.sold} / {ticket.capacity.toLocaleString()}
          </div>
          <div style={{ height: 4, background: 'var(--line)', borderRadius: 9999, overflow: 'hidden', marginTop: 6 }}>
            <div style={{ height: '100%', borderRadius: 9999, background: 'var(--fg)', width: `${pct}%` }} />
          </div>
        </div>
        <button
          className={`admin-review-icon-btn${ticket.closed ? ' closed-active' : ''}`}
          style={{ width: 36, height: 36 }}
          title={ticket.closed ? 'เปิดขายอีกครั้ง' : 'ปิดการขาย (Sold Out)'}
          disabled={busy}
          onClick={() => run(() => adminApi.updateTicket(token, ticket.id, { closed: !ticket.closed }, eventSlug))}
        >
          {busy ? <span className="btn-spinner" aria-hidden="true" /> : <IconHand size={16} />}
        </button>
        <button
          className="admin-review-icon-btn"
          style={{ width: 36, height: 36 }}
          title={ticket.visible ? 'ซ่อนจาก Landing Page' : 'แสดงบน Landing Page'}
          disabled={busy}
          onClick={() => run(() => adminApi.updateTicket(token, ticket.id, { visible: !ticket.visible }, eventSlug))}
        >
          {busy ? <span className="btn-spinner" aria-hidden="true" /> : (ticket.visible ? <IconEye /> : <IconEyeOff />)}
        </button>
        <button
          className="admin-review-icon-btn reject"
          style={{ width: 36, height: 36 }}
          title="ลบบัตรประเภทนี้"
          disabled={busy}
          onClick={() => {
            if (window.confirm(`ลบบัตร "${ticket.id}" ?`)) run(() => adminApi.deleteTicket(token, ticket.id, eventSlug));
          }}
        >
          <IconTrash size={16} />
        </button>
      </div>

      <div className="admin-group-body" style={{ paddingTop: 0, borderTop: '1px solid var(--line)' }}>
        <div className="ticket-edit-rows" style={{ marginTop: 16 }}>
          <label className="ticket-check">
            <input type="checkbox" checked={draft.promotion} onChange={(e) => onDraftChange({ ...draft, promotion: e.target.checked })} />
            โปรโมชั่น
          </label>
          {/*
            * TICK-077 — this type is sold as a printed card over a counter.
            *
            * The wording says what it does to the storefront, because that is the surprising half:
            * ticking it removes the ticket from the public page entirely. A buy button and a counter
            * selling the same seats would race each other for the same capacity.
            */}
          <label className="ticket-check" title="บัตรแข็ง/บัตรกระดาษที่พิมพ์ไว้ล่วงหน้าแล้วขายหน้างาน — ประเภทนี้จะไม่แสดงบนหน้าขายออนไลน์">
            <input type="checkbox" checked={draft.offline ?? false} onChange={(e) => onDraftChange({ ...draft, offline: e.target.checked })} />
            บัตรขายหน้างาน (ไม่ขายออนไลน์)
          </label>
          {draft.offline && (
            <label style={{ display: 'grid', gap: 4, fontSize: 12, maxWidth: 260 }}>
              รหัสนำหน้าเลขบัตร <span style={{ color: 'var(--dim)', fontWeight: 400 }}>(อังกฤษ/ตัวเลข 4 หลัก)</span>
              <input
                value={draft.serialPrefix ?? ''}
                onChange={(e) => onDraftChange({ ...draft, serialPrefix: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 4) })}
                placeholder="MPAS"
                style={{ fontFamily: 'var(--mono)', letterSpacing: 2 }}
              />
              <span style={{ color: 'var(--dim)', fontSize: 11 }}>
                เลขบัตรจะเดินต่อจากรหัสนี้ เช่น MPAS-0001 · ใช้ซ้ำกับประเภทตั๋วอื่นในงานเดียวกันไม่ได้
              </span>
            </label>
          )}
          <div className="ticket-edit-row-4">
            <label>ชื่อ (EN)<input value={draft.nameEn} onChange={(e) => onDraftChange({ ...draft, nameEn: e.target.value })} /></label>
            <label>ชื่อ (TH)<input value={draft.nameTh} onChange={(e) => onDraftChange({ ...draft, nameTh: e.target.value })} /></label>
            <label>ราคาปกติ<PriceInput value={draft.priceRegular} onChange={(v) => onDraftChange({ ...draft, priceRegular: v })} /></label>
            <label>
              ราคา Early <span style={{ color: 'var(--dim)', fontWeight: 400 }}>{earlyBirdEnabled ? '(ว่าง = ใช้ราคาปกติ)' : '(ปิดใช้งาน Early Bird)'}</span>
              <PriceInput value={draft.priceEarly} onChange={(v) => onDraftChange({ ...draft, priceEarly: v })} disabled={!earlyBirdEnabled} />
            </label>
          </div>
          <div className="ticket-edit-row-2">
            <label>โควตา (Quota)<input type="number" value={draft.capacity || ''} onChange={(e) => onDraftChange({ ...draft, capacity: num(e.target.value) })} /></label>
            <label>ที่นั่ง/ใบ<input type="number" min={1} value={draft.seats} onChange={(e) => onDraftChange({ ...draft, seats: Math.max(1, num(e.target.value)) })} /></label>
          </div>
        </div>

        <CustomFieldsEditor fields={draft.customFields} onChange={(next) => onDraftChange({ ...draft, customFields: next })} />

        {err && <div className="form-error">{err}</div>}

        {wristbands.length > 0 && (
          <div style={{ marginTop: 18, paddingTop: 16, borderTop: '1px solid var(--line)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <div className="card-step" style={{ marginBottom: 0 }}>Wristband ที่จะได้รับ</div>
              <button
                type="button"
                className="admin-review-icon-btn"
                style={{ width: 32, height: 32 }}
                title="เพิ่ม Wristband"
                disabled={wbAvailableToAdd.length === 0}
                onClick={() => onWbRowsChange([...wbRows, { wristbandId: wbAvailableToAdd[0].id, qty: 1 }])}
              >
                <IconPlus size={15} />
              </button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {wbRows.map((row, i) => {
                const selected = wristbands.find((w) => w.id === row.wristbandId);
                const options = wristbands.filter((w) => w.id === row.wristbandId || !wbUsedIds.has(w.id));
                return (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    {selected && <WristbandSwatch color={selected.color} />}
                    <select
                      style={{ flex: '1 1 160px' }}
                      value={row.wristbandId}
                      onChange={(e) => onWbRowsChange(wbRows.map((r, j) => (j === i ? { ...r, wristbandId: e.target.value } : r)))}
                    >
                      {options.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
                    </select>
                    <input
                      type="number"
                      min={1}
                      max={20}
                      style={{ width: 70 }}
                      value={row.qty}
                      onChange={(e) => onWbRowsChange(wbRows.map((r, j) => (j === i ? { ...r, qty: Math.max(1, Math.round(Number(e.target.value) || 1)) } : r)))}
                    />
                    <span style={{ fontSize: 11.5, color: 'var(--dim)' }}>เส้น / ใบ</span>
                    <button
                      type="button"
                      className="admin-review-icon-btn reject"
                      style={{ width: 32, height: 32, flex: 'none' }}
                      title="เอาออก"
                      onClick={() => onWbRowsChange(wbRows.filter((_, j) => j !== i))}
                    >
                      <IconTrash size={15} />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const EXPIRY_OPTIONS: { key: '24h' | '7d' | '30d' | 'never'; label: string }[] = [
  { key: '24h', label: '24 ชั่วโมง' },
  { key: '7d', label: '7 วัน' },
  { key: '30d', label: '30 วัน' },
  { key: 'never', label: 'ไม่หมดอายุ' },
];

function tokenStatusLabel(t: CheckinTokenDto): { label: string; bg: string; border: string; color: string } {
  if (t.revoked) return { label: 'เพิกถอนแล้ว', bg: 'rgba(217,154,154,0.12)', border: 'rgba(217,154,154,0.35)', color: '#d99a9a' };
  if (t.expired) return { label: 'หมดอายุ', bg: 'rgba(217,154,154,0.12)', border: 'rgba(217,154,154,0.35)', color: '#d99a9a' };
  if (t.usedUp) return { label: 'ใช้ไปแล้ว', bg: 'rgba(217,154,154,0.12)', border: 'rgba(217,154,154,0.35)', color: '#d99a9a' };
  return { label: 'ใช้งานได้', bg: 'rgba(140,200,150,0.12)', border: 'rgba(140,200,150,0.35)', color: '#8cc896' };
}

function StaffPanel({ token, eventSlug }: { token: string; eventSlug?: string }) {
  const [tokens, setTokens] = useState<CheckinTokenDto[]>([]);
  const [error, setError] = useState('');
  const [adding, setAdding] = useState(false);
  const [holderLabel, setHolderLabel] = useState('');
  const [expiry, setExpiry] = useState<'24h' | '7d' | '30d' | 'never'>('never');
  const [role, setRole] = useState<'checkin' | 'sales' | 'pos'>('checkin');
  const [addErr, setAddErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [revealed, setRevealed] = useState<{ holderLabel: string; token: string } | null>(null);
  const [log, setLog] = useState<CheckinLogDto | null>(null);
  const [logError, setLogError] = useState('');
  const [copiedTokenId, setCopiedTokenId] = useState<string | null>(null);

  const copyToken = (id: string, value: string) => {
    navigator.clipboard.writeText(value).then(() => {
      setCopiedTokenId(id);
      setTimeout(() => setCopiedTokenId((c) => (c === id ? null : c)), 1500);
    });
  };

  const loadLog = useCallback(async () => {
    try {
      setLog(await adminApi.getCheckinLog(token, eventSlug));
      setLogError('');
    } catch {
      setLogError('โหลดบันทึกการเช็กอินไม่สำเร็จ');
    }
  }, [token, eventSlug]);

  usePolling(loadLog, 15000);

  const load = useCallback(async () => {
    try {
      setTokens(await adminApi.listCheckinTokens(token, eventSlug));
      setError('');
    } catch {
      setError('โหลดรายการ Token ไม่สำเร็จ');
    }
  }, [token, eventSlug]);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!holderLabel.trim()) return;
    setAddErr('');
    setBusy(true);
    try {
      const created = await adminApi.createCheckinToken(token, holderLabel.trim(), expiry, eventSlug, role);
      setRevealed({ holderLabel: created.holderLabel, token: created.token });
      setHolderLabel('');
      setExpiry('never');
      setRole('checkin');
      setAdding(false);
      await load();
    } catch (e) {
      setAddErr(e instanceof ApiError ? e.message : 'สร้าง Token ไม่สำเร็จ');
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (id: string) => {
    if (!window.confirm('เพิกถอน Token นี้? พนักงานที่ถือ Token นี้จะเข้าใช้งานเช็กอินไม่ได้อีก')) return;
    try {
      await adminApi.revokeCheckinToken(token, id, eventSlug);
      await load();
    } catch {
      setError('เพิกถอน Token ไม่สำเร็จ');
    }
  };

  return (
    <>
      <UrlsPanel eventSlug={eventSlug} />

      {error && <div className="form-error">{error}</div>}

      <Group icon={<IconUsers />} title="Token สำหรับทีมงาน" sub="สร้าง Token แยกต่อคน — เช็กอินหน้างาน, จุดจำหน่าย (ขายบัตรหน้างาน) หรือ ฝ่ายขาย (เห็นแค่ภาพรวม/ตรวจสอบสลิป)">
        {revealed && (
          <div style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 11, padding: 16, marginBottom: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
              สร้าง Token ให้ "{revealed.holderLabel}" แล้ว — คัดลอกตอนนี้ จะไม่แสดงให้ดูอีก
            </div>
            <div className="coupon-row">
              <input readOnly value={revealed.token} onFocus={(e) => e.currentTarget.select()} />
              <button
                className="btn-ghost"
                onClick={() => { navigator.clipboard.writeText(revealed.token); }}
              >
                คัดลอก
              </button>
            </div>
            <button className="btn-link" style={{ marginTop: 8 }} onClick={() => setRevealed(null)}>ปิด</button>
          </div>
        )}

        {!adding ? (
          <button className="btn-primary" style={{ padding: '11px 20px' }} onClick={() => setAdding(true)}>
            + สร้าง Token ใหม่
          </button>
        ) : (
          <>
            <div className="ticket-edit-grid">
              <label>ชื่อพนักงาน<input value={holderLabel} onChange={(e) => setHolderLabel(e.target.value)} placeholder="เช่น เจ้าหน้าที่ประตู 1 — คุณเอ" /></label>
              <label>
                ประเภท Token
                <select value={role} onChange={(e) => setRole(e.target.value as typeof role)}>
                  <option value="checkin">เช็กอินหน้างาน</option>
                  {/* TICK-078 — its own role: selling stock is not admitting people, and one
                      permission must not be the price of the other. */}
                  <option value="pos">จุดจำหน่าย (ขายบัตรหน้างาน)</option>
                  <option value="sales">ฝ่ายขาย</option>
                </select>
              </label>
              <label>
                อายุการใช้งาน
                <select value={expiry} onChange={(e) => setExpiry(e.target.value as typeof expiry)}>
                  {EXPIRY_OPTIONS.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
                </select>
              </label>
            </div>
            {addErr && <div className="form-error">{addErr}</div>}
            <div className="admin-actions">
              <button className="btn-primary" style={{ padding: '9px 18px' }} disabled={busy || !holderLabel.trim()} onClick={create}>สร้าง</button>
              <button className="btn-ghost" onClick={() => { setAdding(false); setHolderLabel(''); setAddErr(''); }}>ยกเลิก</button>
            </div>
          </>
        )}
      </Group>

      <div className="admin-ticket-head-row">
        <div>ชื่อพนักงาน</div><div>ประเภท</div><div>Token</div><div>ใช้ล่าสุด</div><div>สถานะ</div><div></div>
      </div>

      {tokens.length === 0 && <div style={{ color: 'var(--dim)', fontSize: 13.5, padding: '18px 4px' }}>ยังไม่มี Token — สร้าง Token แรกด้านบน</div>}

      {tokens.map((t) => {
        const s = tokenStatusLabel(t);
        return (
          <div key={t.id} className="admin-group" style={{ marginBottom: 8 }}>
            <div className="admin-token-row">
              <div>
                <div style={{ fontWeight: 600 }}>{t.holderLabel || '(ไม่มีชื่อ)'}</div>
                <div style={{ fontSize: 12, color: 'var(--dim)' }}>
                  สร้างเมื่อ {new Date(t.createdAt).toLocaleString('th-TH')}
                  {t.expiresAt ? ` · หมดอายุ ${new Date(t.expiresAt).toLocaleString('th-TH')}` : ''}
                </div>
              </div>
              <div style={{ fontSize: 12.5, color: 'var(--muted)' }}>{t.role === 'sales' ? 'ฝ่ายขาย' : t.role === 'pos' ? 'จุดจำหน่าย' : 'เช็กอิน'}</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                <span style={{ fontFamily: 'var(--mono)', fontSize: 12.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {t.token ?? t.tokenPreview}
                </span>
                {t.token && (
                  <button
                    type="button"
                    className="admin-review-icon-btn"
                    style={{ width: 26, height: 26, flex: 'none' }}
                    title="คัดลอก Token"
                    onClick={() => copyToken(t.id, t.token!)}
                  >
                    {copiedTokenId === t.id ? <IconCheck size={13} /> : <IconCopy size={13} />}
                  </button>
                )}
              </div>
              <div style={{ fontSize: 12.5, color: 'var(--muted)' }}>
                {t.lastUsedAt ? new Date(t.lastUsedAt).toLocaleString('th-TH') : 'ยังไม่เคยใช้'}
              </div>
              <span style={{ fontSize: 11.5, padding: '4px 10px', borderRadius: 7, fontWeight: 500, background: s.bg, border: `1px solid ${s.border}`, color: s.color, justifySelf: 'start' }}>
                {s.label}
              </span>
              {t.active ? (
                <button className="btn-link" style={{ color: '#d99a9a' }} onClick={() => revoke(t.id)}>เพิกถอน</button>
              ) : <div />}
            </div>
          </div>
        );
      })}

      <Group icon={<IconList />} title="สรุปการเช็กอินรายคน" sub="จำนวนบัตรที่แต่ละคนเช็กอินไปแล้ว">
        {logError && <div className="form-error">{logError}</div>}
        {log && log.byStaff.length === 0 && (
          <div style={{ color: 'var(--dim)', fontSize: 13.5 }}>ยังไม่มีการเช็กอิน</div>
        )}
        {log && log.byStaff.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {log.byStaff.map((s) => (
              <div key={s.actor} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 9 }}>
                <div>
                  <div style={{ fontSize: 13.5, fontWeight: 500 }}>{s.actor}</div>
                  <div style={{ fontSize: 11.5, color: 'var(--dim)' }}>ล่าสุด {new Date(s.lastCheckedInAt).toLocaleString('th-TH')}</div>
                </div>
                <div style={{ fontFamily: 'var(--mono)', fontSize: 16, fontWeight: 600 }}>{s.count}</div>
              </div>
            ))}
          </div>
        )}
      </Group>

      <Group icon={<IconClock />} title="ประวัติการเช็กอินล่าสุด" sub="เรียงจากล่าสุดไปเก่าสุด">
        {log && log.entries.length === 0 && (
          <div style={{ color: 'var(--dim)', fontSize: 13.5 }}>ยังไม่มีรายการ</div>
        )}
        {log && log.entries.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 320, overflowY: 'auto' }}>
            {log.entries.map((e) => (
              <div key={e.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5, padding: '6px 2px', borderBottom: '1px solid var(--line)' }}>
                <span>{e.actor}<span style={{ color: 'var(--dim)' }}> เช็กอินบัตร </span>{e.detail}</span>
                <span style={{ color: 'var(--muted)', fontFamily: 'var(--mono)' }}>{new Date(e.createdAt).toLocaleString('th-TH')}</span>
              </div>
            ))}
          </div>
        )}
      </Group>
    </>
  );
}

/** Local <input type="datetime-local"> wants "YYYY-MM-DDTHH:mm" in the viewer's own timezone, not UTC. */
function isoToLocalInput(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * One Early Bird cutoff per event — not per ticket. Every ticket type's price/phase badge reads
 * off the same `resolveEventPhase` on the server, so changing this here takes effect for all of
 * them at once.
 */
function EarlyBirdSettings({ token, eventSlug, onEnabledChange }: { token: string; eventSlug?: string; onEnabledChange?: (enabled: boolean) => void }) {
  const [data, setData] = useState<EarlyBirdDto | null>(null);
  const [draftLocal, setDraftLocal] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    try {
      const d = await adminApi.getEarlyBird(token, eventSlug);
      setData(d);
      setDraftLocal(isoToLocalInput(d.earlyBirdUntil ?? d.effectiveUntil));
      onEnabledChange?.(d.enabled);
    } catch {
      setErr('โหลดข้อมูลไม่สำเร็จ');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, eventSlug]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!draftLocal) return;
    setSaving(true);
    setErr('');
    try {
      const d = await adminApi.setEarlyBird(token, { earlyBirdUntil: new Date(draftLocal).toISOString(), enabled: data?.enabled }, eventSlug);
      setData(d);
      onEnabledChange?.(d.enabled);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'บันทึกไม่สำเร็จ');
    } finally {
      setSaving(false);
    }
  };

  const clear = async () => {
    setSaving(true);
    setErr('');
    try {
      const d = await adminApi.setEarlyBird(token, { earlyBirdUntil: null, enabled: data?.enabled }, eventSlug);
      setData(d);
      setDraftLocal(isoToLocalInput(d.effectiveUntil));
      onEnabledChange?.(d.enabled);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'บันทึกไม่สำเร็จ');
    } finally {
      setSaving(false);
    }
  };

  const toggleEnabled = async () => {
    if (!data) return;
    setSaving(true);
    setErr('');
    try {
      const d = await adminApi.setEarlyBird(token, { earlyBirdUntil: data.earlyBirdUntil, enabled: !data.enabled }, eventSlug);
      setData(d);
      onEnabledChange?.(d.enabled);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'บันทึกไม่สำเร็จ');
    } finally {
      setSaving(false);
    }
  };

  /**
   * Display only, and saved on its own — an owner hiding the date must not have to press บันทึก and
   * push the date draft they are still editing live at the same time.
   */
  const toggleDateVisible = async () => {
    if (!data) return;
    setSaving(true);
    setErr('');
    try {
      const d = await adminApi.setEarlyBird(
        token, { earlyBirdUntil: data.earlyBirdUntil, dateVisible: !data.dateVisible }, eventSlug,
      );
      setData(d);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'บันทึกไม่สำเร็จ');
    } finally {
      setSaving(false);
    }
  };

  if (!data) return null;

  const isEarlyBird = data.enabled && Date.now() < new Date(data.effectiveUntil).getTime();
  const statusStyle = !data.enabled
    ? { bg: '#241616', border: '#4a2626', color: '#d99a9a' }
    : isEarlyBird
      ? { bg: '#241f16', border: '#4a3f24', color: '#d9b36a' }
      : { bg: '#16241a', border: '#244a2c', color: '#8fca9a' };
  const statusLabel = !data.enabled
    ? 'ปิดใช้งาน Early Bird (ราคาปกติ)'
    : isEarlyBird
      ? 'กำลังอยู่ในช่วง Early Bird'
      : 'พ้นช่วง Early Bird แล้ว (ราคาปกติ)';

  return (
    <Group
      icon={<IconClock />}
      title="ช่วง Early Bird"
      sub="กำหนดวันเวลาสิ้นสุดราคา Early Bird ของ event นี้ — มีผลกับตั๋วทุกประเภทพร้อมกัน (ราคา + ป้ายบน Landing Page)"
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
        <label className="ticket-check" style={{ marginBottom: 0 }}>
          <input type="checkbox" checked={data.enabled} disabled={saving} onChange={toggleEnabled} />
          เปิดใช้งาน Early Bird
        </label>
        <span
          style={{
            fontSize: 11.5, padding: '4px 10px', borderRadius: 7, fontWeight: 500,
            background: statusStyle.bg, border: `1px solid ${statusStyle.border}`, color: statusStyle.color,
          }}
        >
          {statusLabel}
        </span>
      </div>

      <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--line)', opacity: data.enabled ? 1 : 0.5 }}>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 5, fontSize: 12.5, maxWidth: 260 }}>
          สิ้นสุด Early Bird
          <input type="datetime-local" value={draftLocal} disabled={!data.enabled} onChange={(e) => setDraftLocal(e.target.value)} />
        </label>
        {!data.earlyBirdUntil && (
          <div style={{ fontSize: 11.5, color: 'var(--dim)', marginTop: 6 }}>
            ยังไม่ได้ตั้งค่าเฉพาะ event นี้ — ใช้ค่าเริ่มต้นของแพลตฟอร์ม
          </div>
        )}
        <label className="ticket-check" style={{ marginTop: 12, marginBottom: 0 }}>
          <input type="checkbox" checked={data.dateVisible} disabled={saving || !data.enabled} onChange={toggleDateVisible} />
          แสดงวันที่สิ้นสุดบนหน้า Landing
        </label>
        <div style={{ fontSize: 11.5, color: 'var(--dim)', marginTop: 6, maxWidth: 420 }}>
          {data.dateVisible
            ? 'ลูกค้าเห็นป้าย EARLY BIRD พร้อมวันที่สิ้นสุดบนหัวเว็บ'
            : 'ลูกค้าเห็นป้าย EARLY BIRD อย่างเดียว ไม่เห็นวันที่ — ราคายังเปลี่ยนตามวันเวลาที่ตั้งไว้เหมือนเดิม'}
        </div>
        <div className="admin-actions" style={{ marginTop: 12 }}>
          <button className="btn-primary" style={{ padding: '9px 16px' }} disabled={saving || !draftLocal || !data.enabled} onClick={save}>
            บันทึก
          </button>
          {data.earlyBirdUntil && (
            <button className="btn-link" disabled={saving || !data.enabled} onClick={clear}>ใช้ค่าเริ่มต้นของแพลตฟอร์มแทน</button>
          )}
        </div>
      </div>

      {err && <div className="form-error">{err}</div>}
    </Group>
  );
}

function CheckoutSettings({ token, eventSlug }: { token: string; eventSlug?: string }) {
  const [data, setData] = useState<CheckoutSettingsDto | null>(null);
  const [draftLocal, setDraftLocal] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    try {
      const d = await adminApi.getCheckoutSettings(token, eventSlug);
      setData(d);
      setDraftLocal(String(d.reservationMinutes ?? d.effectiveMinutes));
    } catch {
      setErr('โหลดข้อมูลไม่สำเร็จ');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, eventSlug]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    const minutes = Number(draftLocal);
    if (!draftLocal || !Number.isInteger(minutes) || minutes < 1 || minutes > 120) return;
    setSaving(true);
    setErr('');
    try {
      setData(await adminApi.setCheckoutSettings(token, minutes, eventSlug));
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'บันทึกไม่สำเร็จ');
    } finally {
      setSaving(false);
    }
  };

  const clear = async () => {
    setSaving(true);
    setErr('');
    try {
      const d = await adminApi.setCheckoutSettings(token, null, eventSlug);
      setData(d);
      setDraftLocal(String(d.effectiveMinutes));
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'บันทึกไม่สำเร็จ');
    } finally {
      setSaving(false);
    }
  };

  if (!data) return null;

  return (
    <Group
      icon={<IconClock />}
      title="ระยะเวลาล็อกบัตรตอน Checkout"
      sub="ลูกค้ามีเวลาเท่านี้ในการโอนเงินและแนบสลิปก่อนออเดอร์หมดเวลาและบัตรกลับเข้าระบบ — มีผลกับออเดอร์ใหม่ของ event นี้เท่านั้น"
    >
      <label style={{ display: 'flex', flexDirection: 'column', gap: 5, fontSize: 12.5, maxWidth: 200 }}>
        นาที
        <input
          type="number" min={1} max={120} value={draftLocal}
          onChange={(e) => setDraftLocal(e.target.value)}
        />
      </label>
      {data.reservationMinutes === null && (
        <div style={{ fontSize: 11.5, color: 'var(--dim)', marginTop: 6 }}>
          ยังไม่ได้ตั้งค่าเฉพาะ event นี้ — ใช้ค่าเริ่มต้นของแพลตฟอร์ม ({data.platformDefault} นาที)
        </div>
      )}
      <div className="admin-actions" style={{ marginTop: 12 }}>
        <button className="btn-primary" style={{ padding: '9px 16px' }} disabled={saving || !draftLocal} onClick={save}>
          บันทึก
        </button>
        {data.reservationMinutes !== null && (
          <button className="btn-link" disabled={saving} onClick={clear}>ใช้ค่าเริ่มต้นของแพลตฟอร์มแทน</button>
        )}
      </div>

      {err && <div className="form-error">{err}</div>}
    </Group>
  );
}

/* ---------- TICK-090: post-checkout delivery mode ---------- */

const DELIVERY_MODE_COPY: {
  key: TicketDeliveryMode; title: string; body: string; badge?: string;
}[] = [
  {
    key: 'verified_channel',
    title: 'ให้ยืนยันช่องทางก่อนจึงเห็นตั๋ว',
    body: 'ผู้ซื้อเลือก LINE / SMS / อีเมล แล้วยืนยันว่าเป็นเจ้าของช่องทางนั้น ตั๋วจึงจะแสดงและถูกส่งออกไป ปลอดภัยที่สุด แต่ผู้ซื้อบางส่วนหลุดระหว่างทาง',
    badge: 'ค่าเริ่มต้น',
  },
  {
    key: 'instant',
    title: 'แสดงตั๋วบนหน้าจอทันที',
    body: 'ผู้ซื้อเห็น QR ทันทีที่สลิปผ่าน และระบบไม่ส่งอะไรออกไปเลย — ไม่มีค่าส่ง SMS ต่อออเดอร์ ผู้ซื้อกลับมาดูตั๋วภายหลังได้ที่ /myticket',
  },
  {
    key: 'instant_and_send',
    title: 'แสดงตั๋วทันที และส่งสำเนาให้ด้วย',
    body: 'ผู้ซื้อเห็นตั๋วบนหน้าจอก่อน แล้วระบบส่งลิงก์ตั๋วตามช่องทางที่เลือกไว้ด้านล่างเป็นสำเนา ข้อความที่ส่งจะใช้ถ้อยคำแบบ “สำเนาบัตร” ไม่ใช่ “ชำระเงินสำเร็จ”',
  },
];

const COPY_CHANNEL_COPY: { key: DeliveryChannel; label: string; hint: string }[] = [
  { key: 'line', label: 'JamTick LINE', hint: 'ส่งได้เฉพาะออเดอร์ที่เคยผูกบัญชี LINE ไว้แล้ว' },
  { key: 'sms', label: 'SMS', hint: 'ถึงแน่นอนที่สุด แต่มีค่าส่งต่อข้อความ' },
  { key: 'email', label: 'อีเมล', hint: 'ไม่มีค่าส่ง และผู้ซื้อค้นย้อนหลังได้ง่าย' },
];

/**
 * TICK-090 — the one screen that decides when this event's buyers see their ticket.
 *
 * Owner-only on the server too. The copy is written to make the trade legible rather than to sell
 * the fastest option: `instant` removes a verification step, which is a real reduction in how sure
 * we are that the person holding the screen is the person who paid, and an organizer choosing it
 * should be choosing it knowingly.
 */
function TicketDeliveryPanel({ token, eventSlug }: { token: string; eventSlug?: string }) {
  const [data, setData] = useState<TicketDeliveryDto | null>(null);
  const [mode, setMode] = useState<TicketDeliveryMode>('verified_channel');
  const [channels, setChannels] = useState<DeliveryChannel[]>([]);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    adminApi.getTicketDelivery(token, eventSlug)
      .then((d) => { setData(d); setMode(d.mode); setChannels(d.copyChannels); })
      .catch((e) => setErr(e instanceof ApiError ? e.message : 'โหลดการตั้งค่าการส่งมอบตั๋วไม่สำเร็จ'));
  }, [token, eventSlug]);

  const dirty = !!data && (mode !== data.mode || channels.join(',') !== data.copyChannels.join(','));

  const toggleChannel = (key: DeliveryChannel) => {
    setSaved(false);
    setChannels((prev) => (prev.includes(key) ? prev.filter((c) => c !== key) : [...prev, key]));
  };

  const save = async () => {
    setSaving(true);
    setErr('');
    try {
      const next = await adminApi.setTicketDelivery(token, { mode, copyChannels: channels }, eventSlug);
      setData(next);
      setMode(next.mode);
      setChannels(next.copyChannels);
      setSaved(true);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'บันทึกไม่สำเร็จ');
    } finally {
      setSaving(false);
    }
  };

  if (!data) return err ? <div className="form-error">{err}</div> : null;

  const sendMode = mode === 'instant_and_send';
  // Selected but not deliverable. Worth its own warning: the form accepting a channel the platform
  // has switched off is exactly how an organizer concludes copies are going out when none are.
  const blocked = sendMode ? channels.filter((c) => !data.channelAvailability[c]) : [];

  return (
    <Group
      icon={<IconSend />}
      title="การส่งมอบตั๋วหลังชำระเงิน"
      sub="เลือกว่าผู้ซื้อจะเห็นตั๋วเมื่อไหร่ และระบบจะส่งอะไรตามไปให้บ้าง — มีผลกับออเดอร์ใหม่ของ event นี้เท่านั้น"
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {DELIVERY_MODE_COPY.map((opt) => (
          <label
            key={opt.key}
            style={{
              display: 'flex', gap: 11, alignItems: 'flex-start', cursor: 'pointer',
              padding: '13px 15px', borderRadius: 8,
              border: `1px solid ${mode === opt.key ? 'var(--fg)' : 'var(--border)'}`,
              background: mode === opt.key ? 'rgba(255,255,255,0.03)' : 'transparent',
            }}
          >
            <input
              type="radio" name="ticket-delivery-mode" checked={mode === opt.key}
              onChange={() => { setMode(opt.key); setSaved(false); }}
              style={{ marginTop: 3 }}
            />
            <span style={{ minWidth: 0 }}>
              <span style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', fontSize: 13.5, fontWeight: 600 }}>
                {opt.title}
                {opt.badge && (
                  <span style={{ fontSize: 10, letterSpacing: '0.08em', padding: '2px 7px', borderRadius: 4, border: '1px solid var(--border)', color: 'var(--muted)', fontWeight: 500 }}>
                    {opt.badge}
                  </span>
                )}
              </span>
              <span style={{ display: 'block', fontSize: 12.5, color: 'var(--dim)', lineHeight: 1.6, marginTop: 3 }}>{opt.body}</span>
            </span>
          </label>
        ))}
      </div>

      <div style={{ marginTop: 20, opacity: sendMode ? 1 : 0.45 }}>
        <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 8 }}>ส่งสำเนาลิงก์ตั๋วทาง</div>
        {COPY_CHANNEL_COPY.map((ch) => (
          <label
            key={ch.key}
            style={{
              display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: 8,
              fontSize: 13.5, cursor: sendMode ? 'pointer' : 'default',
            }}
          >
            <input
              type="checkbox" checked={channels.includes(ch.key)} disabled={!sendMode}
              onChange={() => toggleChannel(ch.key)}
              style={{ width: 16, height: 16, marginTop: 2, accentColor: 'var(--fg)', flex: 'none' }}
            />
            <span>
              {ch.label}
              <span style={{ display: 'block', fontSize: 11.5, color: 'var(--dim)', lineHeight: 1.55, marginTop: 2 }}>
                {ch.hint}{data.channelAvailability[ch.key] ? '' : ' · ปิดอยู่ที่ระดับแพลตฟอร์ม'}
              </span>
            </span>
          </label>
        ))}
        {sendMode && !channels.length && (
          <div style={{ fontSize: 11.5, color: '#d9b36a', marginTop: 6 }}>
            ยังไม่ได้เลือกช่องทาง — โหมดนี้จะทำงานเหมือน “แสดงตั๋วทันที” คือไม่ส่งอะไรออกไปเลย
          </div>
        )}
        {!!blocked.length && (
          <div style={{ fontSize: 11.5, color: '#d9b36a', marginTop: 6 }}>
            {blocked.join(', ')} ถูกปิดอยู่ที่ระดับแพลตฟอร์ม — เลือกไว้ได้ แต่ตอนนี้จะยังไม่มีอะไรถูกส่งออกไปทางช่องทางนั้น
          </div>
        )}
      </div>

      {mode === 'instant' && (
        <div
          style={{
            fontSize: 12, lineHeight: 1.65, marginTop: 16, padding: '11px 13px', borderRadius: 6,
            border: `1px dashed ${data.myTicketPortalEnabled ? 'var(--border)' : '#4a3f24'}`,
            color: data.myTicketPortalEnabled ? 'var(--muted)' : '#d9b36a',
          }}
        >
          โหมดนี้ไม่ส่งข้อความใด ๆ ออกไป ถ้าผู้ซื้อปิดหน้าจอไปแล้ว ทางกลับมาหาตั๋วมีทางเดียวคือหน้า
          {' '}<code>/myticket</code>
          {data.myTicketPortalEnabled
            ? ' ซึ่งเปิดใช้งานอยู่'
            : ' ซึ่งตอนนี้ยังปิดอยู่ทั้งแพลตฟอร์ม — เลือกโหมดนี้แล้วผู้ซื้อจะไม่มีทางกลับมาหาตั๋วได้เลย ติดต่อผู้ดูแลระบบให้เปิดก่อน'}
        </div>
      )}

      <div className="admin-actions" style={{ marginTop: 16 }}>
        <button className="btn-primary" style={{ padding: '9px 16px' }} disabled={saving || !dirty} onClick={save}>
          บันทึก
        </button>
        {saved && !dirty && <span style={{ fontSize: 12, color: '#9fdcb2' }}>บันทึกแล้ว</span>}
      </div>

      {err && <div className="form-error">{err}</div>}
    </Group>
  );
}

/** Fields tracked in the per-ticket draft/save-all flow — everything else (closed/visible/sort) stays immediate. */
type TicketDraftFields = Pick<TicketInput, 'nameEn' | 'nameTh' | 'priceRegular' | 'priceEarly' | 'capacity' | 'seats' | 'promotion' | 'offline' | 'serialPrefix' | 'customFields'>;

function ticketInputOf(t: AdminTicketDto): TicketDraftFields {
  return {
    nameEn: t.nameEn, nameTh: t.nameTh, priceRegular: t.priceRegular, priceEarly: t.priceEarly,
    capacity: t.capacity, seats: t.seats, promotion: t.promotion, offline: t.offline,
    serialPrefix: t.serialPrefix ?? null, customFields: t.customFields,
  };
}

function TicketsPanel({ token, eventSlug, headRight }: { token: string; eventSlug?: string; headRight: (b: ReactNode) => void }) {
  const [tickets, setTickets] = useState<AdminTicketDto[]>([]);
  const [error, setError] = useState('');
  const [newDraft, setNewDraft] = useState<TicketInput>(BLANK);
  const [adding, setAdding] = useState(false);
  const [creating, setCreating] = useState(false);
  const [addErr, setAddErr] = useState('');
  const [dragId, setDragId] = useState<string | null>(null);
  const dragIndexRef = useRef<number | null>(null);
  const [earlyBirdEnabled, setEarlyBirdEnabled] = useState(true);
  const [wristbands, setWristbands] = useState<WristbandDto[]>([]);
  const [bindings, setBindings] = useState<Record<string, WristbandBinding[]>>({});
  const [search, setSearch] = useState('');

  // Draft/save-all: each row edits a local copy; nothing hits the server until "บันทึกทั้งหมด".
  // Reload-triggering actions (toggle closed/visible, drag reorder, create) refresh `tickets`/
  // `bindings` from the server, but only *new* ticket ids get a draft initialized here — an
  // in-progress edit on another row survives because its existing draft entry is left alone.
  const [ticketDrafts, setTicketDrafts] = useState<Record<string, TicketDraftFields>>({});
  const [wbDrafts, setWbDrafts] = useState<Record<string, WristbandBinding[]>>({});
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState('');
  const [saved, setSaved] = useState(false);
  const saveErrRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (saveErr) saveErrRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [saveErr]);

  useEffect(() => {
    setTicketDrafts((prev) => {
      const next: Record<string, TicketDraftFields> = {};
      for (const t of tickets) next[t.id] = prev[t.id] ?? ticketInputOf(t);
      return next;
    });
  }, [tickets]);

  useEffect(() => {
    setWbDrafts((prev) => {
      const next: Record<string, WristbandBinding[]> = {};
      for (const t of tickets) {
        next[t.id] = prev[t.id] ?? (bindings[t.id] ?? []).filter((b) => b.qty > 0).map((b) => ({ ...b }));
      }
      return next;
    });
  }, [tickets, bindings]);

  const load = useCallback(async () => {
    try {
      const [ticketsRes, wristbandsRes, bindingsRes] = await Promise.all([
        adminApi.listTickets(token, eventSlug),
        adminApi.listWristbands(token, eventSlug),
        adminApi.getTicketWristbands(token, eventSlug),
      ]);
      setTickets(ticketsRes);
      setWristbands(wristbandsRes);
      setBindings(bindingsRes);
      setError('');
    } catch {
      setError('โหลดข้อมูลตั๋วไม่สำเร็จ');
    }
  }, [token, eventSlug]);

  useEffect(() => { load(); }, [load]);

  const ticketDirty = (t: AdminTicketDto) => {
    const d = ticketDrafts[t.id];
    return !!d && JSON.stringify(d) !== JSON.stringify(ticketInputOf(t));
  };
  const sortedWb = (rows: WristbandBinding[]) =>
    JSON.stringify([...rows].filter((r) => r.qty > 0).sort((a, b) => a.wristbandId.localeCompare(b.wristbandId)));
  const wbDirty = (t: AdminTicketDto) => sortedWb(wbDrafts[t.id] ?? []) !== sortedWb(bindings[t.id] ?? []);
  const anyDirty = tickets.some((t) => ticketDirty(t) || wbDirty(t));

  const saveAll = useCallback(async () => {
    setSaving(true);
    setSaveErr('');
    setSaved(false);
    const dirtyTickets = tickets.filter(ticketDirty);
    const dirtyWb = tickets.filter(wbDirty);
    const failedTicketIds = new Set<string>();
    const failedWbIds = new Set<string>();
    const failMessages: string[] = [];

    await Promise.allSettled([
      ...dirtyTickets.map(async (t) => {
        try {
          await adminApi.updateTicket(token, t.id, ticketDrafts[t.id], eventSlug);
        } catch (e) {
          failedTicketIds.add(t.id);
          failMessages.push(`${t.nameEn || t.id}: ${e instanceof ApiError ? e.message : 'บันทึกไม่สำเร็จ'}`);
        }
      }),
      ...dirtyWb.map(async (t) => {
        try {
          await adminApi.setTicketWristbands(token, t.id, (wbDrafts[t.id] ?? []).filter((r) => r.wristbandId && r.qty > 0), eventSlug);
        } catch (e) {
          failedWbIds.add(t.id);
          failMessages.push(`Wristband ${t.nameEn || t.id}: ${e instanceof ApiError ? e.message : 'บันทึกไม่สำเร็จ'}`);
        }
      }),
    ]);

    await load();
    setTicketDrafts((prev) => {
      const next = { ...prev };
      for (const t of dirtyTickets) if (!failedTicketIds.has(t.id)) delete next[t.id];
      return next;
    });
    setWbDrafts((prev) => {
      const next = { ...prev };
      for (const t of dirtyWb) if (!failedWbIds.has(t.id)) delete next[t.id];
      return next;
    });
    setSaving(false);
    if (failMessages.length) {
      setSaveErr(failMessages.join(' · '));
    } else {
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tickets, ticketDrafts, wbDrafts, bindings, token, eventSlug, load]);

  useEffect(() => {
    headRight(
      <button
        className="btn-primary"
        style={{ padding: '11px 18px', display: 'inline-flex', alignItems: 'center', gap: 8 }}
        disabled={saving || !anyDirty}
        onClick={saveAll}
      >
        {saved ? 'บันทึกแล้ว ✓' : saving ? 'กำลังบันทึก…' : 'บันทึกทั้งหมด'}
      </button>,
    );
    return () => headRight(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saveAll, saving, saved, anyDirty]);

  const num = (v: string) => Math.max(0, Math.round(Number(v) || 0));
  const create = async () => {
    if (creating) return;
    setCreating(true);
    setAddErr('');
    try {
      const nextSort = tickets.length ? Math.max(...tickets.map((t) => t.sort)) + 1 : 0;
      await adminApi.createTicket(token, { ...newDraft, sort: nextSort }, eventSlug);
      setNewDraft(BLANK);
      setAdding(false);
      await load();
    } catch (e) {
      setAddErr(e instanceof ApiError ? e.message : 'เพิ่มบัตรไม่สำเร็จ');
    } finally {
      setCreating(false);
    }
  };

  const handleDragStart = (index: number) => {
    dragIndexRef.current = index;
    setDragId(tickets[index].id);
  };

  const handleDragOver = (index: number) => (e: DragEvent) => {
    e.preventDefault();
    const from = dragIndexRef.current;
    if (from === null || from === index) return;
    setTickets((prev) => {
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(index, 0, moved);
      return next;
    });
    dragIndexRef.current = index;
  };

  const persistOrder = async (list: AdminTicketDto[]) => {
    const updates = list
      .map((t, i) => ({ id: t.id, sort: i, changed: t.sort !== i }))
      .filter((u) => u.changed);
    if (!updates.length) return;
    try {
      await Promise.all(updates.map((u) => adminApi.updateTicket(token, u.id, { sort: u.sort }, eventSlug)));
      await load();
    } catch {
      setError('บันทึกลำดับไม่สำเร็จ');
      await load();
    }
  };

  const handleDrop = (e: DragEvent) => {
    e.preventDefault();
    dragIndexRef.current = null;
    setDragId(null);
    persistOrder(tickets);
  };

  const handleDragEnd = () => {
    dragIndexRef.current = null;
    setDragId(null);
  };

  /**
   * Four jobs live on this screen and they are not done at the same time: the sale is configured
   * once, types are edited while building the event, discounts are a separate errand, and the
   * counter's stock is handled on the day. Stacking them made one page nobody could scan.
   */
  const [sub, setSub] = useState<'settings' | 'types' | 'discounts' | 'offline'>('settings');
  const offlineCount = tickets.filter((t) => t.offline).length;

  const totalQuota = tickets.reduce((sum, t) => sum + t.capacity, 0);
  const openZones = tickets.filter((t) => !t.closed).length;
  const totalEarlyValue = tickets.reduce((sum, t) => sum + t.capacity * (t.priceEarly || t.priceRegular), 0);

  const searchActive = search.trim().length > 0;
  const q = search.trim().toLowerCase();
  const filteredTickets = searchActive
    ? tickets.filter((t) => t.nameEn.toLowerCase().includes(q) || t.nameTh.toLowerCase().includes(q))
    : tickets;

  return (
    <>
      {error && <div className="form-error">{error}</div>}
      {saveErr && <div className="form-error" ref={saveErrRef}>{saveErr}</div>}

      <div className="admin-subtabs">
        {([
          { key: 'settings', label: 'สถานะและตั้งค่าการขายตั๋ว' },
          { key: 'types', label: 'สร้างประเภทตั๋ว', count: tickets.length },
          { key: 'discounts', label: 'ส่วนลดและตัวแทนขาย' },
          { key: 'offline', label: 'จัดการบัตรขายหน้างาน', count: offlineCount },
        ] as const).map((s) => (
          <button
            key={s.key}
            type="button"
            className={`admin-subtab${sub === s.key ? ' active' : ''}`}
            onClick={() => setSub(s.key)}
          >
            {s.label}
            {'count' in s && s.count > 0 && <span className="admin-subtab-count">{s.count}</span>}
          </button>
        ))}
      </div>

      {sub === 'settings' && (
      <>
      <div className="admin-substat-grid">
        <StatTile label="โควตารวมทั้งหมด" value={`${totalQuota.toLocaleString()} ใบ`} />
        <StatTile label="โซนตั๋วที่เปิดขาย" value={`${openZones} โซน`} />
        <StatTile label="สถานะ Early Bird" value={earlyBirdEnabled ? 'กำลังใช้งาน' : 'ปิดใช้งาน'} />
        <StatTile label="มูลค่ารวม (Early Bird)" value={fmt(totalEarlyValue)} />
      </div>

      <EarlyBirdSettings token={token} eventSlug={eventSlug} onEnabledChange={setEarlyBirdEnabled} />
      <CheckoutSettings token={token} eventSlug={eventSlug} />
      </>
      )}

      {sub === 'types' && (
      <>
      <div className="admin-group">
        <div className="admin-group-body">
          {!adding ? (
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
              <input
                style={{ flex: '1 1 220px' }}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="ค้นหาโซนหรือชื่อตั๋ว..."
              />
              <button className="btn-primary" style={{ padding: '11px 20px' }} onClick={() => setAdding(true)}>
                + เพิ่มประเภทตั๋วใหม่
              </button>
            </div>
          ) : (
            <>
              <div className="card-step">เพิ่มประเภทตั๋วใหม่</div>
              <div className="ticket-edit-rows">
                <label className="ticket-check">
                  <input type="checkbox" checked={newDraft.promotion} onChange={(e) => setNewDraft({ ...newDraft, promotion: e.target.checked })} />
                  โปรโมชั่น
                </label>
                <label className="ticket-check" title="บัตรแข็ง/บัตรกระดาษที่พิมพ์ไว้ล่วงหน้าแล้วขายหน้างาน — ประเภทนี้จะไม่แสดงบนหน้าขายออนไลน์">
                  <input type="checkbox" checked={newDraft.offline ?? false} onChange={(e) => setNewDraft({ ...newDraft, offline: e.target.checked })} />
                  บัตรขายหน้างาน (ไม่ขายออนไลน์)
                </label>
                {newDraft.offline && (
            <label style={{ display: 'grid', gap: 4, fontSize: 12, maxWidth: 260 }}>
              รหัสนำหน้าเลขบัตร <span style={{ color: 'var(--dim)', fontWeight: 400 }}>(อังกฤษ/ตัวเลข 4 หลัก)</span>
              <input
                value={newDraft.serialPrefix ?? ''}
                onChange={(e) => setNewDraft({ ...newDraft, serialPrefix: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 4) })}
                placeholder="MPAS"
                style={{ fontFamily: 'var(--mono)', letterSpacing: 2 }}
              />
              <span style={{ color: 'var(--dim)', fontSize: 11 }}>
                เลขบัตรจะเดินต่อจากรหัสนี้ เช่น MPAS-0001 · ใช้ซ้ำกับประเภทตั๋วอื่นในงานเดียวกันไม่ได้
              </span>
            </label>
          )}
                <div className="ticket-edit-row-4">
                  <label>ชื่อ (EN)<input value={newDraft.nameEn} onChange={(e) => setNewDraft({ ...newDraft, nameEn: e.target.value })} placeholder="ZONE D — STANDING" /></label>
                  <label>ชื่อ (TH)<input value={newDraft.nameTh} onChange={(e) => setNewDraft({ ...newDraft, nameTh: e.target.value })} placeholder="บัตรโซน D" /></label>
                  <label>ราคาปกติ<PriceInput value={newDraft.priceRegular} onChange={(v) => setNewDraft({ ...newDraft, priceRegular: v })} /></label>
                  <label>
                    ราคา Early <span style={{ color: 'var(--dim)', fontWeight: 400 }}>{earlyBirdEnabled ? '(ว่าง = ใช้ราคาปกติ)' : '(ปิดใช้งาน Early Bird)'}</span>
                    <PriceInput value={newDraft.priceEarly} onChange={(v) => setNewDraft({ ...newDraft, priceEarly: v })} disabled={!earlyBirdEnabled} />
                  </label>
                </div>
                <div className="ticket-edit-row-2">
                  <label>โควตา (Quota)<input type="number" value={newDraft.capacity || ''} onChange={(e) => setNewDraft({ ...newDraft, capacity: num(e.target.value) })} /></label>
                  <label>ที่นั่ง/ใบ<input type="number" min={1} value={newDraft.seats} onChange={(e) => setNewDraft({ ...newDraft, seats: Math.max(1, num(e.target.value)) })} /></label>
                </div>
              </div>

              <CustomFieldsEditor fields={newDraft.customFields} onChange={(next) => setNewDraft({ ...newDraft, customFields: next })} />

              {addErr && <div className="form-error">{addErr}</div>}
              <div className="admin-actions">
                <button className="btn-primary" style={{ padding: '9px 18px' }} disabled={creating} onClick={create}>
                  {creating ? <span className="btn-spinner" aria-hidden="true" /> : 'สร้าง'}
                </button>
                <button className="btn-ghost" disabled={creating} onClick={() => { setAdding(false); setNewDraft(BLANK); setAddErr(''); }}>ยกเลิก</button>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="admin-ticket-head-row">
        <div>สถานะ</div><div>ประเภท</div><div>จำนวน</div><div>ขายแล้ว / โควตา</div><div></div>
      </div>

      {filteredTickets.map((t) => {
        const i = tickets.indexOf(t);
        return (
          <TicketRow
            key={t.id}
            ticket={t}
            token={token}
            eventSlug={eventSlug}
            onChanged={load}
            dragging={dragId === t.id}
            onDragStart={() => { if (!searchActive) handleDragStart(i); }}
            onDragOver={(e) => { if (!searchActive) handleDragOver(i)(e); }}
            onDrop={(e) => { if (!searchActive) handleDrop(e); }}
            onDragEnd={handleDragEnd}
            earlyBirdEnabled={earlyBirdEnabled}
            wristbands={wristbands}
            draft={ticketDrafts[t.id] ?? ticketInputOf(t)}
            onDraftChange={(next) => setTicketDrafts((prev) => ({ ...prev, [t.id]: next }))}
            wbRows={wbDrafts[t.id] ?? []}
            onWbRowsChange={(next) => setWbDrafts((prev) => ({ ...prev, [t.id]: next }))}
          />
        );
      })}

      </>
      )}

      {sub === 'discounts' && (
      <>
        <CouponsPanel token={token} eventSlug={eventSlug} />
        <ResellerReportPanel token={token} eventSlug={eventSlug} />
      </>
      )}

      {/* TICK-077/078 — stock for the counter: printing runs, selling them, and the runs so far. */}
      {sub === 'offline' && (
        <OfflineBatchPanel token={token} eventSlug={eventSlug} tickets={tickets} onChanged={() => { void load(); }} />
      )}
    </>
  );
}

const BLANK_COUPON: CouponInput = { code: '', pct: null, flat: null, usesLeft: null };
const BLANK_RESELLER: ResellerPartnerInput = { partnerName: '', contactInfo: '', commissionType: 'flat_per_ticket', commissionFlat: 0, commissionPct: null };

function CouponsPanel({ token, eventSlug }: { token: string; eventSlug?: string }) {
  const [coupons, setCoupons] = useState<AdminCouponDto[]>([]);
  const [error, setError] = useState('');
  const [adding, setAdding] = useState(false);
  const [addErr, setAddErr] = useState('');
  const [draft, setDraft] = useState<CouponInput>(BLANK_COUPON);
  const [discountKind, setDiscountKind] = useState<'pct' | 'flat'>('pct');
  const [limited, setLimited] = useState(false);
  const [isReseller, setIsReseller] = useState(false);
  const [resellerDraft, setResellerDraft] = useState<ResellerPartnerInput>(BLANK_RESELLER);
  const [busyCode, setBusyCode] = useState<string | null>(null);
  const [editingResellerCode, setEditingResellerCode] = useState<string | null>(null);
  const [editReseller, setEditReseller] = useState<ResellerPartnerInput>(BLANK_RESELLER);
  const [editErr, setEditErr] = useState('');

  const load = useCallback(async () => {
    try {
      setCoupons((await adminApi.getStats(token, eventSlug)).coupons);
      setError('');
    } catch {
      setError('โหลดรายการโค้ดส่วนลดไม่สำเร็จ');
    }
  }, [token, eventSlug]);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    setAddErr('');
    if (isReseller && !resellerDraft.partnerName.trim()) {
      setAddErr('กรุณาระบุชื่อพาร์ทเนอร์');
      return;
    }
    try {
      await adminApi.createCoupon(token, {
        code: draft.code,
        pct: discountKind === 'pct' ? draft.pct : null,
        flat: discountKind === 'flat' ? draft.flat : null,
        usesLeft: limited ? draft.usesLeft : null,
        reseller: isReseller ? resellerDraft : null,
      }, eventSlug);
      setDraft(BLANK_COUPON);
      setDiscountKind('pct');
      setLimited(false);
      setIsReseller(false);
      setResellerDraft(BLANK_RESELLER);
      setAdding(false);
      await load();
    } catch (e) {
      setAddErr(e instanceof ApiError ? e.message : 'สร้างโค้ดไม่สำเร็จ');
    }
  };

  const startEditReseller = (c: AdminCouponDto) => {
    setEditErr('');
    setEditingResellerCode(c.code);
    setEditReseller(
      c.reseller
        ? {
            partnerName: c.reseller.partnerName, contactInfo: c.reseller.contactInfo,
            commissionType: c.reseller.commissionType, commissionFlat: c.reseller.commissionFlat, commissionPct: c.reseller.commissionPct,
          }
        : { ...BLANK_RESELLER, partnerName: c.resellerPartnerName ?? '' },
    );
  };

  const saveReseller = async (code: string) => {
    setEditErr('');
    if (!editReseller.partnerName.trim()) {
      setEditErr('กรุณาระบุชื่อพาร์ทเนอร์');
      return;
    }
    setBusyCode(code);
    try {
      await adminApi.setCouponReseller(token, code, editReseller, eventSlug);
      setEditingResellerCode(null);
      await load();
    } catch (e) {
      setEditErr(e instanceof ApiError ? e.message : 'บันทึกไม่สำเร็จ');
    } finally {
      setBusyCode(null);
    }
  };

  const removeReseller = async (code: string) => {
    if (!window.confirm(`เลิกเป็นโค้ดตัวแทนขาย "${code}" ? (โค้ดส่วนลดยังใช้งานได้ตามปกติ)`)) return;
    setBusyCode(code);
    try {
      await adminApi.removeCouponReseller(token, code, eventSlug);
      setEditingResellerCode(null);
      await load();
    } catch {
      setError('ทำรายการไม่สำเร็จ');
    } finally {
      setBusyCode(null);
    }
  };

  const toggleActive = async (c: AdminCouponDto) => {
    setBusyCode(c.code);
    try {
      await adminApi.setCouponActive(token, c.code, !c.active, eventSlug);
      await load();
    } catch {
      setError('ทำรายการไม่สำเร็จ');
    } finally {
      setBusyCode(null);
    }
  };

  const remove = async (c: AdminCouponDto) => {
    if (!window.confirm(`ลบโค้ด "${c.code}" ?`)) return;
    setBusyCode(c.code);
    try {
      await adminApi.deleteCoupon(token, c.code, eventSlug);
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'ลบโค้ดไม่สำเร็จ');
    } finally {
      setBusyCode(null);
    }
  };

  return (
    <Group icon={<IconTicket />} title="ส่วนลด" sub="ออกรหัสส่วนลดสำหรับใช้ตอน Checkout">
      {error && <div className="form-error">{error}</div>}

      {!adding ? (
        <button className="btn-primary" style={{ padding: '11px 20px' }} onClick={() => setAdding(true)}>
          + สร้างโค้ดส่วนลด
        </button>
      ) : (
        <>
          <div className="card-step">สร้างโค้ดส่วนลดใหม่</div>
          <div className="ticket-edit-grid">
            <label>โค้ด<input value={draft.code} onChange={(e) => setDraft({ ...draft, code: e.target.value.toUpperCase() })} placeholder="เช่น MOON10" /></label>
            <label>
              ประเภทส่วนลด
              <select value={discountKind} onChange={(e) => setDiscountKind(e.target.value as 'pct' | 'flat')}>
                <option value="pct">เปอร์เซ็นต์ (%)</option>
                <option value="flat">จำนวนเงิน (บาท)</option>
              </select>
            </label>
            {discountKind === 'pct' ? (
              <label>ส่วนลด (%)<input type="number" min={1} max={100} value={draft.pct ?? ''} onChange={(e) => setDraft({ ...draft, pct: Math.max(0, Math.round(Number(e.target.value) || 0)) })} /></label>
            ) : (
              <label>ส่วนลด (บาท)<PriceInput value={draft.flat ?? 0} onChange={(v) => setDraft({ ...draft, flat: v })} /></label>
            )}
            <label className="ticket-check">
              <input type="checkbox" checked={limited} onChange={(e) => setLimited(e.target.checked)} />
              จำกัดจำนวนสิทธิ์การใช้
            </label>
            {limited && (
              <label>จำนวนสิทธิ์<input type="number" min={1} value={draft.usesLeft ?? ''} onChange={(e) => setDraft({ ...draft, usesLeft: Math.max(1, Math.round(Number(e.target.value) || 0)) })} /></label>
            )}
            <label className="ticket-check">
              <input type="checkbox" checked={isReseller} onChange={(e) => setIsReseller(e.target.checked)} />
              โค้ดนี้เป็นโค้ดตัวแทนขาย/รีเซลเลอร์ (reseller/referral)
            </label>
          </div>
          {isReseller && (
            <ResellerFields value={resellerDraft} onChange={setResellerDraft} />
          )}
          {addErr && <div className="form-error">{addErr}</div>}
          <div className="admin-actions">
            <button className="btn-primary" style={{ padding: '9px 18px' }} disabled={!draft.code.trim()} onClick={create}>สร้าง</button>
            <button className="btn-ghost" onClick={() => { setAdding(false); setDraft(BLANK_COUPON); setDiscountKind('pct'); setLimited(false); setIsReseller(false); setResellerDraft(BLANK_RESELLER); setAddErr(''); }}>ยกเลิก</button>
          </div>
        </>
      )}

      {coupons.length === 0 ? (
        <div style={{ color: 'var(--dim)', fontSize: 13.5, padding: '18px 4px' }}>ยังไม่มีโค้ดส่วนลด</div>
      ) : (
        coupons.map((c) => (
          <div key={c.code} className="admin-group" style={{ marginTop: 12 }}>
            <div style={{ display: 'flex', gap: 14, alignItems: 'center', padding: '14px 20px', flexWrap: 'wrap' }}>
              <span className="order-code" style={{ marginTop: 0 }}>{c.code}</span>
              <div style={{ flex: '1 1 160px', fontSize: 13, color: 'var(--muted)' }}>
                {c.pct != null ? `ลด ${c.pct}%` : `ลด ${fmt(c.flat ?? 0)}`}
                {' · '}
                {c.usesLeft == null ? 'ไม่จำกัดสิทธิ์' : `เหลือสิทธิ์ ${c.usesLeft}`}
                {' · '}
                ใช้แล้ว {c.uses ?? 0} ครั้ง (ชำระแล้ว {c.paidUses ?? 0}) · ส่วนลดรวม {fmt(c.totalDiscount ?? 0)}
              </div>
              {c.isReseller && (
                <span style={{
                  fontSize: 11.5, padding: '4px 10px', borderRadius: 7, fontWeight: 500,
                  background: '#241c16', border: '1px solid #4a3826', color: '#d9a86a',
                }}>
                  ตัวแทนขาย: {c.reseller?.partnerName ?? c.resellerPartnerName}
                </span>
              )}
              <span style={{
                fontSize: 11.5, padding: '4px 10px', borderRadius: 7, fontWeight: 500,
                background: c.active ? '#16241a' : '#241616',
                border: `1px solid ${c.active ? '#244a2c' : '#4a2626'}`,
                color: c.active ? '#8fca9a' : '#e08b8b',
              }}>
                {c.active ? 'ใช้งานได้' : 'ปิดใช้งาน'}
              </span>
              <button
                className="btn-link"
                disabled={busyCode === c.code}
                onClick={() => (editingResellerCode === c.code ? setEditingResellerCode(null) : startEditReseller(c))}
              >
                {c.isReseller ? 'แก้ไขตัวแทนขาย' : '+ ตั้งเป็นตัวแทนขาย'}
              </button>
              <button
                className="admin-review-icon-btn"
                style={{ width: 36, height: 36 }}
                title={c.active ? 'ปิดใช้งาน' : 'เปิดใช้งาน'}
                disabled={busyCode === c.code}
                onClick={() => toggleActive(c)}
              >
                {c.active ? <IconEyeOff /> : <IconEye />}
              </button>
              <button
                className="btn-link"
                style={{ color: '#d99a9a' }}
                disabled={busyCode === c.code}
                onClick={() => remove(c)}
              >
                ลบ
              </button>
            </div>
            {editingResellerCode === c.code && (
              <div style={{ padding: '0 20px 16px' }}>
                <ResellerFields value={editReseller} onChange={setEditReseller} />
                {editErr && <div className="form-error">{editErr}</div>}
                <div className="admin-actions">
                  <button className="btn-primary" style={{ padding: '9px 18px' }} disabled={busyCode === c.code} onClick={() => saveReseller(c.code)}>บันทึก</button>
                  {c.isReseller && (
                    <button className="btn-link" style={{ color: '#d99a9a' }} disabled={busyCode === c.code} onClick={() => removeReseller(c.code)}>เลิกเป็นตัวแทนขาย</button>
                  )}
                  <button className="btn-ghost" onClick={() => setEditingResellerCode(null)}>ยกเลิก</button>
                </div>
              </div>
            )}
          </div>
        ))
      )}
    </Group>
  );
}

function ResellerFields({ value, onChange }: { value: ResellerPartnerInput; onChange: (v: ResellerPartnerInput) => void }) {
  return (
    <div className="ticket-edit-grid">
      <label>ชื่อพาร์ทเนอร์<input value={value.partnerName} onChange={(e) => onChange({ ...value, partnerName: e.target.value })} placeholder="เช่น โรงแรมริมทะเล" /></label>
      <label>ข้อมูลติดต่อ (ถ้ามี)<input value={value.contactInfo ?? ''} onChange={(e) => onChange({ ...value, contactInfo: e.target.value })} placeholder="เบอร์โทร / Line / อีเมล" /></label>
      <label>
        รูปแบบค่าคอมมิชชั่น
        <select
          value={value.commissionType}
          onChange={(e) => onChange({ ...value, commissionType: e.target.value as ResellerCommissionType })}
        >
          <option value="flat_per_ticket">จำนวนเงินต่อใบ (บาท)</option>
          <option value="pct_discounted_price">เปอร์เซ็นต์ของยอดหลังส่วนลด (%)</option>
        </select>
      </label>
      {value.commissionType === 'flat_per_ticket' ? (
        <label>ค่าคอมมิชชั่น (บาท/ใบ)<PriceInput value={value.commissionFlat ?? 0} onChange={(v) => onChange({ ...value, commissionFlat: v })} /></label>
      ) : (
        <label>ค่าคอมมิชชั่น (%)<input type="number" min={0} max={100} value={value.commissionPct ?? ''} onChange={(e) => onChange({ ...value, commissionPct: Math.max(0, Math.min(100, Math.round(Number(e.target.value) || 0))) })} /></label>
      )}
    </div>
  );
}

function downloadCsv(filename: string, rows: (string | number)[][]) {
  const csv = rows.map((row) => row.map((cell) => {
    const s = String(cell);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  }).join(',')).join('\r\n');
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

const RESELLER_COMMISSION_LABEL: Record<ResellerCommissionType, string> = {
  flat_per_ticket: 'บาท/ใบ',
  pct_discounted_price: '% ของยอดหลังส่วนลด',
};

function ResellerReportPanel({ token, eventSlug }: { token: string; eventSlug?: string }) {
  const [rows, setRows] = useState<ResellerReportRowDto[]>([]);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      setRows(await adminApi.getResellerReport(token, eventSlug));
      setError('');
    } catch {
      setError('โหลดรายงานตัวแทนขายไม่สำเร็จ');
    }
  }, [token, eventSlug]);

  useEffect(() => { load(); }, [load]);

  const exportCsv = () => {
    downloadCsv(`reseller-report-${new Date().toISOString().slice(0, 10)}.csv`, [
      ['Code', 'Partner', 'Contact', 'Commission Rate', 'Tickets Sold', 'Orders Paid', 'Gross Revenue', 'Commission Owed'],
      ...rows.map((r) => [
        r.code, r.partnerName, r.contactInfo,
        r.commissionType === 'flat_per_ticket' ? `${r.commissionFlat} บาท/ใบ` : `${r.commissionPct}%`,
        r.ticketsSold, r.ordersPaid, r.grossRevenue, r.commissionOwed,
      ]),
    ]);
  };

  if (rows.length === 0 && !error) return null;

  const totalCommission = rows.reduce((a, r) => a + r.commissionOwed, 0);

  return (
    <Group icon={<IconTicket />} title="รายงานตัวแทนขาย" sub="ยอดขายและค่าคอมมิชชั่นต่อโค้ดตัวแทนขาย สำหรับเคลียร์ยอดหลังงาน">
      {error && <div className="form-error">{error}</div>}
      {rows.length > 0 && (
        <>
          <div className="admin-actions" style={{ marginBottom: 10 }}>
            <button className="btn-ghost" onClick={exportCsv}>Export CSV</button>
          </div>
          {rows.map((r) => (
            <div key={r.code} className="admin-group" style={{ marginTop: 12 }}>
              <div style={{ display: 'flex', gap: 14, alignItems: 'center', padding: '14px 20px', flexWrap: 'wrap' }}>
                <span className="order-code" style={{ marginTop: 0 }}>{r.code}</span>
                <div style={{ flex: '1 1 220px', fontSize: 13, color: 'var(--muted)' }}>
                  {r.partnerName}{r.contactInfo ? ` · ${r.contactInfo}` : ''}
                  {' · '}
                  {r.commissionType === 'flat_per_ticket' ? `${fmt(r.commissionFlat ?? 0)} / ใบ` : `${r.commissionPct}% ${RESELLER_COMMISSION_LABEL.pct_discounted_price}`}
                </div>
                <div style={{ fontSize: 13, color: 'var(--muted)', textAlign: 'right' }}>
                  ตั๋วขายแล้ว {r.ticketsSold} ใบ · {r.ordersPaid} ออเดอร์<br />
                  ยอดขายรวม {fmt(r.grossRevenue)}
                </div>
                <span style={{
                  fontSize: 13, padding: '6px 12px', borderRadius: 7, fontWeight: 600,
                  background: '#241c16', border: '1px solid #4a3826', color: '#d9a86a',
                }}>
                  ค่าคอมมิชชั่น {fmt(r.commissionOwed)}
                </span>
              </div>
            </div>
          ))}
          <div style={{ padding: '14px 20px', fontSize: 13.5, fontWeight: 600, textAlign: 'right' }}>
            รวมค่าคอมมิชชั่นทั้งหมด {fmt(totalCommission)}
          </div>
        </>
      )}
    </Group>
  );
}

/* ---------- wristbands ---------- */
const WRISTBAND_COLOR_META: Record<WristbandColor, { label: string; swatch: string }> = {
  red: { label: 'แดง', swatch: '#d9534f' },
  blue: { label: 'ฟ้า', swatch: '#4a90d9' },
  green: { label: 'เขียว', swatch: '#5cb85c' },
  yellow: { label: 'เหลือง', swatch: '#e0c341' },
  orange: { label: 'ส้ม', swatch: '#e08b3a' },
  purple: { label: 'ม่วง', swatch: '#9b6bd9' },
  white: { label: 'ขาว', swatch: '#e8e8e8' },
};

function WristbandColorPicker({ value, onChange }: { value: WristbandColor; onChange: (c: WristbandColor) => void }) {
  return (
    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
      {WRISTBAND_COLORS.map((c) => {
        const meta = WRISTBAND_COLOR_META[c];
        const selected = c === value;
        return (
          <button
            key={c}
            type="button"
            title={meta.label}
            aria-label={meta.label}
            onClick={() => onChange(c)}
            style={{
              width: 26,
              height: 26,
              borderRadius: '50%',
              background: meta.swatch,
              border: '1px solid var(--border)',
              boxShadow: selected ? '0 0 0 2px var(--panel), 0 0 0 4px var(--fg)' : 'none',
              cursor: 'pointer',
              padding: 0,
            }}
          />
        );
      })}
    </div>
  );
}

function WristbandSwatch({ color }: { color: WristbandColor }) {
  const meta = WRISTBAND_COLOR_META[color];
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <span style={{ width: 12, height: 12, borderRadius: '50%', background: meta.swatch, border: '1px solid var(--border)', display: 'inline-block' }} />
      {meta.label}
    </span>
  );
}

function WristbandPanel({ token, eventSlug }: { token: string; eventSlug?: string }) {
  const [wristbands, setWristbands] = useState<WristbandDto[]>([]);
  const [error, setError] = useState('');
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  const [color, setColor] = useState<WristbandColor>('red');
  const [addErr, setAddErr] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setWristbands(await adminApi.listWristbands(token, eventSlug));
      setError('');
    } catch {
      setError('โหลดรายการ Wristband ไม่สำเร็จ');
    }
  }, [token, eventSlug]);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!name.trim()) return;
    setAddErr('');
    try {
      await adminApi.createWristband(token, name.trim(), color, eventSlug);
      setName('');
      setColor('red');
      setAdding(false);
      await load();
    } catch (e) {
      setAddErr(e instanceof ApiError ? e.message : 'สร้าง Wristband ไม่สำเร็จ');
    }
  };

  const remove = async (w: WristbandDto) => {
    if (!window.confirm(`ลบ Wristband "${w.name}" ? (จะถูกถอดออกจากตั๋วทุกประเภทที่ผูกไว้ด้วย)`)) return;
    setBusyId(w.id);
    try {
      await adminApi.deleteWristband(token, w.id, eventSlug);
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'ลบไม่สำเร็จ');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Group icon={<IconWristband />} title="Wristband" sub="สร้างริสแบนด์แต่ละสี — ผูกกับตั๋วได้ที่หน้าจัดการตั๋ว">
      {error && <div className="form-error">{error}</div>}

      {!adding ? (
        <button className="btn-primary" style={{ padding: '11px 20px' }} onClick={() => setAdding(true)}>
          + สร้าง Wristband ใหม่
        </button>
      ) : (
        <>
          <div className="card-step">สร้าง Wristband ใหม่</div>
          <div className="ticket-edit-grid">
            <label>ชื่อ<input value={name} onChange={(e) => setName(e.target.value)} placeholder="เช่น VIP, โซน A" /></label>
            <label>
              สี
              <WristbandColorPicker value={color} onChange={setColor} />
            </label>
          </div>
          {addErr && <div className="form-error">{addErr}</div>}
          <div className="admin-actions">
            <button className="btn-primary" style={{ padding: '9px 18px' }} disabled={!name.trim()} onClick={create}>สร้าง</button>
            <button className="btn-ghost" onClick={() => { setAdding(false); setName(''); setColor('red'); setAddErr(''); }}>ยกเลิก</button>
          </div>
        </>
      )}

      {wristbands.length === 0 ? (
        <div style={{ color: 'var(--dim)', fontSize: 13.5, padding: '18px 4px' }}>ยังไม่มี Wristband</div>
      ) : (
        wristbands.map((w) => (
          <div key={w.id} className="admin-group" style={{ marginTop: 12 }}>
            <div style={{ display: 'flex', gap: 14, alignItems: 'center', padding: '14px 20px', flexWrap: 'wrap' }}>
              <div style={{ flex: '1 1 160px', fontSize: 13.5, fontWeight: 500 }}>{w.name}</div>
              <WristbandSwatch color={w.color} />
              <button className="btn-link" style={{ color: '#d99a9a' }} disabled={busyId === w.id} onClick={() => remove(w)}>ลบ</button>
            </div>
          </div>
        ))
      )}
    </Group>
  );
}

/* ---------- payment accounts ---------- */

const PROVIDER_LABEL: Record<PaymentProvider, string> = {
  promptpay: 'พร้อมเพย์',
  kshop: 'K-Shop',
  mae_manee: 'แม่มณี',
  tungngern: 'ถุงเงิน',
  bank: 'บัญชีธนาคาร',
};
const PROMPTPAY_TYPE_LABEL: Record<'msisdn' | 'natid' | 'ewallet', string> = {
  msisdn: 'เบอร์โทรศัพท์',
  natid: 'เลขบัตรประชาชน',
  ewallet: 'eWallet ID',
};
const REF1_PROVIDERS: PaymentProvider[] = ['kshop', 'mae_manee', 'tungngern'];

function emptyDraft(provider: PaymentProvider): PaymentAccountInput {
  return {
    provider,
    identifier: '',
    promptpayType: provider === 'promptpay' ? 'msisdn' : null,
    merchantName: null,
    displayName: '',
    bankName: null,
    bankCode: null,
    qrImageKey: null,
    receiverProxyId: null,
  };
}

/* ---------- LINE OA ของผู้จัด ---------- */

const LINE_OA_STATUS_LABEL: Record<LineOaStatusDto, string> = {
  draft: 'ร่าง — ยังกรอกไม่ครบ',
  verification_pending: 'รอตรวจสอบการเชื่อมต่อ',
  active: 'ใช้งานอยู่',
  verification_failed: 'ตรวจสอบไม่ผ่าน',
  disabled: 'ปิดใช้งานอยู่',
  credential_invalid: 'credential ใช้ไม่ได้แล้ว',
};

const LINE_OA_FIELD_LABEL: Record<string, string> = {
  displayName: 'ชื่อที่แสดง',
  basicId: 'LINE Basic ID',
  addFriendUrl: 'ลิงก์เพิ่มเพื่อน',
  messagingChannelId: 'Messaging Channel ID',
  messagingSecret: 'Channel secret',
  messagingAccessToken: 'Channel access token',
  deploymentSecrets: 'secret ฝั่ง deployment',
};

const LINE_OA_CHECK_LABEL: Record<LineOaCheckDto['id'], string> = {
  identifiers: 'รูปแบบข้อมูล',
  credential: 'credential ใช้งานได้',
  consistency: 'ตรงกับ OA ที่ LINE ตอบ',
  webhook: 'Webhook',
  login_channel: 'LINE Login',
};

/** '?' is not a failure — see the note beside the check list. */
const LINE_OA_CHECK_MARK: Record<LineOaCheckDto['status'], string> = {
  passed: '✓', failed: '✕', skipped: '–', unverified: '?',
};

/**
 * TICK-064 — the organizer's own OA: the add-friend button on the payment-result screen, and
 * (optionally) the credentials that let it be verified.
 *
 * Owner-only, mirroring the server: `requireOwner` gates every route behind this panel, and the
 * tab is filtered out for staff for the same reason.
 *
 * Nothing here can display a secret, because nothing the server returns contains one. The fields
 * below write; `secretsConfigured` is all that is ever read back.
 */
function LineOaPanel({ token, eventSlug }: { token: string; eventSlug?: string }) {
  const [view, setView] = useState<EventLineOaDto | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState<LineOaVerifyDto['report'] | null>(null);
  const [audit, setAudit] = useState<LineOaAuditEntryDto[] | null>(null);
  const [message, setMessage] = useState('');
  const [draft, setDraft] = useState({ displayName: '', basicId: '', addFriendUrl: '', messagingChannelId: '' });
  const [wantsMessaging, setWantsMessaging] = useState(false);
  const [secrets, setSecrets] = useState({ messagingChannelSecret: '', messagingAccessToken: '' });

  const load = useCallback(async () => {
    try {
      const next = await lineOaApi.get(token, eventSlug);
      setView(next);
      setMessage(next.settings.message);
      if (next.connection) {
        setDraft({
          displayName: next.connection.displayName,
          basicId: next.connection.basicId ?? '',
          addFriendUrl: next.connection.addFriendUrl ?? '',
          messagingChannelId: next.connection.messagingChannelId ?? '',
        });
        setWantsMessaging(next.connection.capability === 'messaging');
      }
      setError('');
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'โหลดการตั้งค่า LINE OA ไม่สำเร็จ');
    }
  }, [token, eventSlug]);

  useEffect(() => { load(); }, [load]);

  /** One wrapper so no action can leave the button spinning after it throws. */
  const run = async (fn: () => Promise<unknown>, fallback: string) => {
    setBusy(true);
    setError('');
    try {
      await fn();
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : fallback);
    } finally {
      setBusy(false);
    }
  };

  const connection = view?.connection ?? null;

  const create = () => run(async () => {
    await lineOaApi.create(token, {
      displayName: draft.displayName,
      basicId: draft.basicId || null,
      addFriendUrl: draft.addFriendUrl || null,
      messagingChannelId: wantsMessaging ? draft.messagingChannelId || null : null,
      capability: wantsMessaging ? 'messaging' : 'add_friend_only',
    }, eventSlug);
  }, 'สร้างการตั้งค่าไม่สำเร็จ');

  const save = () => run(async () => {
    await lineOaApi.update(token, {
      displayName: draft.displayName,
      basicId: draft.basicId || null,
      addFriendUrl: draft.addFriendUrl || null,
      messagingChannelId: wantsMessaging ? draft.messagingChannelId || null : null,
      capability: wantsMessaging ? 'messaging' : 'add_friend_only',
    }, eventSlug);
  }, 'บันทึกไม่สำเร็จ');

  const rotate = () => run(async () => {
    await lineOaApi.rotateSecrets(token, {
      ...(secrets.messagingChannelSecret ? { messagingChannelSecret: secrets.messagingChannelSecret } : {}),
      ...(secrets.messagingAccessToken ? { messagingAccessToken: secrets.messagingAccessToken } : {}),
    }, eventSlug);
    // Cleared immediately: there is no reason for a credential to sit in a form field after it
    // has been sent, and the server will never send it back to repopulate one.
    setSecrets({ messagingChannelSecret: '', messagingAccessToken: '' });
  }, 'บันทึก secret ไม่สำเร็จ');

  const verify = async () => {
    setBusy(true);
    setError('');
    try {
      const result = await lineOaApi.verify(token, eventSlug);
      setReport(result.report);
      await load();
    } catch (e) {
      setReport(null);
      setError(e instanceof ApiError ? e.message : 'ตรวจสอบการเชื่อมต่อไม่สำเร็จ');
    } finally {
      setBusy(false);
    }
  };

  const statusTone = (status: LineOaStatusDto) => (
    status === 'active' ? 'var(--ok, #16a34a)'
      : status === 'verification_failed' || status === 'credential_invalid' ? 'var(--danger, #dc2626)'
        : 'var(--dim)'
  );

  return (
    <Group
      icon={<IconUsers />}
      title="LINE OA ของผู้จัด"
      sub="ปุ่มเพิ่มเพื่อน OA ของคุณ แสดงท้ายหน้าสถานะคำสั่งซื้อของลูกค้า — ไม่เกี่ยวกับการส่งตั๋ว และลูกค้าที่ไม่กดก็ได้ตั๋วตามปกติ"
    >
      {error && <div className="form-error">{error}</div>}

      <div style={{ fontSize: 12.5, color: 'var(--dim)', marginBottom: 14 }}>
        การส่งตั๋วผ่าน JamTick LINE OA: {view?.platform.ready ? 'พร้อมใช้งาน' : 'ยังไม่เปิดใช้งาน'}
        {' · '}environment: {view?.environment ?? '—'}
      </div>

      {!connection ? (
        <>
          <div className="card-step">เพิ่ม LINE OA ของผู้จัด</div>
          <div className="ticket-edit-grid" style={{ marginTop: 12 }}>
            <label>
              ชื่อที่แสดงใน JamTick
              <input value={draft.displayName} onChange={(e) => setDraft((d) => ({ ...d, displayName: e.target.value }))} placeholder="เช่น Moonfield Fest OA" />
            </label>
            <label>
              LINE Basic ID
              <input value={draft.basicId} onChange={(e) => setDraft((d) => ({ ...d, basicId: e.target.value }))} placeholder="@moonfield" />
            </label>
            <label>
              ลิงก์เพิ่มเพื่อน
              <input value={draft.addFriendUrl} onChange={(e) => setDraft((d) => ({ ...d, addFriendUrl: e.target.value }))} placeholder="https://lin.ee/xxxxxxx" />
            </label>
          </div>
          <button className="btn-primary" style={{ marginTop: 14, padding: '11px 20px' }} disabled={busy || !draft.displayName} onClick={create}>
            บันทึก
          </button>
        </>
      ) : (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <strong style={{ color: statusTone(connection.status) }}>{LINE_OA_STATUS_LABEL[connection.status]}</strong>
            {connection.lastVerifiedAt && (
              <span style={{ fontSize: 12, color: 'var(--dim)' }}>ตรวจสอบล่าสุด {new Date(connection.lastVerifiedAt).toLocaleString('th-TH')}</span>
            )}
            {connection.botUserId && <span style={{ fontSize: 12, color: 'var(--dim)' }}>bot {connection.botUserId.slice(0, 10)}…</span>}
          </div>

          {connection.missingFields.length > 0 && (
            <div style={{ fontSize: 12.5, color: 'var(--dim)', marginTop: 8 }}>
              ยังไม่ได้กรอก: {connection.missingFields.map((f) => LINE_OA_FIELD_LABEL[f] ?? f).join(', ')}
            </div>
          )}
          {connection.lastVerificationError && (
            <div className="form-error" style={{ marginTop: 8 }}>{connection.lastVerificationError}</div>
          )}

          <div className="ticket-edit-grid" style={{ marginTop: 14 }}>
            <label>
              ชื่อที่แสดงใน JamTick
              <input value={draft.displayName} onChange={(e) => setDraft((d) => ({ ...d, displayName: e.target.value }))} />
            </label>
            <label>
              LINE Basic ID
              <input value={draft.basicId} onChange={(e) => setDraft((d) => ({ ...d, basicId: e.target.value }))} placeholder="@moonfield" />
            </label>
            <label>
              ลิงก์เพิ่มเพื่อน
              <input value={draft.addFriendUrl} onChange={(e) => setDraft((d) => ({ ...d, addFriendUrl: e.target.value }))} placeholder="https://lin.ee/xxxxxxx" />
            </label>
            {wantsMessaging && (
              <label>
                Messaging API Channel ID
                <input value={draft.messagingChannelId} onChange={(e) => setDraft((d) => ({ ...d, messagingChannelId: e.target.value }))} />
              </label>
            )}
          </div>

          <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, fontSize: 13 }}>
            <input type="checkbox" checked={wantsMessaging} onChange={(e) => setWantsMessaging(e.target.checked)} />
            ใช้ Messaging API ด้วย (ต้องมี channel secret และ access token)
          </label>

          <div style={{ display: 'flex', gap: 10, marginTop: 14, flexWrap: 'wrap' }}>
            <button className="btn-primary" style={{ padding: '11px 20px' }} disabled={busy} onClick={save}>บันทึก</button>
            <button className="btn-ghost" style={{ padding: '11px 20px' }} disabled={busy} onClick={verify}>ตรวจสอบการเชื่อมต่อ</button>
            <button
              className="btn-ghost"
              style={{ padding: '11px 20px' }}
              disabled={busy}
              onClick={() => run(() => lineOaApi.setEnabled(token, connection.status === 'disabled', eventSlug), 'เปลี่ยนสถานะไม่สำเร็จ')}
            >
              {connection.status === 'disabled' ? 'เปิดใช้งาน' : 'ปิดใช้งาน'}
            </button>
          </div>

          {report && (
            <div style={{ marginTop: 14, fontSize: 12.5 }}>
              <div className="card-step">ผลการตรวจสอบ</div>
              {report.checks.map((c) => (
                <div key={c.id} style={{ marginTop: 6 }}>
                  <span style={{ color: c.status === 'failed' ? 'var(--danger, #dc2626)' : 'var(--dim)' }}>
                    {LINE_OA_CHECK_MARK[c.status]}
                  </span>{' '}
                  {LINE_OA_CHECK_LABEL[c.id]}
                  {c.detail && <span style={{ color: 'var(--dim)' }}> — {c.detail}</span>}
                </div>
              ))}
              <div style={{ color: 'var(--dim)', marginTop: 8 }}>
                “–” คือข้ามเพราะไม่เกี่ยวกับการตั้งค่าแบบนี้ และ “?” คือยังตรวจอัตโนมัติไม่ได้ — ทั้งสองอย่างไม่ใช่ข้อผิดพลาด
              </div>
            </div>
          )}

          {wantsMessaging && (
            <div style={{ marginTop: 18 }}>
              <div className="card-step">Channel secret / access token</div>
              <div style={{ fontSize: 12.5, color: 'var(--dim)', margin: '6px 0 10px' }}>
                สถานะปัจจุบัน: secret {connection.secretsConfigured.messagingSecret ? 'ตั้งค่าแล้ว' : 'ยังไม่ได้ตั้ง'}
                {' · '}access token {connection.secretsConfigured.messagingAccessToken ? 'ตั้งค่าแล้ว' : 'ยังไม่ได้ตั้ง'}
                {' — '}ระบบไม่แสดงค่าเดิมและอ่านกลับไม่ได้ ถ้าต้องเปลี่ยนให้ใส่ค่าใหม่ทับ
              </div>
              <div className="ticket-edit-grid">
                <label>
                  Channel secret ใหม่
                  <input type="password" autoComplete="off" value={secrets.messagingChannelSecret} onChange={(e) => setSecrets((v) => ({ ...v, messagingChannelSecret: e.target.value }))} />
                </label>
                <label>
                  Channel access token ใหม่
                  <input type="password" autoComplete="off" value={secrets.messagingAccessToken} onChange={(e) => setSecrets((v) => ({ ...v, messagingAccessToken: e.target.value }))} />
                </label>
              </div>
              <button
                className="btn-primary"
                style={{ marginTop: 12, padding: '11px 20px' }}
                disabled={busy || (!secrets.messagingChannelSecret && !secrets.messagingAccessToken)}
                onClick={rotate}
              >
                บันทึก secret
              </button>
            </div>
          )}

          <div style={{ marginTop: 22 }}>
            <div className="card-step">ปุ่มเพิ่มเพื่อนในหน้าสถานะคำสั่งซื้อ</div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, fontSize: 13 }}>
              <input
                type="checkbox"
                checked={!!view?.settings.addFriendEnabled}
                disabled={busy || connection.status !== 'active'}
                onChange={(e) => run(() => lineOaApi.updateSettings(token, { addFriendEnabled: e.target.checked }, eventSlug), 'บันทึกไม่สำเร็จ')}
              />
              แสดงปุ่มเพิ่มเพื่อน OA ของผู้จัด
              {connection.status !== 'active' && (
                <span style={{ color: 'var(--dim)' }}>— เปิดได้เมื่อตรวจสอบการเชื่อมต่อผ่านแล้ว</span>
              )}
            </label>

            <div className="ticket-edit-grid" style={{ marginTop: 12 }}>
              <label>
                ข้อความประกอบปุ่ม (ไม่เกิน 280 ตัวอักษร)
                <input value={message} maxLength={280} onChange={(e) => setMessage(e.target.value)} placeholder="เช่น ติดตามข่าวสารและของแถมได้ที่ LINE ของเรา" />
              </label>
            </div>
            <button
              className="btn-primary"
              style={{ marginTop: 12, padding: '11px 20px' }}
              disabled={busy || message === (view?.settings.message ?? '')}
              onClick={() => run(() => lineOaApi.updateSettings(token, { message }, eventSlug), 'บันทึกข้อความไม่สำเร็จ')}
            >
              บันทึกข้อความ
            </button>

            <div style={{ marginTop: 14, padding: 14, border: '1px dashed var(--line, #ccc)', borderRadius: 10 }}>
              <div style={{ fontSize: 12, color: 'var(--dim)', marginBottom: 8 }}>ตัวอย่างที่ลูกค้าจะเห็น</div>
              {view?.preview.visible ? (
                <>
                  {view.preview.message && <div style={{ fontSize: 13, marginBottom: 8 }}>{view.preview.message}</div>}
                  <a className="btn-primary" style={{ padding: '9px 16px', display: 'inline-block' }} href={view.preview.addFriendUrl ?? '#'} target="_blank" rel="noreferrer">
                    เพิ่มเพื่อน LINE ของผู้จัด
                  </a>
                  {view.preview.source === 'event_column' && (
                    <div style={{ fontSize: 11.5, color: 'var(--dim)', marginTop: 8 }}>
                      ตอนนี้ใช้ลิงก์เดิมที่ตั้งไว้ก่อนหน้านี้ — เปิดสวิตช์ด้านบนเพื่อใช้ OA ที่เพิ่งตั้งค่าแทน
                    </div>
                  )}
                </>
              ) : (
                <div style={{ fontSize: 12.5, color: 'var(--dim)' }}>ยังไม่แสดงปุ่มนี้กับลูกค้า</div>
              )}
            </div>
          </div>

          <div style={{ marginTop: 22 }}>
            <button
              className="btn-ghost"
              style={{ padding: '9px 16px' }}
              disabled={busy}
              onClick={async () => {
                if (audit) { setAudit(null); return; }
                try {
                  setAudit(await lineOaApi.audit(token, eventSlug));
                } catch {
                  setError('โหลดประวัติไม่สำเร็จ');
                }
              }}
            >
              {audit ? 'ซ่อนประวัติการแก้ไข' : 'ดูประวัติการแก้ไข'}
            </button>
            {audit && (
              <div style={{ marginTop: 10, fontSize: 12.5 }}>
                {audit.length === 0 && <div style={{ color: 'var(--dim)' }}>ยังไม่มีประวัติ</div>}
                {audit.map((entry) => (
                  <div key={entry.id} style={{ marginTop: 6, color: 'var(--dim)' }}>
                    {new Date(entry.createdAt).toLocaleString('th-TH')} · {entry.action.replace('line_oa.', '')} · {entry.actor}
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </Group>
  );
}

function PaymentAccountsPanel({ token, eventSlug }: { token: string; eventSlug?: string }) {
  const [accounts, setAccounts] = useState<PaymentAccountDto[]>([]);
  const [error, setError] = useState('');
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState<PaymentAccountInput>(emptyDraft('promptpay'));
  const [addErr, setAddErr] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);
  const [editingQrId, setEditingQrId] = useState<string | null>(null);
  const [testQr, setTestQr] = useState<{ loading: boolean; dataUrl: string; error: string }>({ loading: false, dataUrl: '', error: '' });
  const [qrUpload, setQrUpload] = useState<{ busy: boolean; error: string; detectedLabel: string }>({ busy: false, error: '', detectedLabel: '' });

  const load = useCallback(async () => {
    try {
      setAccounts(await adminApi.listPaymentAccounts(token, eventSlug));
      setError('');
    } catch {
      setError('โหลดรายการบัญชีไม่สำเร็จ');
    }
  }, [token, eventSlug]);

  useEffect(() => { load(); }, [load]);

  const normalizedIdentifier = REF1_PROVIDERS.includes(draft.provider)
    ? draft.identifier.toUpperCase().replace(/[^A-Z0-9]/g, '')
    : draft.identifier.trim();

  const create = async () => {
    setAddErr('');
    if (!normalizedIdentifier || !draft.displayName.trim()) return;
    try {
      await adminApi.createPaymentAccount(token, { ...draft, identifier: normalizedIdentifier }, eventSlug);
      setDraft(emptyDraft('promptpay'));
      setTestQr({ loading: false, dataUrl: '', error: '' });
      setAdding(false);
      await load();
    } catch (e) {
      setAddErr(e instanceof ApiError ? e.message : 'บันทึกบัญชีไม่สำเร็จ');
    }
  };

  const runTestQr = async () => {
    if (!normalizedIdentifier || draft.provider === 'bank') return;
    setTestQr({ loading: true, dataUrl: '', error: '' });
    try {
      const { payload } = await adminApi.testPaymentAccountQr(
        token,
        { provider: draft.provider, identifier: normalizedIdentifier, promptpayType: draft.promptpayType, merchantName: draft.merchantName },
        eventSlug,
      );
      const dataUrl = await QRCode.toDataURL(payload, { margin: 1, width: 200 });
      setTestQr({ loading: false, dataUrl, error: '' });
    } catch (e) {
      setTestQr({ loading: false, dataUrl: '', error: e instanceof ApiError ? e.message : 'สร้าง QR ทดสอบไม่สำเร็จ' });
    }
  };

  /**
   * Auto-fills provider/identifier(+txnCode for tungngern) by decoding a photo of the QR the
   * organizer already has from their own K-Shop/แม่มณี/ถุงเงิน app — avoids the exact typo class
   * that causes K-Plus to reject payment. `receiverProxyId` (BILLERID) is never touched: it isn't
   * part of the QR payload at all, and stays a manual field. On any failure the manual inputs
   * below are left exactly as they were — this is a convenience, never a requirement.
   */
  const handleQrUpload = async (file: File) => {
    setQrUpload({ busy: true, error: '', detectedLabel: '' });
    const decoded = await decodeQrFromImageFile(file);
    if (!decoded) {
      setQrUpload({ busy: false, error: 'อ่าน QR จากรูปไม่สำเร็จ — ลองรูปที่ชัดกว่านี้', detectedLabel: '' });
      return;
    }
    const parsed = parseThaiMerchantQr(decoded);
    if (!parsed.ok) {
      const message =
        parsed.reason === 'no_tag30' || parsed.reason === 'unknown_provider'
          ? 'QR นี้ไม่ใช่ QR ร้านค้าของ K-Shop / แม่มณี / ถุงเงิน'
          : 'อ่านข้อมูลใน QR นี้ไม่ครบ — ลองรูปอื่นหรือกรอกเอง';
      setQrUpload({ busy: false, error: message, detectedLabel: '' });
      return;
    }
    const { provider, ref1, txnCode } = parsed.result;
    setDraft((d) => ({ ...emptyDraft(provider), identifier: ref1, merchantName: txnCode ?? null, displayName: d.displayName }));
    setTestQr({ loading: false, dataUrl: '', error: '' });
    setQrUpload({ busy: false, error: '', detectedLabel: PROVIDER_LABEL[provider] });
  };

  const remove = async (a: PaymentAccountDto) => {
    if (!window.confirm(`ลบบัญชี "${a.displayName}" ?`)) return;
    setBusyId(a.id);
    try {
      await adminApi.deletePaymentAccount(token, a.id, eventSlug);
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'ลบไม่สำเร็จ');
    } finally {
      setBusyId(null);
    }
  };

  const saveQrImage = async (a: PaymentAccountDto, qrImageKey: string | null) => {
    setBusyId(a.id);
    try {
      await adminApi.updatePaymentAccount(token, a.id, { qrImageKey }, eventSlug);
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'บันทึก QR ไม่สำเร็จ');
    } finally {
      setBusyId(null);
    }
  };

  const makeDefault = async (a: PaymentAccountDto) => {
    setBusyId(a.id);
    try {
      await adminApi.updatePaymentAccount(token, a.id, { isDefault: true }, eventSlug);
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'ตั้งค่าไม่สำเร็จ');
    } finally {
      setBusyId(null);
    }
  };

  // Mirrors the server's checkout fallback order (is_default DESC, created_at ASC) so the
  // "ใช้ก่อน / สำรอง N" labels match the order accounts are actually tried in.
  const fallbackOrder = [...accounts].sort(
    (x, y) => Number(y.isDefault) - Number(x.isDefault) || x.createdAt.localeCompare(y.createdAt),
  );

  return (
    <Group icon={<IconWallet />} title="บัญชีรับเงิน" sub="ใช้บัญชีค่าเริ่มต้นก่อนเสมอ — ถ้าสร้าง QR ไม่สำเร็จจะไล่ใช้บัญชีสำรองตามลำดับ">
      {error && <div className="form-error">{error}</div>}

      {!adding ? (
        <button className="btn-primary" style={{ padding: '11px 20px' }} onClick={() => setAdding(true)}>
          + เพิ่มบัญชี
        </button>
      ) : (
        <>
          <div className="card-step">เพิ่มบัญชี</div>
          <div className="admin-subtabs">
            {/* 'bank' hidden for now — plain bank-transfer accounts aren't offered as an add option yet. */}
            {PAYMENT_PROVIDERS.filter((p) => p !== 'bank').map((p) => (
              <button
                key={p}
                className={`admin-nav-btn ${draft.provider === p ? 'active' : ''}`}
                style={{ width: 'auto' }}
                onClick={() => { setDraft(emptyDraft(p)); setTestQr({ loading: false, dataUrl: '', error: '' }); setQrUpload({ busy: false, error: '', detectedLabel: '' }); }}
              >
                {PROVIDER_LABEL[p]}
              </button>
            ))}
          </div>

          {REF1_PROVIDERS.includes(draft.provider) && (
            <div style={{ marginTop: 14 }}>
              <label className="btn-ghost" style={{ display: 'inline-block', cursor: 'pointer' }}>
                {qrUpload.busy ? 'กำลังอ่าน QR…' : 'อัปโหลดรูป QR เพื่อกรอกอัตโนมัติ'}
                <input
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  disabled={qrUpload.busy}
                  onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) handleQrUpload(f); }}
                />
              </label>
              <div style={{ fontSize: 11.5, color: 'var(--dim)', marginTop: 6 }}>
                ถ่ายรูปหรือแนบสกรีนช็อต QR รับเงินจากแอป {PROVIDER_LABEL[draft.provider]} — ระบบจะกรอกเลขร้านค้าให้อัตโนมัติ (ยังต้องกรอก BILLERID เองเหมือนเดิม เพราะไม่ได้อยู่ใน QR)
              </div>
              {qrUpload.error && <div className="form-error" style={{ marginTop: 8 }}>{qrUpload.error}</div>}
              {qrUpload.detectedLabel && (
                <div style={{ fontSize: 12.5, color: 'var(--fg)', marginTop: 8 }}>
                  ตรวจพบ QR {qrUpload.detectedLabel} — กรอกให้อัตโนมัติแล้ว กรุณาตรวจสอบก่อนบันทึก
                </div>
              )}
            </div>
          )}

          <div className="ticket-edit-grid" style={{ marginTop: 14 }}>
            {draft.provider === 'promptpay' && (
              <label>
                ประเภทพร้อมเพย์
                <select
                  value={draft.promptpayType ?? 'msisdn'}
                  onChange={(e) => setDraft((d) => ({ ...d, promptpayType: e.target.value as 'msisdn' | 'natid' | 'ewallet' }))}
                >
                  {(['msisdn', 'natid', 'ewallet'] as const).map((t) => (
                    <option key={t} value={t}>{PROMPTPAY_TYPE_LABEL[t]}</option>
                  ))}
                </select>
              </label>
            )}

            {draft.provider === 'bank' && (
              <label>
                ธนาคาร
                <BankNameSelect
                  value={draft.bankName ?? ''}
                  onChange={(v) => setDraft((d) => ({ ...d, bankName: v }))}
                  code={draft.bankCode ?? ''}
                  onCodeChange={(v) => setDraft((d) => ({ ...d, bankCode: v }))}
                />
              </label>
            )}

            <label>
              {draft.provider === 'promptpay' ? 'เบอร์/เลขบัตร/eWallet ID'
                : draft.provider === 'bank' ? 'เลขบัญชี'
                : `เลขร้านค้า ${PROVIDER_LABEL[draft.provider]}`}
              <input
                value={draft.identifier}
                onChange={(e) => { setDraft((d) => ({ ...d, identifier: e.target.value })); setTestQr({ loading: false, dataUrl: '', error: '' }); }}
                placeholder={REF1_PROVIDERS.includes(draft.provider) ? 'เช่น KB000001537292' : ''}
              />
            </label>

            {draft.provider === 'tungngern' && (
              <label>
                รหัสธุรกรรมถุงเงิน
                <input
                  value={draft.merchantName ?? ''}
                  onChange={(e) => { setDraft((d) => ({ ...d, merchantName: e.target.value })); setTestQr({ loading: false, dataUrl: '', error: '' }); }}
                  maxLength={25}
                  placeholder="เช่น KATIMOBILEGADGET"
                />
              </label>
            )}

            {REF1_PROVIDERS.includes(draft.provider) && (
              <label>
                BILLERID (ไม่บังคับ)
                <input
                  value={draft.receiverProxyId ?? ''}
                  onChange={(e) => setDraft((d) => ({ ...d, receiverProxyId: e.target.value }))}
                  placeholder="เช่น 011555900001508"
                  inputMode="numeric"
                />
                <span style={{ display: 'block', fontSize: 11.5, color: 'var(--dim)', marginTop: 5, lineHeight: 1.6 }}>
                  ดูได้ในแอป {PROVIDER_LABEL[draft.provider]} — ใส่แล้วระบบจะตรวจสอบเพิ่มว่าเงินเข้าบัญชีนี้จริง
                  <br />
                  <strong style={{ color: 'var(--fg)' }}>ถ้าใส่ผิด สลิปจริงจะถูกปฏิเสธทุกใบ</strong> — กดปุ่มทดสอบ QR ด้านล่างยืนยันก่อนเปิดขาย
                </span>
              </label>
            )}

            <label>ชื่อบัญชีที่แสดงหน้า Checkout<input value={draft.displayName} onChange={(e) => setDraft((d) => ({ ...d, displayName: e.target.value }))} /></label>
          </div>

          <div style={{ marginTop: 18 }}>
            <ImageSlot
              token={token}
              eventSlug={eventSlug}
              imageKey={draft.qrImageKey ?? null}
              onChange={(k) => setDraft((d) => ({ ...d, qrImageKey: k }))}
              alt="static QR"
              label="QR สำรอง (ไม่บังคับ)"
              placeholder="QR รับเงินจากแอปธนาคาร (แนวตั้งได้)"
              sub="ใช้เมื่อสร้าง QR ระบุยอดอัตโนมัติไม่สำเร็จ — ลูกค้าจะสแกนแล้วกรอกยอดเอง"
              size={220}
              fit="contain"
              enableCrop
              maxDimension={800}
            />
          </div>

          {draft.provider !== 'bank' && (
            <div style={{ marginTop: 18 }}>
              <button className="btn-ghost" disabled={!normalizedIdentifier || testQr.loading} onClick={runTestQr}>
                {testQr.loading ? 'กำลังสร้าง QR ทดสอบ…' : 'ทดสอบ QR (ยอด ฿1)'}
              </button>
              <div style={{ fontSize: 11.5, color: 'var(--dim)', marginTop: 6 }}>
                ยิงขอ QR จริงจากผู้ให้บริการ — ลองสแกนด้วยแอปธนาคารของคุณก่อนบันทึก เพื่อเช็กว่าเลขร้านค้า/บัญชีถูกต้อง
              </div>
              {testQr.error && <div className="form-error" style={{ marginTop: 8 }}>{testQr.error}</div>}
              {testQr.dataUrl && (
                <div style={{ marginTop: 12 }}>
                  <img src={testQr.dataUrl} alt="QR ทดสอบ" width={180} height={180} />
                </div>
              )}
            </div>
          )}

          {addErr && <div className="form-error" style={{ marginTop: 14 }}>{addErr}</div>}
          <div className="admin-actions" style={{ marginTop: 14 }}>
            <button className="btn-primary" style={{ padding: '9px 18px' }} disabled={!normalizedIdentifier || !draft.displayName.trim()} onClick={create}>บันทึกบัญชี</button>
            <button className="btn-ghost" onClick={() => { setAdding(false); setDraft(emptyDraft('promptpay')); setAddErr(''); setTestQr({ loading: false, dataUrl: '', error: '' }); }}>ยกเลิก</button>
          </div>
        </>
      )}

      {accounts.length === 0 ? (
        <div style={{ color: 'var(--dim)', fontSize: 13.5, padding: '18px 4px' }}>ยังไม่มีบัญชีรับเงิน</div>
      ) : (
        fallbackOrder.map((a, i) => (
          <div key={a.id} className="admin-group" style={{ marginTop: 12, borderColor: a.isDefault ? 'var(--fg)' : undefined }}>
            <div style={{ display: 'flex', gap: 14, alignItems: 'center', padding: '14px 20px', flexWrap: 'wrap' }}>
              <div style={{ flex: '1 1 200px' }}>
                <div style={{ fontSize: 13.5, fontWeight: 500 }}>{a.displayName}</div>
                <div style={{ fontSize: 12, color: 'var(--dim)', marginTop: 2, fontFamily: 'var(--mono)' }}>
                  {PROVIDER_LABEL[a.provider]} · {a.identifier}
                </div>
              </div>
              <span style={{ fontSize: 11.5, padding: '4px 10px', borderRadius: 999, ...(a.isDefault ? { background: 'var(--fg)', color: 'var(--bg)' } : { border: '1px solid var(--border)', color: 'var(--muted)' }) }}>
                {a.isDefault ? 'ใช้ก่อน' : `สำรอง ${i}`}
              </span>
              {!a.isDefault && <button className="btn-ghost" disabled={busyId === a.id} onClick={() => makeDefault(a)}>ตั้งเป็นค่าเริ่มต้น</button>}
              <button className="btn-link" disabled={busyId === a.id} onClick={() => setEditingQrId((cur) => (cur === a.id ? null : a.id))}>
                {a.qrImageUrl ? 'แก้ QR สำรอง' : '+ QR สำรอง'}
              </button>
              <button className="btn-link" style={{ color: '#d99a9a' }} disabled={busyId === a.id} onClick={() => remove(a)}>ลบ</button>
            </div>
            {editingQrId === a.id && (
              <div style={{ padding: '0 20px 18px' }}>
                <ImageSlot
                  token={token}
                  eventSlug={eventSlug}
                  imageKey={a.qrImageKey}
                  onChange={(k) => saveQrImage(a, k)}
                  alt="static QR"
                  label="QR สำรอง"
                  placeholder="QR รับเงินจากแอปธนาคาร (แนวตั้งได้)"
                  sub="ใช้เมื่อสร้าง QR ระบุยอดอัตโนมัติไม่สำเร็จ — ลูกค้าจะสแกนแล้วกรอกยอดเอง"
                  size={220}
                  fit="contain"
                  enableCrop
                  maxDimension={800}
                />
              </div>
            )}
          </div>
        ))
      )}
    </Group>
  );
}

/* ---------- landing page content editor ---------- */
/**
 * Drag-and-drop image slot matching the Claude Design mockup's `image-slot` component:
 * a full-bleed preview when an image is set, a dashed/solid placeholder otherwise,
 * and (unless `bare`) a label row with an inline remove button above it.
 */
function ImageSlot({
  token, eventSlug, imageKey, onChange, alt, aspect = '21 / 9', size, label, sub, placeholder = 'ลากไฟล์มาวาง หรือคลิกเพื่อเลือก', bare, fit = 'cover',
  enableCrop, cropAspect, maxDimension = 1600,
}: {
  token: string;
  eventSlug?: string;
  imageKey: string | null;
  onChange: (key: string | null) => void;
  alt: string;
  aspect?: string;
  size?: number;
  label?: string;
  sub?: string;
  placeholder?: string;
  bare?: boolean;
  /** 'cover' crops to fill the frame (banners); 'contain' shows the whole image (QR codes, logos). */
  fit?: 'cover' | 'contain';
  /** Opens a crop-then-convert-to-WebP dialog before uploading, instead of uploading the raw file. */
  enableCrop?: boolean;
  /** Locks the crop box to a ratio (e.g. 21/9). Omit for a freely resizable box. */
  cropAspect?: number;
  /** Cap on the exported image's longest side, in px — downscale only. */
  maxDimension?: number;
}) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [cropSrc, setCropSrc] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const pickedFileUrl = useRef<string | null>(null);

  const upload = async (file: File) => {
    setBusy(true);
    setErr('');
    try {
      const { key } = await adminApi.uploadContentImage(token, file, eventSlug);
      onChange(key);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'อัปโหลดรูปไม่สำเร็จ');
    } finally {
      setBusy(false);
    }
  };

  const handleFile = (file: File) => {
    if (!enableCrop) {
      upload(file);
      return;
    }
    pickedFileUrl.current = URL.createObjectURL(file);
    setCropSrc(pickedFileUrl.current);
  };

  const closeCropper = () => {
    if (pickedFileUrl.current) {
      URL.revokeObjectURL(pickedFileUrl.current);
      pickedFileUrl.current = null;
    }
    setCropSrc(null);
  };

  return (
    <div style={size ? { width: size, flex: 'none' } : undefined}>
      {!bare && label && (
        <div className="admin-image-label-row">
          <div className="admin-image-label">{label}</div>
          <div style={{ display: 'flex', gap: 6 }}>
            {enableCrop && imageKey && (
              <button
                type="button"
                className="admin-review-icon-btn"
                style={{ width: 30, height: 30 }}
                title="แก้ไขการครอบตัด"
                onClick={(e) => {
                  e.stopPropagation();
                  setCropSrc(adminApi.contentImageUrl(imageKey, eventSlug));
                }}
              >
                <IconCrop />
              </button>
            )}
            {imageKey && (
              <button
                type="button"
                className="admin-review-icon-btn reject"
                style={{ width: 30, height: 30 }}
                title="ลบรูป"
                onClick={() => onChange(null)}
              >
                <IconTrash />
              </button>
            )}
          </div>
        </div>
      )}
      <div
        className={`admin-image-slot ${dragOver ? 'drag' : ''}`}
        style={size ? { width: size, height: size } : { aspectRatio: aspect }}
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          const file = e.dataTransfer.files?.[0];
          if (file) handleFile(file);
        }}
      >
        {imageKey ? (
          <img src={adminApi.contentImageUrl(imageKey, eventSlug)} alt={alt} style={{ objectFit: fit }} />
        ) : (
          <div className="placeholder">
            <IconPhoto size={size && size < 80 ? 18 : 26} />
            {!size || size >= 80 ? <span>{busy ? 'กำลังอัปโหลด…' : placeholder}</span> : null}
          </div>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        style={{ display: 'none' }}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) handleFile(file);
          e.target.value = '';
        }}
      />
      {sub && <div style={{ fontSize: 11.5, color: 'var(--dim)', marginTop: 8, lineHeight: 1.5 }}>{sub}</div>}
      {err && <div className="form-error">{err}</div>}
      {cropSrc && (
        <ImageCropModal
          src={cropSrc}
          aspect={cropAspect}
          maxDimension={maxDimension}
          title={`ครอบตัดรูป${label ? ` — ${label}` : ''}`}
          onCancel={closeCropper}
          onConfirm={(blob) => {
            closeCropper();
            upload(new File([blob], `${alt || 'image'}.webp`, { type: 'image/webp' }));
          }}
        />
      )}
    </div>
  );
}

const THAI_BANKS = [
  'ธนาคารกรุงเทพ',
  'ธนาคารกสิกรไทย',
  'ธนาคารกรุงไทย',
  'ธนาคารไทยพาณิชย์',
  'ธนาคารกรุงศรีอยุธยา',
  'ธนาคารทหารไทยธนชาต',
  'ธนาคารออมสิน',
  'ธนาคารเพื่อการเกษตรและสหกรณ์การเกษตร',
  'ธนาคารซีไอเอ็มบีไทย',
  'ธนาคารยูโอบี',
];
const BANK_OTHER = '__other__';

/**
 * TICK-040 S3 — BOT 3-digit codes for the presets, so choosing a bank fills the code the slip check
 * compares against. Free text alone can't be compared to what a vendor reports.
 */
const THAI_BANK_CODES: Record<string, string> = {
  'ธนาคารกรุงเทพ': '002',
  'ธนาคารกสิกรไทย': '004',
  'ธนาคารกรุงไทย': '006',
  'ธนาคารไทยพาณิชย์': '014',
  'ธนาคารกรุงศรีอยุธยา': '025',
  'ธนาคารทหารไทยธนชาต': '011',
  'ธนาคารออมสิน': '030',
  'ธนาคารเพื่อการเกษตรและสหกรณ์การเกษตร': '034',
  'ธนาคารซีไอเอ็มบีไทย': '022',
  'ธนาคารยูโอบี': '024',
};

/** Preset dropdown of the top-10 Thai banks, falling back to a free-text field for anything else. */
function BankNameSelect({
  value, onChange, code, onCodeChange,
}: {
  value: string; onChange: (v: string) => void;
  code: string; onCodeChange: (v: string) => void;
}) {
  const isPreset = THAI_BANKS.includes(value);
  const [customMode, setCustomMode] = useState(!isPreset && value.trim().length > 0);
  useEffect(() => { setCustomMode(!THAI_BANKS.includes(value) && value.trim().length > 0); }, [value]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <select
        value={customMode ? BANK_OTHER : value}
        onChange={(e) => {
          if (e.target.value === BANK_OTHER) { setCustomMode(true); onChange(''); onCodeChange(''); }
          else { setCustomMode(false); onChange(e.target.value); onCodeChange(THAI_BANK_CODES[e.target.value] ?? ''); }
        }}
      >
        <option value="">— เลือกธนาคาร —</option>
        {THAI_BANKS.map((b) => <option key={b} value={b}>{b}</option>)}
        <option value={BANK_OTHER}>อื่น ๆ (ระบุเอง)</option>
      </select>
      {customMode && (
        <input value={value} onChange={(e) => onChange(e.target.value)} placeholder="ชื่อธนาคาร" />
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <input
          value={code}
          onChange={(e) => onCodeChange(e.target.value.replace(/\D/g, '').slice(0, 3))}
          placeholder="รหัสธนาคาร 3 หลัก"
          style={{ width: 150, fontFamily: 'var(--mono)' }}
        />
        {!code.trim() && (
          <span style={{ fontSize: 12, color: '#ff9a3d', lineHeight: 1.4 }}>
            บัญชีที่ไม่มีรหัสธนาคารจะเข้าคิวตรวจด้วยคนเสมอ เมื่อผู้ให้บริการปิดบังเลขบัญชีผู้รับ
          </span>
        )}
      </div>
    </div>
  );
}

/**
 * TICK-081 — the switch that publishes a coming-soon page, and the preview pass for testing.
 *
 * Separate from the CMS save below it because these two publish, and the fields around them only
 * describe. Pressing "บันทึกทั้งหมด" should never be the thing that made an event visible.
 */
/**
 * The windows an organizer can pick for a preview pass.
 *
 * A fixed list rather than a free-text field: the server bounds this to 5 minutes … 7 days anyway,
 * and every number worth typing is already here — so the control cannot produce a value the server
 * will refuse, and nobody has to guess whether the box wants minutes or hours.
 */
const PREVIEW_PASS_WINDOWS: { minutes: number; label: string }[] = [
  { minutes: 30, label: '30 นาที' },
  { minutes: 120, label: '2 ชั่วโมง' },
  { minutes: 8 * 60, label: '8 ชั่วโมง' },
  { minutes: 24 * 60, label: '1 วัน' },
  { minutes: 3 * 24 * 60, label: '3 วัน' },
  { minutes: 7 * 24 * 60, label: '7 วัน' },
];

function ComingSoonPanel({ token, eventSlug }: { token: string; eventSlug?: string }) {
  const [state, setState] = useState<ComingSoonStateDto | null>(null);
  const [pass, setPass] = useState<PreviewPassDto | null>(null);
  const [minutes, setMinutes] = useState(120);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    adminApi.getComingSoon(token, eventSlug)
      .then((s) => { if (!cancelled) setState(s); })
      .catch(() => { if (!cancelled) setErr('โหลดสถานะไม่สำเร็จ'); });
    return () => { cancelled = true; };
  }, [token, eventSlug]);

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setErr('');
    try {
      await fn();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'ทำรายการไม่สำเร็จ');
    } finally {
      setBusy(false);
    }
  };

  if (!state) return null;

  const isUat = state.salesMode === 'uat';
  const previewUrl = pass
    ? `${window.location.origin}/e/${eventSlug ?? ''}?preview=${encodeURIComponent(pass.token)}`
    : '';

  return (
    <Group
      icon={<IconClock />}
      title="โหมดพบกันเร็ว ๆ นี้"
      sub="แสดงหน้าประกาศแทนหน้าขายบัตร สำหรับงานที่ยังไม่พร้อมเปิดเผยรายละเอียด"
      right={
        <span className="mono-tag" style={{
          fontSize: 10,
          color: isUat ? '#ff9a3d' : 'var(--dim)',
          borderColor: isUat ? 'rgba(255,154,61,0.4)' : 'var(--border)',
        }}>
          {isUat ? 'UAT' : state.salesMode === 'public' ? 'เปิดขายแล้ว' : 'ปิดการขาย'}
        </span>
      }
    >
      <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: 13.5 }}>
        <input
          type="checkbox"
          checked={state.enabled}
          disabled={busy || (!isUat && !state.enabled)}
          style={{ width: 16, height: 16, marginTop: 2, flex: 'none' }}
          onChange={(e) => {
            const enabled = e.target.checked;
            run(async () => setState(await adminApi.setComingSoon(token, enabled, eventSlug)));
          }}
        />
        <span>
          เปิดใช้งาน — ผู้เข้าชมทั่วไปจะเห็นหน้าประกาศที่ {window.location.origin}/e/{eventSlug ?? ''}
          <span style={{ display: 'block', color: 'var(--dim)', fontSize: 12, marginTop: 4 }}>
            {isUat
              ? 'ปิดอยู่ = ลิงก์ของงานนี้จะยังตอบว่า "ไม่พบงานนี้" เหมือนเดิม · หน้านี้ไม่แสดง Lineup ราคา หรือปุ่มซื้อบัตร'
              : 'ใช้ได้เฉพาะงานที่อยู่ในโหมด UAT — เปลี่ยนโหมดการขายได้ที่หน้า Superadmin'}
          </span>
        </span>
      </label>

      <div style={{ borderTop: '1px solid var(--line)', marginTop: 18, paddingTop: 18 }}>
        <div style={{ fontSize: 13.5, marginBottom: 4 }}>พรีวิวสำหรับทีมทดสอบ</div>
        <div style={{ fontSize: 12, color: 'var(--dim)', marginBottom: 12, lineHeight: 1.7 }}>
          เปิดหน้าขายจริงของงาน UAT นี้ในเบราว์เซอร์ เพื่อทดสอบตั้งแต่เลือกบัตรจนถึงชำระเงิน ·
          ลิงก์ให้สิทธิ์ดูและซื้อในงานนี้เท่านั้น เข้าหน้า Admin ไม่ได้ และหมดอายุเองตามเวลาที่เลือก
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          {/*
            Beside the button rather than above it: the window is part of pressing "เปิดพรีวิว",
            and changing it after a pass exists changes nothing until the next mint.
          */}
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: 'var(--dim)' }}>
            ใช้ได้นาน
            <select
              value={minutes}
              disabled={busy || !isUat}
              onChange={(e) => setMinutes(Number(e.target.value))}
              style={{ fontSize: 12.5, padding: '6px 8px', borderRadius: 4 }}
            >
              {PREVIEW_PASS_WINDOWS.map((w) => (
                <option key={w.minutes} value={w.minutes}>{w.label}</option>
              ))}
            </select>
          </label>
          <button
            className="btn-ghost"
            type="button"
            disabled={busy || !isUat}
            onClick={() => run(async () => {
              setPass(await adminApi.createPreviewPass(token, minutes, eventSlug));
              setCopied(false);
            })}
          >
            {pass ? 'สร้างลิงก์ใหม่' : 'เปิดพรีวิว'}
          </button>
          {pass && (
            <>
              <button
                className="btn-ghost"
                type="button"
                onClick={() => navigator.clipboard.writeText(previewUrl).then(() => setCopied(true))}
              >
                {copied ? 'คัดลอกแล้ว ✓' : 'คัดลอกลิงก์ให้ทีม'}
              </button>
              <a className="btn-ghost" href={previewUrl} target="_blank" rel="noreferrer">เปิดในแท็บใหม่</a>
              <button
                className="btn-link"
                type="button"
                disabled={busy}
                onClick={() => run(async () => {
                  await adminApi.revokePreviewPass(token, eventSlug);
                  setPass(null);
                })}
              >
                ยกเลิกลิงก์
              </button>
            </>
          )}
        </div>
        {pass && (
          <div style={{
            marginTop: 12, padding: '10px 12px', border: '1px dashed var(--border)', borderRadius: 4,
            fontFamily: 'var(--mono)', fontSize: 11.5, color: 'var(--muted)', wordBreak: 'break-all',
          }}>
            <div style={{ color: 'var(--dim)', marginBottom: 4 }}>
              ใช้ได้ถึง {new Date(pass.expiresAt).toLocaleString('th-TH', { hour: '2-digit', minute: '2-digit', day: 'numeric', month: 'short' })} น.
            </div>
            {previewUrl}
          </div>
        )}
      </div>

      {err && <div className="form-error" style={{ marginTop: 12 }}>{err}</div>}
    </Group>
  );
}

function ContentPanel({ token, eventSlug, headRight }: { token: string; eventSlug?: string; headRight: (b: ReactNode) => void }) {
  /**
   * Which half of the landing page is being edited. `pre` is the placeholder an event shows before
   * it is announced; `post` is the sales page every customer reads afterwards.
   *
   * Opens on `post`: the pre-announcement copy is written once and then switched off for good,
   * while the sales page is edited for the whole life of the event. First tab in reading order,
   * last one an owner actually needs.
   */
  const [sub, setSub] = useState<'pre' | 'post'>('post');
  const [content, setContent] = useState<ContentDto | null>(null);
  const [error, setError] = useState('');
  const [saveErr, setSaveErr] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [factsDrag, setFactsDrag] = useState<number | null>(null);
  const factsDragIndexRef = useRef<number | null>(null);

  const load = useCallback(async () => {
    try {
      setContent(await adminApi.getContent(token, eventSlug));
      setError('');
    } catch {
      setError('โหลดเนื้อหาไม่สำเร็จ');
    }
  }, [token, eventSlug]);

  useEffect(() => { load(); }, [load]);

  const set = <K extends keyof ContentDto>(key: K, value: ContentDto[K]) =>
    setContent((c) => (c ? { ...c, [key]: value } : c));

  const save = useCallback(async () => {
    if (!content) return;
    setSaving(true);
    setSaveErr('');
    setSaved(false);
    try {
      // Drop stub rows created by "+ add" buttons but never filled in — otherwise saving
      // right after adding one opaquely fails server validation (empty required fields).
      const payload: ContentDto = {
        ...content,
        facts: content.facts.filter((f) => f.k.trim() || f.v.trim()),
        lineup: content.lineup.filter((a) => a.name.trim() || a.time.trim()),
        sections: content.sections.map((s) => s.type === 'faq'
          ? { ...s, items: s.items.filter((it) => it.q.trim() || it.a.trim()) }
          : { ...s, items: s.items.filter((it) => it.name.trim()) }),
      };
      const updated = await adminApi.updateContent(token, payload, eventSlug);
      setContent(updated);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      setSaveErr(e instanceof ApiError ? e.message : 'บันทึกไม่สำเร็จ');
    } finally {
      setSaving(false);
    }
  }, [content, token, eventSlug]);

  useEffect(() => {
    headRight(
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <a href={eventSlug ? `/e/${eventSlug}` : '/'} target="_blank" rel="noreferrer" className="btn-ghost" style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          ดูตัวอย่าง
        </a>
        <button className="btn-primary" style={{ padding: '11px 18px', display: 'inline-flex', alignItems: 'center', gap: 8 }} disabled={saving || !content} onClick={save}>
          {saved ? 'บันทึกแล้ว ✓' : saving ? 'กำลังบันทึก…' : 'บันทึกทั้งหมด'}
        </button>
      </div>,
    );
    return () => headRight(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [save, saving, saved, content, eventSlug]);

  if (error) return <div className="form-error">{error}</div>;
  if (!content) return <div style={{ color: 'var(--muted)', fontSize: 14 }}>กำลังโหลด…</div>;

  const moveArtist = (index: number, delta: number) => {
    const target = index + delta;
    if (target < 0 || target >= content.lineup.length) return;
    const next = [...content.lineup];
    [next[index], next[target]] = [next[target], next[index]];
    set('lineup', next);
  };

  const addSection = (type: 'faq' | 'sponsors') => {
    const section: ContentSection =
      type === 'faq'
        ? { id: crypto.randomUUID(), type: 'faq', title: 'คำถามที่พบบ่อย', items: [] }
        : { id: crypto.randomUUID(), type: 'sponsors', title: 'สปอนเซอร์', items: [] };
    set('sections', [...content.sections, section]);
  };

  const comingSoon = content.comingSoon ?? {
    headline: '', eyebrow: '', message: '', ctaLabel: '', ctaUrl: '', showHero: true,
  };
  const setComingSoon = <K extends keyof ComingSoonDto>(key: K, value: ComingSoonDto[K]) =>
    set('comingSoon', { ...comingSoon, [key]: value });

  return (
    <>
      {/*
        Two jobs, and an event is only ever doing one of them: before the announcement the page is
        the placeholder — is it live, and what does it say — and after it, everything a customer
        reads on the sales page. Stacking them meant scrolling past a coming-soon editor that is
        finished and switched off to reach the Lineup, on every visit for the rest of the event.

        Both halves still edit one `content` object and are saved by the one บันทึกทั้งหมด button
        above, so switching tabs never loses an edit and never needs a save first.
      */}
      <div className="admin-subtabs">
        {([
          { key: 'pre', label: 'ก่อนประกาศ' },
          { key: 'post', label: 'หลังประกาศ' },
        ] as const).map((s) => (
          <button
            key={s.key}
            type="button"
            className={`admin-subtab${sub === s.key ? ' active' : ''}`}
            onClick={() => setSub(s.key)}
          >
            {s.label}
          </button>
        ))}
      </div>

      {sub === 'pre' && (
      <>
      <ComingSoonPanel token={token} eventSlug={eventSlug} />

      <Group
        icon={<IconMonitor />}
        title="ข้อความหน้าพบกันเร็ว ๆ นี้"
        sub="ข้อความที่แสดงเมื่อเปิดโหมดพบกันเร็ว ๆ นี้ — เว้นว่างไว้จะใช้ค่าเริ่มต้น"
      >
        <div className="ticket-edit-grid ticket-edit-grid--stacked">
          <label>
            หัวข้อใหญ่
            <input
              value={comingSoon.headline}
              onChange={(e) => setComingSoon('headline', e.target.value)}
              placeholder="พบกันเร็ว ๆ นี้"
            />
          </label>
          <label>
            บรรทัดบนหัวข้อ
            <input
              value={comingSoon.eyebrow}
              onChange={(e) => setComingSoon('eyebrow', e.target.value)}
              placeholder="เว้นว่าง = ใช้วันที่ของงาน"
            />
          </label>
          <label>
            ข้อความ
            <textarea
              rows={3}
              value={comingSoon.message}
              onChange={(e) => setComingSoon('message', e.target.value)}
              placeholder="เช่น ประกาศวันจัดงานแล้ว ศิลปินและรอบการขายบัตรจะประกาศในลำดับถัดไป"
            />
          </label>
        </div>
        <div className="ticket-edit-grid ticket-edit-grid--stacked" style={{ marginTop: 14 }}>
          <label>
            ข้อความบนปุ่ม (ไม่ใส่ก็ได้)
            <input
              value={comingSoon.ctaLabel}
              onChange={(e) => setComingSoon('ctaLabel', e.target.value)}
              placeholder="ติดตามข่าวสาร"
            />
          </label>
          <label>
            ลิงก์ปุ่ม
            <input
              value={comingSoon.ctaUrl}
              onChange={(e) => setComingSoon('ctaUrl', e.target.value)}
              placeholder="https://…"
            />
          </label>
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13.5, marginTop: 16 }}>
          <input
            type="checkbox"
            checked={comingSoon.showHero}
            style={{ width: 16, height: 16 }}
            onChange={(e) => setComingSoon('showHero', e.target.checked)}
          />
          แสดงรูป Hero ของงานบนหน้าประกาศ
        </label>
      </Group>
      </>
      )}

      {sub === 'post' && (
      <>
      <Group icon={<IconInfo />} title="ข้อมูลหลัก" sub="ชื่องาน โปรโมชัน และรูปหลัก">
        <div className="ticket-edit-grid ticket-edit-grid--stacked">
          <label>ชื่องาน<input value={content.name} onChange={(e) => set('name', e.target.value)} /></label>
          <label>Tagline<input value={content.tagline} onChange={(e) => set('tagline', e.target.value)} /></label>
          <label>ข้อความ Early Bird<input value={content.earlyBirdNote} onChange={(e) => set('earlyBirdNote', e.target.value)} /></label>
          <label>อีเมลติดต่อ<input value={content.contactEmail} onChange={(e) => set('contactEmail', e.target.value)} /></label>
          <label>ชื่อบริษัท (footer)<input value={content.company} onChange={(e) => set('company', e.target.value)} /></label>
        </div>
        {/*
          Two crops of one hero, uploaded separately, because a 21:9 banner on a phone is a thin
          strip of a photograph. The browser picks between them by viewport width (see `HeroMedia`
          in `Landing`), so the portrait slot is genuinely optional: leave it empty and the
          landscape image is served everywhere, which is what every event does today.
        */}
        <div style={{ marginTop: 22, display: 'grid', gap: 22 }}>
          <ImageSlot
            token={token}
            eventSlug={eventSlug}
            imageKey={content.heroImageKey}
            onChange={(k) => set('heroImageKey', k)}
            alt="hero"
            label="รูป Hero — จอกว้าง (Desktop)"
            placeholder="รูป Hero แนวนอน — ลากไฟล์มาวาง"
            sub="ขนาดที่แนะนำ 1920 × 823 px (สัดส่วน 21:9) · ใช้กับจอกว้างกว่า 640px และใช้กับทุกจอถ้าไม่ได้ใส่รูปแนวตั้ง — แปลงเป็น WebP อัตโนมัติ"
            enableCrop
            cropAspect={21 / 9}
            maxDimension={1920}
          />
          {/* Boxed to a phone-ish width: `size` on ImageSlot means a square, and a full-width 4:5
              frame would be a metre of preview for a picture nobody sees that large. */}
          <div style={{ maxWidth: 280 }}>
          <ImageSlot
            token={token}
            eventSlug={eventSlug}
            imageKey={content.heroImagePortraitKey}
            onChange={(k) => set('heroImagePortraitKey', k)}
            alt="hero portrait"
            aspect="4 / 5"
            label="รูป Hero — มือถือ (ไม่ใส่ก็ได้)"
            placeholder="รูป Hero แนวตั้ง — ลากไฟล์มาวาง"
            sub="ขนาดที่แนะนำ 1080 × 1350 px (สัดส่วน 4:5) · ใช้กับจอกว้างไม่เกิน 640px · เว้นว่างไว้ = ใช้รูปแนวนอนทุกจอเหมือนเดิม"
            enableCrop
            cropAspect={4 / 5}
            maxDimension={1350}
          />
          </div>
        </div>
      </Group>

      <Group icon={<IconList />} title="Tagline หมวดตั๋ว" sub="ป้ายข้อความเล็กข้าง หัวข้อ TICKETS บนหน้า Landing Page">
        <div className="ticket-edit-grid ticket-edit-grid--stacked">
          <label>
            ข้อความ Tagline
            <input
              value={content.ticketsTagline}
              onChange={(e) => set('ticketsTagline', e.target.value)}
              placeholder="PHASE 1 · EARLY BIRD"
            />
          </label>
          <label className="ticket-check">
            <input
              type="checkbox"
              checked={content.ticketsTaglineVisible}
              onChange={(e) => set('ticketsTaglineVisible', e.target.checked)}
            />
            แสดง Tagline นี้บน Landing Page
          </label>
        </div>
      </Group>

      <Group icon={<IconList />} title="Facts" sub="แถบข้อมูลใต้ Hero · ลากไอคอนเพื่อจัดลำดับ">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {content.facts.map((f, i) => (
            <div
              key={i}
              className="admin-fact-row"
              style={{ opacity: factsDrag === i ? 0.4 : 1 }}
              onDragOver={(e) => {
                e.preventDefault();
                const from = factsDragIndexRef.current;
                if (from === null || from === i) return;
                const next = [...content.facts];
                const [moved] = next.splice(from, 1);
                next.splice(i, 0, moved);
                factsDragIndexRef.current = i;
                setFactsDrag(i);
                set('facts', next);
              }}
              onDrop={(e) => { e.preventDefault(); factsDragIndexRef.current = null; setFactsDrag(null); }}
            >
              <span
                className="grip"
                draggable
                title="ลากเพื่อจัดลำดับ"
                onDragStart={() => { factsDragIndexRef.current = i; setFactsDrag(i); }}
                onDragEnd={() => { factsDragIndexRef.current = null; setFactsDrag(null); }}
              >
                <IconGrip />
              </span>
              <input
                className="admin-fact-key"
                value={f.k}
                placeholder="KEY"
                onChange={(e) => set('facts', content.facts.map((x, j) => (j === i ? { ...x, k: e.target.value } : x)))}
              />
              <input
                style={{ flex: 1, minWidth: 0 }}
                value={f.v}
                placeholder="ค่า"
                onChange={(e) => set('facts', content.facts.map((x, j) => (j === i ? { ...x, v: e.target.value } : x)))}
              />
              <button
                className="admin-review-icon-btn reject"
                style={{ width: 36, height: 36 }}
                title="ลบ Fact นี้"
                onClick={() => set('facts', content.facts.filter((_, j) => j !== i))}
              >
                <IconTrash size={16} />
              </button>
            </div>
          ))}
        </div>
        <button
          className="btn-ghost"
          style={{ marginTop: 14 }}
          onClick={() => set('facts', [...content.facts, { k: '', v: '' }])}
        >
          + เพิ่ม Fact
        </button>
      </Group>

      <Group icon={<IconUsers />} title="Lineup" sub="รายชื่อศิลปิน · จัดลำดับด้วยปุ่มลูกศร">
        <label style={{ display: 'block', maxWidth: 160, marginBottom: 14 }}>
          จำนวนวันงาน
          <input
            type="number"
            min={1}
            value={content.lineupDays}
            onChange={(e) => set('lineupDays', Math.max(1, Math.round(Number(e.target.value) || 1)))}
          />
        </label>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {content.lineup.map((a, i) => (
            <div key={i} className="admin-lineup-row">
              <div className="admin-lineup-reorder">
                <button type="button" disabled={i === 0} title="เลื่อนขึ้น" onClick={() => moveArtist(i, -1)}>
                  <IconChevronUp />
                </button>
                <button type="button" disabled={i === content.lineup.length - 1} title="เลื่อนลง" onClick={() => moveArtist(i, 1)}>
                  <IconChevronDown />
                </button>
              </div>
              <ImageSlot
                token={token}
                eventSlug={eventSlug}
                imageKey={a.photoKey}
                onChange={(k) => set('lineup', content.lineup.map((x, j) => (j === i ? { ...x, photoKey: k } : x)))}
                alt={a.name}
                size={52}
                bare
                enableCrop
                cropAspect={4 / 5}
                maxDimension={800}
              />
              <div className="admin-lineup-no">#{i + 1}</div>
              <div className="admin-lineup-fields">
                <input
                  value={a.name}
                  placeholder="ชื่อศิลปิน"
                  onChange={(e) => set('lineup', content.lineup.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))}
                />
                <input
                  value={a.time}
                  placeholder="เวลา / เวที"
                  style={{ fontFamily: 'var(--mono)', fontSize: 12, letterSpacing: '.04em' }}
                  onChange={(e) => set('lineup', content.lineup.map((x, j) => (j === i ? { ...x, time: e.target.value } : x)))}
                />
              </div>
              <button
                className="admin-review-icon-btn"
                style={{ width: 36, height: 36 }}
                title="ลบรูป"
                onClick={() => set('lineup', content.lineup.map((x, j) => (j === i ? { ...x, photoKey: null } : x)))}
              >
                <IconImageOff />
              </button>
              <button
                className="admin-review-icon-btn reject"
                style={{ width: 36, height: 36 }}
                title="ลบศิลปินนี้"
                onClick={() => set('lineup', content.lineup.filter((_, j) => j !== i))}
              >
                <IconTrash size={16} />
              </button>
            </div>
          ))}
        </div>
        <button
          className="btn-ghost"
          style={{ marginTop: 14, alignSelf: 'flex-start' }}
          onClick={() => set('lineup', [...content.lineup, { name: '', time: '', photoKey: null }])}
        >
          + เพิ่มศิลปิน
        </button>
      </Group>

      <Group icon={<IconSections />} title="Sections เพิ่มเติม" sub="FAQ และสปอนเซอร์">
        {content.sections.map((s, i) => (
          <div key={s.id} style={{ marginBottom: 16, paddingBottom: 16, borderBottom: '1px solid var(--panel-border)' }}>
            <div className="coupon-row" style={{ marginTop: 0 }}>
              <span className="status-chip" style={{ alignSelf: 'center' }}>{s.type.toUpperCase()}</span>
              <input
                value={s.title}
                placeholder="หัวข้อ section"
                onChange={(e) => set('sections', content.sections.map((x, j) => (j === i ? { ...x, title: e.target.value } : x)))}
              />
              <button className="btn-link" onClick={() => set('sections', content.sections.filter((_, j) => j !== i))}>ลบ Section</button>
            </div>

            {s.type === 'faq' ? (
              <div style={{ marginTop: 10 }}>
                {s.items.map((it, k) => (
                  <div key={k} style={{ marginBottom: 8 }}>
                    <input
                      value={it.q}
                      placeholder="คำถาม"
                      style={{ width: '100%', marginBottom: 6 }}
                      onChange={(e) => set('sections', content.sections.map((x, j) =>
                        j === i && x.type === 'faq'
                          ? { ...x, items: x.items.map((y, m) => (m === k ? { ...y, q: e.target.value } : y)) }
                          : x))}
                    />
                    <textarea
                      value={it.a}
                      placeholder="คำตอบ"
                      rows={2}
                      style={{ width: '100%' }}
                      onChange={(e) => set('sections', content.sections.map((x, j) =>
                        j === i && x.type === 'faq'
                          ? { ...x, items: x.items.map((y, m) => (m === k ? { ...y, a: e.target.value } : y)) }
                          : x))}
                    />
                    <button
                      className="btn-link"
                      onClick={() => set('sections', content.sections.map((x, j) =>
                        j === i && x.type === 'faq' ? { ...x, items: x.items.filter((_, m) => m !== k) } : x))}
                    >
                      ลบคำถามนี้
                    </button>
                  </div>
                ))}
                <button
                  className="btn-ghost"
                  onClick={() => set('sections', content.sections.map((x, j) =>
                    j === i && x.type === 'faq' ? { ...x, items: [...x.items, { q: '', a: '' }] } : x))}
                >
                  + เพิ่มคำถาม
                </button>
              </div>
            ) : (
              <div style={{ marginTop: 10 }}>
                {s.items.map((it, k) => (
                  <div key={k} className="ticket-edit-grid" style={{ marginBottom: 8 }}>
                    <label>ชื่อสปอนเซอร์
                      <input
                        value={it.name}
                        onChange={(e) => set('sections', content.sections.map((x, j) =>
                          j === i && x.type === 'sponsors'
                            ? { ...x, items: x.items.map((y, m) => (m === k ? { ...y, name: e.target.value } : y)) }
                            : x))}
                      />
                    </label>
                    <label>ลิงก์ (ถ้ามี)
                      <input
                        value={it.url}
                        onChange={(e) => set('sections', content.sections.map((x, j) =>
                          j === i && x.type === 'sponsors'
                            ? { ...x, items: x.items.map((y, m) => (m === k ? { ...y, url: e.target.value } : y)) }
                            : x))}
                      />
                    </label>
                    <div style={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', gap: 12 }}>
                      <ImageSlot
                        token={token}
                        eventSlug={eventSlug}
                        imageKey={it.logoKey}
                        alt={it.name}
                        size={56}
                        bare
                        onChange={(logoKey: string | null) => set('sections', content.sections.map((x, j) =>
                          j === i && x.type === 'sponsors'
                            ? { ...x, items: x.items.map((y, m) => (m === k ? { ...y, logoKey } : y)) }
                            : x))}
                      />
                      <button
                        className="btn-link"
                        onClick={() => set('sections', content.sections.map((x, j) =>
                          j === i && x.type === 'sponsors' ? { ...x, items: x.items.filter((_, m) => m !== k) } : x))}
                      >
                        ลบสปอนเซอร์นี้
                      </button>
                    </div>
                  </div>
                ))}
                <button
                  className="btn-ghost"
                  onClick={() => set('sections', content.sections.map((x, j) =>
                    j === i && x.type === 'sponsors' ? { ...x, items: [...x.items, { name: '', url: '', logoKey: null }] } : x))}
                >
                  + เพิ่มสปอนเซอร์
                </button>
              </div>
            )}
          </div>
        ))}
        <div className="admin-actions">
          <button className="btn-ghost" onClick={() => addSection('faq')}>+ เพิ่ม Section FAQ</button>
          <button className="btn-ghost" onClick={() => addSection('sponsors')}>+ เพิ่ม Section สปอนเซอร์</button>
        </div>
      </Group>
      </>
      )}

      {saveErr && <div className="form-error">{saveErr}</div>}
    </>
  );
}

/* ---------- root ---------- */
export default function Admin() {
  const { eventSlug } = useParams<{ eventSlug?: string }>();

  usePageTitle(eventSlug, 'Admin');

  // Public/unauthenticated — used only for display (sidebar brand, login heading) so it's safe
  // to show before a token is entered, and never confused with another tenant's event.
  const [eventName, setEventName] = useState('');
  useEffect(() => {
    let cancelled = false;
    api.getEvent(eventSlug).then((event) => { if (!cancelled) setEventName(event.name); }).catch(() => {});
    return () => { cancelled = true; };
  }, [eventSlug]);

  const tokenStorageKey = `jamtick.adminToken${eventSlug ? `:${eventSlug}` : ''}`;
  const [token, setToken] = useState(
    () => sessionStorage.getItem(tokenStorageKey) ?? localStorage.getItem('admin_token') ?? localStorage.getItem(tokenStorageKey) ?? ''
  );
  const [tokenInput, setTokenInput] = useState('');
  const [role, setRole] = useState<'owner' | 'staff' | 'sales' | null>(null);
  const [tab, setTab] = useState<MainTab>('review');
  const [reviewSubTab, setReviewSubTab] = useState<ReviewSubTab>('awaiting_verification');
  const [orders, setOrders] = useState<AdminOrderDto[]>([]);
  const [deliveries, setDeliveries] = useState<NotificationDeliveryDto[]>([]);
  const [stats, setStats] = useState<AdminStatsDto | null>(null);
  const [statsError, setStatsError] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');
  const [q, setQ] = useState('');
  const [qDebounced, setQDebounced] = useState('');
  const [csvBusy, setCsvBusy] = useState(false);
  const [contentHeadRight, setContentHeadRight] = useState<ReactNode>(null);
  const [ticketsHeadRight, setTicketsHeadRight] = useState<ReactNode>(null);
  const [reviewPage, setReviewPage] = useState(1);
  const REVIEW_PAGE_SIZE = 10;

  useEffect(() => {
    const t = setTimeout(() => setQDebounced(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => { setReviewPage(1); }, [reviewSubTab, qDebounced, eventSlug]);

  const [pollingEnabled, setPollingEnabled] = useState(true);
  const [pollingIntervalSec, setPollingIntervalSec] = useState(60);

  useEffect(() => {
    if (!token) { setRole(null); return; }
    let cancelled = false;
    adminApi.whoami(token, eventSlug).then((res) => {
      if (cancelled) return;
      setRole(res.role);
      setPollingEnabled(res.pollingEnabled);
      setPollingIntervalSec(res.pollingIntervalSec);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [token, eventSlug]);

  // Sales tokens only ever see the NAV_SALES tabs — force off any other tab (e.g. left over
  // from a previous session on the same browser) the moment the role is known.
  useEffect(() => {
    if (role === 'sales' && !NAV_SALES.some((n) => n.key === tab)) setTab('overview');
  }, [role, tab]);

  // Only needed to phrase the "expired" reason line below with the event's real hold time —
  // fetched once per event rather than polled, since it rarely changes mid-session.
  const [checkoutMinutes, setCheckoutMinutes] = useState<number | null>(null);
  useEffect(() => {
    if (!token) return;
    adminApi.getCheckoutSettings(token, eventSlug).then((d) => setCheckoutMinutes(d.effectiveMinutes)).catch(() => {});
  }, [token, eventSlug]);

  const loadStats = useCallback(async () => {
    if (!token) return;
    try {
      setStats(await adminApi.getStats(token, eventSlug));
      setStatsError('');
    } catch {
      setStatsError('โหลดข้อมูลภาพรวมไม่สำเร็จ');
    }
  }, [token, eventSlug]);

  usePolling(loadStats, pollingIntervalSec * 1000, pollingEnabled);

  const load = useCallback(async () => {
    if (!token || tab !== 'review') return;
    try {
      const p = reviewSubTabParams(reviewSubTab);
      setOrders(await adminApi.listOrders(token, p.status, qDebounced || undefined, eventSlug, p.checkedIn, p.bucket));
      setError('');
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        sessionStorage.removeItem(tokenStorageKey);
        setToken('');
        setError('Token ไม่ถูกต้อง');
      } else {
        setError('โหลดข้อมูลไม่สำเร็จ');
      }
    }
  }, [token, tab, reviewSubTab, qDebounced, eventSlug, tokenStorageKey]);

  usePolling(load, pollingIntervalSec * 1000, pollingEnabled);

  const loadDeliveries = useCallback(async () => {
    if (!token || tab !== 'review') return;
    try {
      setDeliveries(await adminApi.listNotificationDeliveries(token, eventSlug));
    } catch {
      // Order review remains available when the optional delivery ledger cannot be loaded.
    }
  }, [token, tab, eventSlug]);

  usePolling(loadDeliveries, pollingIntervalSec * 1000, pollingEnabled);

  const [refreshCooldown, setRefreshCooldown] = useState(false);
  const refreshNow = useCallback(() => {
    if (refreshCooldown) return;
    loadStats();
    load();
    loadDeliveries();
    setRefreshCooldown(true);
    setTimeout(() => setRefreshCooldown(false), 30_000);
  }, [refreshCooldown, loadStats, load, loadDeliveries]);

  const downloadCsv = async () => {
    setCsvBusy(true);
    try {
      const p = reviewSubTabParams(reviewSubTab);
      await adminApi.downloadOrdersCsv(token, eventSlug, p.status, p.checkedIn, qDebounced || undefined, p.bucket);
    } catch {
      setError('ดาวน์โหลด CSV ไม่สำเร็จ');
    } finally {
      setCsvBusy(false);
    }
  };

  const login = () => {
    const t = tokenInput.trim();
    if (!t) return;
    sessionStorage.setItem(tokenStorageKey, t);
    setToken(t);
  };

  if (!token) {
    return (
      <div className="page admin-login-wrap">
        <h1 style={{ fontSize: 28 }}>{eventName || 'JAMTICK'} — STAFF</h1>
        <p style={{ color: 'var(--muted)', fontSize: 14 }}>ใส่ Admin token เพื่อเข้าใช้งาน</p>
        <div className="coupon-row" style={{ maxWidth: 420 }}>
          <input
            type="password"
            value={tokenInput}
            onChange={(e) => setTokenInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && login()}
            placeholder="Admin token"
          />
          <button className="btn-ghost" onClick={login}>เข้าสู่ระบบ</button>
        </div>
        {error && <div className="form-error">{error}</div>}
      </div>
    );
  }

  const act = async (id: string, action: 'verify' | 'reject' | 'reverify') => {
    if (action === 'reject' && !window.confirm(rejectConfirmMessage(orders.find((o) => o.id === id)?.slipVerification))) return;
    const prevStatus = orders.find((o) => o.id === id)?.status;
    setBusy(id);
    try {
      const updated =
        action === 'verify' ? await adminApi.verify(token, id, eventSlug)
        : action === 'reject' ? await adminApi.reject(token, id, eventSlug)
        : (await adminApi.reverify(token, id, eventSlug)).order;
      // Update the list in place instead of blocking on a full refetch — the row moves to its
      // new status (or drops out of the current sub-tab) the moment the server responds.
      setOrders((prev) =>
        reviewSubTab && reviewSubTab !== updated.status
          ? prev.filter((o) => o.id !== id)
          : prev.map((o) => (o.id === id ? updated : o)),
      );
      if (action === 'reverify' && updated.status === 'awaiting_verification') {
        setError('ตรวจใหม่แล้วแต่ยังไม่ผ่าน — ผู้ให้บริการยังไม่คืนคำตอบที่ชัดเจน (ลองใหม่อีกครั้ง หรือกดอนุมัติ/ปฏิเสธเอง)');
      }
      if (prevStatus && prevStatus !== updated.status) {
        setStats((prev) =>
          prev
            ? {
                ...prev,
                ordersByStatus: {
                  ...prev.ordersByStatus,
                  [prevStatus]: Math.max(0, prev.ordersByStatus[prevStatus] - 1),
                  [updated.status]: (prev.ordersByStatus[updated.status] ?? 0) + 1,
                },
              }
            : prev,
        );
      }
      loadStats();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'ทำรายการไม่สำเร็จ');
    } finally {
      setBusy('');
    }
  };

  /**
   * TICK-045. Separate from `act` because it changes no order status — it re-arms delivery. The
   * server re-uses the existing outbox row, so a double press cannot send two tickets; the button
   * is disabled during the call only to keep the feedback honest.
   */
  const resendTicket = async (id: string) => {
    setBusy(id);
    setError('');
    try {
      const result = await adminApi.resendTicket(token, id, 'all', eventSlug);
      if (result.requeued.length) {
        setError(`ส่งบัตรอีกครั้งแล้ว (${result.requeued.join(', ')}) — ถ้าผู้ให้บริการล่ม ระบบจะลองใหม่ให้เอง`);
      } else {
        const reasons = result.skipped.map((s) => RESEND_SKIP_REASON[s.reason] ?? s.reason).join(' · ');
        setError(`ส่งไม่ได้: ${reasons || 'ไม่มีช่องทางที่ส่งได้'}`);
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'ส่งบัตรอีกครั้งไม่สำเร็จ');
    } finally {
      setBusy('');
    }
  };

  const reviewCount = stats?.ordersByStatus.awaiting_verification ?? 0;
  const subTabCount = (key: ReviewSubTab) => {
    if (!stats) return null;
    if (key === '') return Object.values(stats.ordersByStatus).reduce((a, b) => a + b, 0);
    if (key === 'checked_in') return stats.checkin.checkedIn;
    if (key === 'problem') return stats.problemCount;
    return stats.ordersByStatus[key as OrderStatus] ?? 0;
  };

  const reviewTotalPages = Math.max(1, Math.ceil(orders.length / REVIEW_PAGE_SIZE));
  const reviewPageClamped = Math.min(reviewPage, reviewTotalPages);
  const pagedOrders = orders.slice((reviewPageClamped - 1) * REVIEW_PAGE_SIZE, reviewPageClamped * REVIEW_PAGE_SIZE);

  const meta = PAGE_META[tab];

  return (
    <div className="admin-shell">
      <aside className="admin-sidebar">
        <div className="admin-brand">
          <div className="name" title={eventName || undefined}>{eventName || 'JAMTICK'}</div>
          <div className="tag">STAFF PANEL</div>
        </div>

        <div className="admin-nav-label">การขาย</div>
        <div className="admin-nav-group">
          {NAV_SALES.map((n) => (
            <button key={n.key} className={`admin-nav-btn ${tab === n.key ? 'active' : ''}`} onClick={() => setTab(n.key)}>
              {n.icon}
              <span className="grow">{n.label}</span>
              {n.key === 'review' && reviewCount > 0 && <span className="admin-nav-badge">{reviewCount}</span>}
            </button>
          ))}
        </div>

        {role !== 'sales' && (
          <>
            <div className="admin-nav-label">ปฏิบัติการ</div>
            <div className="admin-nav-group">
              {NAV_OPS.filter((n) => !['payments', 'lineoa'].includes(n.key) || role === 'owner').map((n) => (
                <button key={n.key} className={`admin-nav-btn ${tab === n.key ? 'active' : ''}`} onClick={() => setTab(n.key)}>
                  {n.icon}
                  <span className="grow">{n.label}</span>
                </button>
              ))}
            </div>
          </>
        )}

        <div className="admin-sidebar-foot">
          <div className="admin-user-info">
            <div className="admin-avatar">A</div>
            <div className="admin-user-label">ทีมงาน · {role === 'sales' ? 'ฝ่ายขาย' : 'Admin'}</div>
          </div>
          <button
            className="admin-logout-btn"
            title="ออกจากระบบ"
            onClick={() => {
              sessionStorage.removeItem(tokenStorageKey);
              setToken('');
            }}
          >
            <IconLogout />
          </button>
        </div>
      </aside>

      <main className="admin-main">
        <PageHead
          tab={tab}
          right={
            tab === 'content' ? contentHeadRight
              : tab === 'tickets' ? ticketsHeadRight
              : (tab === 'overview' || tab === 'review') ? (
                <button className="btn-ghost" disabled={refreshCooldown} onClick={refreshNow}>
                  <IconRefresh /> {refreshCooldown ? 'รีเฟรชแล้ว…' : 'รีเฟรช'}
                </button>
              ) : undefined
          }
        />
        <div className={`admin-page-body ${meta.width}`}>
          {tab === 'overview' && (
            <>
              <StatsPanel stats={stats} error={statsError} />
              <RevenueTrendCard token={token} eventSlug={eventSlug} />
            </>
          )}

          {role !== 'sales' && tab === 'checkin' && <CheckinPanel token={token} eventSlug={eventSlug} />}

          {role !== 'sales' && tab === 'staff' && <StaffPanel token={token} eventSlug={eventSlug} />}

          {role !== 'sales' && tab === 'tickets' && <TicketsPanel token={token} eventSlug={eventSlug} headRight={setTicketsHeadRight} />}

          {role !== 'sales' && tab === 'wristbands' && <WristbandPanel token={token} eventSlug={eventSlug} />}

          {role === 'owner' && tab === 'payments' && <PaymentAccountsPanel token={token} eventSlug={eventSlug} />}

          {role === 'owner' && tab === 'delivery' && <TicketDeliveryPanel token={token} eventSlug={eventSlug} />}

          {role === 'owner' && tab === 'lineoa' && <LineOaPanel token={token} eventSlug={eventSlug} />}
          {role === 'owner' && tab === 'channels' && <SalesChannelsPanel token={token} eventSlug={eventSlug} />}

          {role !== 'sales' && tab === 'content' && <ContentPanel token={token} eventSlug={eventSlug} headRight={setContentHeadRight} />}

          {role !== 'sales' && tab === 'calendar' && (
            <ConcertCalendar embedded fetchData={() => adminApi.getConcertCalendar(token, eventSlug)} />
          )}

          {tab === 'review' && (
            <>
              <div>
                <div className="admin-subtabs">
                  {REVIEW_SUB_TABS.map((st) => {
                    const cnt = subTabCount(st.key);
                    return (
                      <button
                        key={st.key || 'all'}
                        className={`admin-subtab ${reviewSubTab === st.key ? 'active' : ''}`}
                        onClick={() => setReviewSubTab(st.key)}
                      >
                        <span>{st.label}</span>
                        {cnt !== null && <span className="admin-subtab-count">{cnt}</span>}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="admin-search-row">
                <div className="admin-search-wrap">
                  <IconSearch />
                  <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="ค้นหา รหัส / เบอร์โทร / อีเมล / ชื่อ" />
                </div>
                <button className="btn-ghost" style={{ display: 'inline-flex', alignItems: 'center', gap: 9 }} disabled={csvBusy} onClick={downloadCsv}>
                  <IconDownload />
                  {csvBusy ? 'กำลังเตรียมไฟล์…' : 'CSV'}
                </button>
              </div>

              {error && <div className="form-error">{error}</div>}

              {orders.length === 0 ? (
                <div className="admin-empty">ไม่มีรายการ</div>
              ) : (
                pagedOrders.map((o) => (
                  <div className="admin-review-card" key={o.id}>
                    <div className="admin-review-summary" style={{ flex: '1 1 240px', minWidth: 0 }}>
                      <div className="mono-label" style={{ marginBottom: 8 }}>ORDER SUMMARY</div>
                      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                        <span style={{ fontFamily: 'var(--mono)', fontSize: 13, fontWeight: 500 }}>{o.code}</span>
                        <span className={`status-chip ${o.status}`}>{STATUS_LABEL[o.status] ?? o.status}</span>
                      </div>
                      <div style={{ fontSize: 13.5, marginTop: 7 }}>
                        {o.name} <span style={{ color: 'var(--dim)' }}>· {o.phone}</span>
                      </div>
                      <div className="admin-order-lines">
                        {o.lines.map((l) => (
                          <div key={l.ticketId}>
                            <div className="admin-order-line">
                              <span>{l.label}</span>
                              <span className="amt">{fmt(l.lineTotal)}</span>
                            </div>
                            {formatCustomFieldValues(l.customFieldValues) && (
                              <div style={{ fontSize: 11.5, color: 'var(--dim)', fontFamily: 'var(--mono)', marginTop: -2, marginBottom: 4 }}>
                                {formatCustomFieldValues(l.customFieldValues)}
                              </div>
                            )}
                          </div>
                        ))}
                        {o.couponCode && (
                          <div className="admin-order-line">
                            <span>โค้ด {o.couponCode}</span>
                            <span className="amt">−{fmt(o.couponDiscount)}</span>
                          </div>
                        )}
                      </div>
                      <div className="admin-order-total">
                        <span>รวม {o.qty} ใบ · เข้างานได้ {o.entries} คน</span>
                        <span>{fmt(o.total)}</span>
                      </div>
                      {o.status === 'expired' && (
                        <div style={{ fontSize: 12, color: 'var(--danger, #d33)', marginTop: 3 }}>
                          เหตุผล: ลูกค้าไม่อัปโหลดสลิปภายใน {checkoutMinutes ?? 15} นาที
                        </div>
                      )}
                      {(o.status === 'awaiting_verification' || o.status === 'rejected') && o.slipVerification
                        && SLIP_VERIFICATION_REASON_LABEL[o.slipVerification.status] && (
                        <div style={{ fontSize: 12, color: 'var(--danger, #d33)', marginTop: 3 }}>
                          {o.status === 'rejected' ? 'เหตุผลที่ปฏิเสธ' : 'เหตุผลที่รอตรวจ'}:{' '}
                          {/* TICK-137 — a duplicate says which of the three it is; everything else keeps one line. */}
                          {(o.slipVerification.duplicateProvenance
                            && DUPLICATE_REASON_LABEL[o.slipVerification.duplicateProvenance])
                            || SLIP_VERIFICATION_REASON_LABEL[o.slipVerification.status]}
                        </div>
                      )}
                      {o.checkedInAt && (
                        <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 3 }}>
                          {wristbandReceivedLabel(o.checkedInAt)}
                          {o.checkedInBy ? ` โดย ${o.checkedInBy}` : ''}
                        </div>
                      )}
                      <DeliveryHistory rows={deliveries.filter((delivery) => delivery.orderId === o.id)} />
                    </div>
                    {o.hasSlip && (
                      <div className="admin-review-slip" style={{ flex: '0 0 auto' }}>
                        {o.status === 'awaiting_verification'
                          ? <SlipImage token={token} orderId={o.id} eventSlug={eventSlug} />
                          : <SlipToggle token={token} orderId={o.id} eventSlug={eventSlug} />}
                      </div>
                    )}
                    {o.status === 'paid' && (
                      <div style={{ display: 'flex', gap: 8, flex: '0 0 auto' }}>
                        <button
                          className="admin-review-icon-btn"
                          title="ส่งบัตรอีกครั้ง — ใช้เมื่อลูกค้าแจ้งว่าไม่ได้รับอีเมล/SMS ระบบจะส่งซ้ำไปที่เดิม กดซ้ำไม่ทำให้ส่งสองใบ"
                          disabled={busy === o.id}
                          onClick={() => resendTicket(o.id)}
                        >
                          <IconSend />
                        </button>
                      </div>
                    )}
                    {o.status === 'awaiting_verification' && (
                      <div style={{ display: 'flex', gap: 8, flex: '0 0 auto' }}>
                        {slipVerificationAllowsReverify(o.hasSlip) && (
                          <button
                            className="admin-review-icon-btn"
                            title="ตรวจใหม่ผ่านผู้ให้บริการ — ใช้เมื่อเพิ่งแก้ปัญหาที่ทำให้ตรวจอัตโนมัติไม่สำเร็จ (เช่น ลืมใส่คีย์ผู้ให้บริการ) หรือสงสัยว่าผลตรวจเดิมคลาดเคลื่อน โดยไม่ต้องรอลูกค้าส่งสลิปใหม่"
                            disabled={busy === o.id}
                            onClick={() => act(o.id, 'reverify')}
                          >
                            <IconRefresh />
                          </button>
                        )}
                        <button
                          className="admin-review-icon-btn reject"
                          title="ปฏิเสธ"
                          disabled={busy === o.id}
                          onClick={() => act(o.id, 'reject')}
                        >
                          <IconX />
                        </button>
                        {slipVerificationAllowsManualAction(o.slipVerification) && (
                          <button
                            className="admin-review-icon-btn approve"
                            title="อนุมัติ"
                            disabled={busy === o.id}
                            onClick={() => act(o.id, 'verify')}
                          >
                            <IconCheck />
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                ))
              )}

              {reviewTotalPages > 1 && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 14, marginTop: 18 }}>
                  <button
                    className="btn-ghost"
                    style={{ padding: '7px 14px' }}
                    disabled={reviewPageClamped <= 1}
                    onClick={() => setReviewPage((p) => Math.max(1, p - 1))}
                  >
                    ก่อนหน้า
                  </button>
                  <span style={{ fontSize: 13, color: 'var(--muted)', fontFamily: 'var(--mono)' }}>
                    หน้า {reviewPageClamped} / {reviewTotalPages}
                  </span>
                  <button
                    className="btn-ghost"
                    style={{ padding: '7px 14px' }}
                    disabled={reviewPageClamped >= reviewTotalPages}
                    onClick={() => setReviewPage((p) => Math.min(reviewTotalPages, p + 1))}
                  >
                    ถัดไป
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
}

/**
 * TICK-075 — the links an organizer hands to content creators, and what came back.
 *
 * Codes are registered before they are used, so the report adds up: one creator cannot become four
 * rows because four people typed their name four ways. An unrecognised code still appears, as
 * itself — "we sent traffic and nothing matched" is an answer the organizer needs.
 */
function SalesChannelsPanel({ token, eventSlug }: { token: string; eventSlug?: string }) {
  const [channels, setChannels] = useState<SalesChannelDto[]>([]);
  const [report, setReport] = useState<SalesChannelReportDto[]>([]);
  const [code, setCode] = useState('');
  const [label, setLabel] = useState('');
  const [platform, setPlatform] = useState<SalesChannelPlatform>('tiktok');
  /*
   * TICK-084/095 — independent of `platform`, because where a link is posted and how it travels are
   * two different facts. A TikTok post can detour through LINE; a LINE broadcast can go straight.
   *
   * One choice of three rather than two checkboxes: a link that sends people into the OA and also
   * lives inside it is a loop, which the server refuses. A radio group makes that a shape the form
   * cannot express, instead of an error the organizer meets after pressing สร้าง.
   */
  const [lineRoute, setLineRoute] = useState<SalesChannelLineRoute>('none');
  const [daily, setDaily] = useState<SalesChannelDailyDto[]>([]);
  const [days, setDays] = useState(30);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  const load = useCallback(async () => {
    const [list, rep, series] = await Promise.all([
      adminApi.listSalesChannels(token, eventSlug),
      adminApi.getSalesChannelReport(token, eventSlug),
      adminApi.getSalesChannelDaily(token, days, eventSlug),
    ]);
    setChannels(list);
    setReport(rep);
    setDaily(series);
  }, [token, eventSlug, days]);

  useEffect(() => { load().catch(() => setMsg('โหลดข้อมูลไม่สำเร็จ')); }, [load]);

  const add = async () => {
    setBusy(true);
    setMsg('');
    try {
      await adminApi.createSalesChannel(token, {
        code, platform, label,
        redirectToLine: lineRoute === 'into_oa',
        resolveFromLine: lineRoute === 'inside_oa',
      }, eventSlug);
      setCode('');
      setLabel('');
      await load();
    } catch (err) {
      setMsg(err instanceof ApiError ? err.message : 'สร้างไม่สำเร็จ');
    } finally {
      setBusy(false);
    }
  };

  const archive = async (id: string) => {
    setBusy(true);
    try {
      await adminApi.archiveSalesChannel(token, id, eventSlug);
      await load();
    } finally {
      setBusy(false);
    }
  };

  /*
   * TICK-095 — the one field on a live channel that can be corrected.
   *
   * Reloads rather than patching the row in place: the link in the next column is derived from this
   * value, and a table showing the new route beside the old URL is the one state an organizer must
   * never copy from.
   */
  const changeRoute = async (id: string, route: SalesChannelLineRoute) => {
    setBusy(true);
    setMsg('');
    try {
      await adminApi.setSalesChannelLineRoute(token, id, route, eventSlug);
      await load();
    } catch (err) {
      setMsg(err instanceof ApiError ? err.message : 'เปลี่ยนเส้นทางไม่สำเร็จ');
    } finally {
      setBusy(false);
    }
  };

  const origin = typeof window === 'undefined' ? '' : window.location.origin;
  const linkFor = (c: SalesChannelDto) => {
    const base = `${origin}/e/${eventSlug ?? ''}?ref=${encodeURIComponent(c.code)}`;
    if (c.redirect_to_line) return `${base}&redirect_to_line=true`;
    // TICK-095 — the marker the landing page reads before it decides which code to remember.
    if (c.resolve_from_line) return `${base}&from_line=true`;
    return base;
  };

  const num = (n: number | null) => (n === null ? '—' : n.toLocaleString());

  return (
    <div style={{ display: 'grid', gap: 20 }}>
      <div className="panel" style={{ padding: 16 }}>
        <h3 style={{ marginTop: 0 }}>สร้างลิงก์ให้ผู้ลงคอนเทนต์</h3>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <label style={{ display: 'grid', gap: 4, fontSize: 12 }}>
            โค้ด (a-z, 0-9, - _)
            <input value={code} onChange={(e) => setCode(e.target.value)} placeholder="aum" style={{ width: 160 }} />
          </label>
          <label style={{ display: 'grid', gap: 4, fontSize: 12 }}>
            ชื่อที่ใช้เรียก
            <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="อั้ม (TikTok)" style={{ width: 200 }} />
          </label>
          <label style={{ display: 'grid', gap: 4, fontSize: 12 }}>
            แพลตฟอร์ม <span style={{ color: 'var(--dim)' }}>โพสต์ที่ไหน</span>
            <select value={platform} onChange={(e) => setPlatform(e.target.value as SalesChannelPlatform)}>
              <option value="tiktok">TikTok</option>
              <option value="facebook">Facebook</option>
              <option value="instagram">Instagram</option>
              <option value="line">LINE</option>
              <option value="other">อื่น ๆ</option>
            </select>
          </label>
          <button type="button" className="btn-primary" disabled={busy || !code.trim() || !label.trim()} onClick={add}>
            สร้าง
          </button>
        </div>

        <div style={{ marginTop: 14, borderTop: '1px solid var(--border)', paddingTop: 13, display: 'grid', gap: 10 }}>
          <div style={{ fontSize: 12, color: 'var(--dim)' }}>ลิงก์นี้เกี่ยวกับ LINE OA อย่างไร</div>
          <label style={{ display: 'flex', gap: 9, alignItems: 'flex-start', fontSize: 13 }}>
            <input type="radio" name="line-route" checked={lineRoute === 'none'} onChange={() => setLineRoute('none')} style={{ marginTop: 3 }} />
            <span>
              ไม่เกี่ยว — เข้าหน้าซื้อบัตรตรง
            </span>
          </label>
          <label style={{ display: 'flex', gap: 9, alignItems: 'flex-start', fontSize: 13 }}>
            <input type="radio" name="line-route" checked={lineRoute === 'into_oa'} onChange={() => setLineRoute('into_oa')} style={{ marginTop: 3 }} />
            <span>
              พาไปแอด LINE OA ของผู้จัดก่อน
              <span style={{ display: 'block', color: 'var(--dim)', fontSize: 12 }}>
                ลูกค้าจะถูกส่งเข้าแอป LINE เพื่อแอดเพื่อน แล้วค่อยกลับมาซื้อผ่าน Rich Menu · ติ๊กได้ทุกแพลตฟอร์ม
              </span>
            </span>
          </label>
          <label style={{ display: 'flex', gap: 9, alignItems: 'flex-start', fontSize: 13 }}>
            <input type="radio" name="line-route" checked={lineRoute === 'inside_oa'} onChange={() => setLineRoute('inside_oa')} style={{ marginTop: 3 }} />
            <span>
              ลิงก์นี้อยู่ใน LINE OA เอง (Rich Menu / ข้อความ)
              <span style={{ display: 'block', color: 'var(--dim)', fontSize: 12 }}>
                ยอดขายจะถูกนับให้ลิงก์ที่พาลูกค้าเข้ามาแอด ไม่ใช่ลิงก์นี้ · คลิกยังนับที่ลิงก์นี้ตามจริง
              </span>
            </span>
          </label>
        </div>
        {msg && <div style={{ marginTop: 10, color: '#e08a8a', fontSize: 13 }}>{msg}</div>}
      </div>

      <div className="panel" style={{ padding: 16 }}>
        <h3 style={{ marginTop: 0 }}>ลิงก์ที่ใช้อยู่</h3>
        {channels.length === 0 ? (
          <div style={{ color: 'var(--muted)', fontSize: 13 }}>ยังไม่มีลิงก์ — สร้างอันแรกด้านบน</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="admin-table" style={{ minWidth: 640 }}>
              <thead><tr><th>ชื่อ</th><th>แพลตฟอร์ม</th><th>เส้นทาง LINE</th><th>ลิงก์ที่ส่งให้</th><th /></tr></thead>
              <tbody>
                {channels.map((c) => (
                  <tr key={c.id}>
                    <td>{c.label}</td>
                    <td style={{ fontFamily: 'var(--mono)', fontSize: 12 }}>{c.platform}</td>
                    <td>
                      <select
                        value={salesChannelLineRouteOf(c)}
                        disabled={busy}
                        onChange={(e) => changeRoute(c.id, e.target.value as SalesChannelLineRoute)}
                        style={{ fontSize: 12, maxWidth: 200 }}
                      >
                        <option value="none">ไม่เกี่ยว — เข้าหน้าซื้อบัตรตรง</option>
                        <option value="into_oa">พาไปแอด LINE OA ก่อน</option>
                        <option value="inside_oa">อยู่ใน LINE OA (Rich Menu)</option>
                      </select>
                      {c.resolve_from_line ? (
                        <span style={{ display: 'block', fontSize: 11, color: '#8ea9d8', marginTop: 3 }}>
                          ยกเครดิตยอดขายให้ลิงก์ต้นทาง
                        </span>
                      ) : null}
                    </td>
                    <td style={{ fontFamily: 'var(--mono)', fontSize: 12, wordBreak: 'break-all' }}>{linkFor(c)}</td>
                    <td>
                      <button type="button" className="btn-ghost" disabled={busy} onClick={() => archive(c.id)}>
                        เลิกใช้
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="panel" style={{ padding: 16 }}>
        <h3 style={{ marginTop: 0 }}>ผลของแต่ละช่องทาง</h3>
        {/*
          * TICK-084 replaced the old disclaimer here — clicks are counted now, so the report can
          * carry a rate. What it still cannot say is written per-column instead: an em dash is a
          * number we do not have, and a zero is one we measured.
          */}
        <div style={{ color: 'var(--muted)', fontSize: 12, marginBottom: 10 }}>
          คลิกนับแบบกันซ้ำในหนึ่ง session — คนเดียวเปิดสองเครื่องยังนับสอง ใช้เทียบช่องทางกันเอง ไม่ใช่จำนวนคนจริง
          · ขีดคือไม่มีอะไรให้วัด ไม่ใช่ศูนย์
        </div>
        {report.length === 0 ? (
          <div style={{ color: 'var(--muted)', fontSize: 13 }}>ยังไม่มีใครกดลิงก์ไหนเลย</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="admin-table" style={{ minWidth: 720 }}>
              <thead>
                <tr>
                  <th>ช่องทาง</th>
                  <th style={{ textAlign: 'right' }}>คลิก</th>
                  <th style={{ textAlign: 'right' }}>ผ่าน LINE</th>
                  <th style={{ textAlign: 'right' }}>ออร์เดอร์</th>
                  <th style={{ textAlign: 'right' }}>จ่ายแล้ว</th>
                  <th style={{ textAlign: 'right' }}>ยอดขาย</th>
                </tr>
              </thead>
              <tbody>
                {report.map((r) => (
                  <tr key={r.channelId ?? 'unknown'}>
                    <td>
                      {r.label}
                      {r.code && <span style={{ color: 'var(--muted)', fontFamily: 'var(--mono)', fontSize: 11 }}> · {r.code}</span>}
                      {r.clicks !== null && r.clicks > 0 && (
                        <ChannelBar clicks={r.clicks} orders={r.orders} paid={r.paidOrders} />
                      )}
                    </td>
                    <td style={{ textAlign: 'right', fontFamily: 'var(--mono)' }}>{num(r.clicks)}</td>
                    <td style={{ textAlign: 'right', fontFamily: 'var(--mono)', color: r.lineRedirects ? '#6fbf8e' : undefined }}>
                      {r.redirectToLine === null ? '—' : num(r.lineRedirects)}
                    </td>
                    <td style={{ textAlign: 'right', fontFamily: 'var(--mono)' }}>{r.orders.toLocaleString()}</td>
                    <td style={{ textAlign: 'right', fontFamily: 'var(--mono)', color: '#6fbf8e' }}>{r.paidOrders.toLocaleString()}</td>
                    <td style={{ textAlign: 'right', fontFamily: 'var(--mono)' }}>{fmt(r.revenue)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="panel" style={{ padding: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <h3 style={{ margin: 0 }}>ย้อนหลัง</h3>
          <div style={{ display: 'inline-flex', gap: 4 }}>
            {[7, 30, 90].map((d) => (
              <button
                key={d} type="button" className={d === days ? 'btn-primary' : 'btn-ghost'}
                onClick={() => setDays(d)} style={{ padding: '4px 12px', fontSize: 12 }}
              >
                {d} วัน
              </button>
            ))}
          </div>
        </div>
        <DailyChart rows={daily} days={days} />
      </div>
    </div>
  );
}

/** Clicks, of which ordered, of which paid — the same three-part split the table columns carry. */
function ChannelBar({ clicks, orders, paid }: { clicks: number; orders: number; paid: number }) {
  const pct = (n: number) => `${Math.min(100, (n / Math.max(clicks, 1)) * 100)}%`;
  return (
    <div style={{ display: 'flex', height: 6, width: 150, borderRadius: 4, overflow: 'hidden', background: 'var(--bg)', marginTop: 7 }}>
      <span style={{ width: pct(paid), background: '#6fbf8e' }} />
      <span style={{ width: pct(Math.max(0, orders - paid)), background: '#d9b36a' }} />
    </div>
  );
}

/**
 * TICK-084 — clicks and paid orders by day, every channel together.
 *
 * Clicks are bars and paid orders a line, because they are not two halves of one quantity: an order
 * on Tuesday can belong to a click on Monday. Stacking them would draw a funnel that the data does
 * not support. Days with nothing are still drawn — a gap in a campaign is a finding, and a chart
 * that omits empty days quietly reports a busier week than there was.
 */
function DailyChart({ rows, days }: { rows: SalesChannelDailyDto[]; days: number }) {
  const series: { day: string; clicks: number; paid: number }[] = useMemo(() => {
    const byDay = new Map<string, { clicks: number; paid: number }>();
    for (let i = days - 1; i >= 0; i -= 1) {
      const d = new Date(Date.now() + 7 * 3600_000 - i * 86_400_000).toISOString().slice(0, 10);
      byDay.set(d, { clicks: 0, paid: 0 });
    }
    for (const r of rows) {
      const slot = byDay.get(r.day);
      if (!slot) continue;
      slot.clicks += r.clicks;
      slot.paid += r.paidOrders;
    }
    return [...byDay].map(([day, v]) => ({ day, ...v }));
  }, [rows, days]);

  const maxClicks = Math.max(1, ...series.map((d) => d.clicks));
  const maxPaid = Math.max(1, ...series.map((d) => d.paid));
  const totals = series.reduce((a, d) => ({ clicks: a.clicks + d.clicks, paid: a.paid + d.paid }), { clicks: 0, paid: 0 });

  if (totals.clicks === 0 && totals.paid === 0) {
    return <div style={{ color: 'var(--muted)', fontSize: 13, marginTop: 12 }}>ยังไม่มีข้อมูลในช่วงนี้</div>;
  }

  const W = 640;
  const H = 120;
  const step = W / series.length;
  const barW = Math.max(1.5, step * 0.62);
  const line = series
    .map((d, i) => `${i === 0 ? 'M' : 'L'} ${(i + 0.5) * step} ${H - (d.paid / maxPaid) * (H - 10)}`)
    .join(' ');

  return (
    <div style={{ marginTop: 14 }}>
      <div style={{ overflowX: 'auto' }}>
        <svg viewBox={`0 0 ${W} ${H + 16}`} width="100%" height={H + 16} role="img"
          aria-label={`คลิก ${totals.clicks} ครั้ง และจ่ายแล้ว ${totals.paid} ออร์เดอร์ ใน ${days} วันที่ผ่านมา`}>
          {series.map((d, i) => (
            <rect key={d.day} x={(i + 0.5) * step - barW / 2} width={barW}
              y={H - (d.clicks / maxClicks) * (H - 10)} height={(d.clicks / maxClicks) * (H - 10)}
              rx={1.5} fill="#3a4046" />
          ))}
          <path d={line} fill="none" stroke="#6fbf8e" strokeWidth={1.75} strokeLinejoin="round" />
          <line x1="0" y1={H} x2={W} y2={H} stroke="var(--border)" strokeWidth="1" />
        </svg>
      </div>
      <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', fontSize: 12, color: 'var(--muted)', marginTop: 8 }}>
        <span><span style={{ display: 'inline-block', width: 9, height: 9, background: '#3a4046', borderRadius: 2, marginRight: 6 }} />คลิก {totals.clicks.toLocaleString()}</span>
        <span><span style={{ display: 'inline-block', width: 9, height: 2, background: '#6fbf8e', marginRight: 6, verticalAlign: 'middle' }} />จ่ายแล้ว {totals.paid.toLocaleString()}</span>
        <span style={{ color: 'var(--dim)' }}>{series[0]?.day} → {series[series.length - 1]?.day}</span>
      </div>
    </div>
  );
}

/**
 * TICK-077 — physical cards, printed before anyone buys them.
 *
 * Lives inside ticket management because that is what a batch is: stock of one ticket type. The
 * counter half sits here too, so the person who prints the run and the person who sells from it are
 * looking at the same numbers.
 */
/* ==================================================================== */
/* TICK-080 — the counter's stock, as a console rather than a list       */
/* ==================================================================== */

const SKIP_REASON: Record<OfflineCardSkipDto['reason'], string> = {
  VOID: 'ถูกยกเลิก',
  ALREADY_SOLD: 'เปิดใช้งานไปแล้ว',
  NOT_SOLD: 'ยังไม่ได้เปิดใช้งาน',
  CHECKED_IN: 'เช็คอินไปแล้ว — ปิดไม่ได้',
  RACED: 'มีคนอื่นทำไปก่อน',
};

function skipSummary(skipped: OfflineCardSkipDto[]): string {
  if (!skipped.length) return '';
  const byReason = new Map<string, string[]>();
  for (const s of skipped) {
    const k = SKIP_REASON[s.reason] ?? s.reason;
    byReason.set(k, [...(byReason.get(k) ?? []), s.serial]);
  }
  return [...byReason].map(([reason, serials]) =>
    `${serials.length} ใบ${reason ? ` (${reason})` : ''}: ${serials.slice(0, 6).join(', ')}${serials.length > 6 ? '…' : ''}`,
  ).join(' · ');
}

/**
 * The capacity bar deliberately breaks out of its own track when stock exceeds capacity.
 *
 * Squeezing the overflow to fit would draw a full bar — which is exactly what a healthy run looks
 * like — for the one state that most needs to be noticed.
 */
function QuotaCard({ s }: { s: OfflineSummaryDto }) {
  const over = s.unreserved > 0;
  const total = Math.max(s.capacity, s.printed) || 1;
  const pct = (n: number) => `${(n / total) * 100}%`;
  const heldUnsold = Math.max(0, s.reserved - s.sold);

  return (
    <div className="panel" style={{ padding: 16, borderColor: over ? 'var(--danger, #d99a9a)' : undefined }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'flex-start' }}>
        <div>
          <div style={{ fontWeight: 600 }}>{s.name}</div>
          <div style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--dim)' }}>ความจุ {s.capacity.toLocaleString()} ที่นั่ง</div>
        </div>
        <span style={{ fontFamily: 'var(--mono)', fontSize: 11.5, color: 'var(--gold, #d9b36a)' }}>
          {s.serialPrefix ?? '— ยังไม่ตั้งรหัส —'}
        </span>
      </div>

      <div style={{ position: 'relative', height: 26, borderRadius: 6, background: 'var(--bg)', display: 'flex', margin: '14px 0 8px' }}>
        <span style={{ width: pct(s.sold), background: '#6fbf8e', borderRadius: '6px 0 0 6px' }} />
        <span style={{ width: pct(heldUnsold), background: '#d9b36a' }} />
        <span style={{ width: pct(Math.max(0, s.capacity - s.reserved)) }} />
        {over && <span style={{ width: pct(s.unreserved), background: 'repeating-linear-gradient(45deg,#d99a9a 0 6px,#a85a52 6px 12px)', borderRadius: '0 6px 6px 0' }} />}
        {over && <span style={{ position: 'absolute', top: -4, bottom: -4, left: pct(s.capacity), width: 2, background: 'var(--fg)' }} />}
      </div>

      <table className="admin-table" style={{ fontSize: 13 }}>
        <tbody>
          <tr><td>พิมพ์ออกมาแล้ว</td><td style={{ textAlign: 'right', fontFamily: 'var(--mono)' }}>{s.printed.toLocaleString()}</td></tr>
          <tr><td>ขายแล้ว</td><td style={{ textAlign: 'right', fontFamily: 'var(--mono)', color: '#6fbf8e' }}>{s.sold.toLocaleString()}</td></tr>
          <tr><td>ยังไม่ขาย</td><td style={{ textAlign: 'right', fontFamily: 'var(--mono)', color: '#d9b36a' }}>{s.unsold.toLocaleString()}</td></tr>
          <tr><td>ยกเลิก คืนโควตาแล้ว</td><td style={{ textAlign: 'right', fontFamily: 'var(--mono)', color: 'var(--dim)' }}>{s.voided ? `+${s.voided.toLocaleString()}` : '0'}</td></tr>
          {over && (
            <tr><td style={{ color: '#d99a9a' }}>ไม่มีที่นั่งรองรับ</td><td style={{ textAlign: 'right', fontFamily: 'var(--mono)', color: '#d99a9a' }}>{s.unreserved.toLocaleString()}</td></tr>
          )}
          <tr>
            <td style={{ fontWeight: 600, paddingTop: 10 }}>โควตาที่ยังใช้ได้</td>
            <td style={{ textAlign: 'right', fontFamily: 'var(--mono)', fontSize: 18, fontWeight: 600, color: '#6fbf8e', paddingTop: 10 }}>
              {s.remaining.toLocaleString()}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

/** Icon-only row actions. Disabled rather than hidden, so the buttons never move between rows. */
function RunTool({ kind, title, disabled, onClick }: {
  kind: 'zip' | 'activate' | 'void'; title: string; disabled?: boolean; onClick: () => void;
}) {
  const path = kind === 'zip'
    ? <><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><path d="M7 10l5 5 5-5" /><path d="M12 15V3" /></>
    : kind === 'activate'
      ? <path d="M13 2 4.1 12.7a1 1 0 0 0 .8 1.6H11l-1 7.7 8.9-10.7a1 1 0 0 0-.8-1.6H12l1-7.7Z" />
      : <><circle cx="12" cy="12" r="9" /><path d="m5.6 5.6 12.8 12.8" /></>;
  return (
    <button type="button" className="btn-ghost" title={title} aria-label={title} disabled={disabled} onClick={onClick}
      style={{ width: 33, height: 33, padding: 0, display: 'grid', placeItems: 'center' }}>
      <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{path}</svg>
    </button>
  );
}

/**
 * What to pre-fill the print quantity with — never more than the ticket type has left.
 *
 * It used to be a flat `50`. A type with 4 unsold seats therefore opened the dialog already
 * refused: quota "-46" in red and the print button disabled, before the operator had touched
 * anything. A default we chose is not the operator's number, so it has no business being the one
 * that puts the form in an invalid state — it follows the quota instead, and only a figure the
 * operator typed themselves is allowed to overflow it.
 */
function suggestedPrintQuantity(type: AdminTicketDto | undefined): string {
  const remaining = type ? type.capacity - type.sold : 0;
  // Floors at 1 rather than 0: a sold-out type has no valid quantity at all, and the overflow
  // warning is the honest way to say so — a silent 0 in the box reads like a broken field.
  return String(Math.max(1, Math.min(50, remaining)));
}

function PrintRunModal({ types, batches, busy, onClose, onPrint }: {
  types: AdminTicketDto[];
  batches: OfflineBatchDto[];
  busy: boolean;
  onClose: () => void;
  onPrint: (input: { ticketId: string; label: string; quantity: number }) => void;
}) {
  const [ticketId, setTicketId] = useState(types[0]?.id ?? '');
  const [label, setLabel] = useState('');
  /* Held as text so clearing the box leaves it empty — a numeric state turns '' into 0 and puts it back. */
  const [quantity, setQuantity] = useState(() => suggestedPrintQuantity(types[0]));
  /* Once the operator types their own number, it is theirs — switching type must not overwrite it. */
  const [quantityEdited, setQuantityEdited] = useState(false);

  const type = types.find((t) => t.id === ticketId) ?? null;
  const runs = batches.filter((b) => b.ticket_id === ticketId);
  const nextRun = runs.reduce((max, b) => Math.max(max, b.run_no), 0) + 1;
  const remaining = type ? type.capacity - type.sold : 0;
  const qty = Number(quantity);
  const valid = Number.isInteger(qty) && qty >= 1 && qty <= 2000;
  const after = valid ? remaining - qty : remaining;
  const overflows = valid && after < 0;
  const ready = Boolean(type?.serialPrefix) && valid && !overflows;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      role="presentation"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{ position: 'fixed', inset: 0, background: 'rgba(6,7,8,.72)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '6vh 20px 20px', zIndex: 60, overflowY: 'auto' }}
    >
      <div className="panel" role="dialog" aria-modal="true" aria-labelledby="printRunTitle" style={{ width: 'min(540px,100%)', padding: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start' }}>
          <div>
            <h3 id="printRunTitle" style={{ margin: 0 }}>สั่งพิมพ์ล็อตใหม่</h3>
            <div style={{ fontSize: 13, color: 'var(--muted)' }}>การสั่งพิมพ์จะกันโควตาทันที</div>
          </div>
          <button type="button" className="btn-ghost" aria-label="ปิด" onClick={onClose} style={{ width: 32, height: 32, padding: 0 }}>✕</button>
        </div>

        <div style={{ display: 'grid', gap: 13, marginTop: 16 }}>
          <label style={{ display: 'grid', gap: 5, fontSize: 12.5 }}>
            ประเภทตั๋ว
            <select
              value={ticketId}
              onChange={(e) => {
                setTicketId(e.target.value);
                // A different type has a different quota, so an untouched suggestion has to follow it.
                if (!quantityEdited) setQuantity(suggestedPrintQuantity(types.find((t) => t.id === e.target.value)));
              }}
            >
              {types.map((t) => <option key={t.id} value={t.id}>{t.nameTh || t.nameEn}</option>)}
            </select>
          </label>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 13 }}>
            <label style={{ display: 'grid', gap: 5, fontSize: 12.5 }}>
              รหัสนำหน้า <span style={{ color: 'var(--dim)' }}>(ตั้งที่ประเภทตั๋ว)</span>
              <input readOnly disabled value={type?.serialPrefix ?? ''} placeholder="— ยังไม่ได้ตั้ง —" style={{ fontFamily: 'var(--mono)', letterSpacing: 2 }} />
            </label>
            <label style={{ display: 'grid', gap: 5, fontSize: 12.5 }}>
              จำนวน
              <input
                type="number" min={1} max={2000} value={quantity}
                onChange={(e) => { setQuantity(e.target.value); setQuantityEdited(true); }}
              />
            </label>
          </div>

          <label style={{ display: 'grid', gap: 5, fontSize: 12.5 }}>
            ชื่อล็อต <span style={{ color: 'var(--dim)' }}>(ว่างได้)</span>
            <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder={`พิมพ์ครั้งที่ ${nextRun}`} />
          </label>

          {/* Recomputed as the number is typed, because it is the figure that decides whether the run is allowed. */}
          <div className="panel" style={{ padding: '11px 13px', display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 10, borderColor: overflows ? '#d99a9a' : undefined }}>
            <span style={{ fontSize: 13, color: 'var(--muted)' }}>โควตาที่ยังใช้ได้ หลังพิมพ์ล็อตนี้</span>
            <span style={{ fontFamily: 'var(--mono)', fontSize: 17, fontWeight: 600, color: overflows ? '#d99a9a' : '#6fbf8e' }}>{after}</span>
          </div>

          {!type?.serialPrefix && (
            <div className="form-error">ประเภทตั๋วนี้ยังไม่ได้ตั้ง<strong>รหัสนำหน้า 4 หลัก</strong> — ตั้งที่แท็บ "สร้างประเภทตั๋ว" ก่อน</div>
          )}
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 9, marginTop: 18 }}>
          <button type="button" className="btn-ghost" onClick={onClose} disabled={busy}>ยกเลิก</button>
          <button type="button" className="btn-primary" disabled={busy || !ready}
            onClick={() => onPrint({ ticketId, label, quantity: qty })}>
            {ready ? `สั่งพิมพ์ ${qty} ใบ` : 'สั่งพิมพ์'}
          </button>
        </div>
      </div>
    </div>
  );
}

function OfflineBatchPanel({ token, eventSlug, tickets, onChanged }: {
  token: string; eventSlug?: string; tickets: AdminTicketDto[]; onChanged: () => void;
}) {
  const [batches, setBatches] = useState<OfflineBatchDto[]>([]);
  const [summary, setSummary] = useState<OfflineSummaryDto[]>([]);
  const [serial, setSerial] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [printing, setPrinting] = useState(false);
  const [activating, setActivating] = useState<{ id: string; done: number; left: number } | null>(null);

  /* The run whose cards are on screen, and the cards themselves. One run at a time: a console that
     expands three runs of 2000 is a console nobody can read. */
  const [openId, setOpenId] = useState<string | null>(null);
  const [cards, setCards] = useState<OfflineCardDto[]>([]);
  const [cardsLoading, setCardsLoading] = useState(false);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [filter, setFilter] = useState<'all' | 'sold' | 'unsold' | 'void'>('all');

  const offlineTypes = tickets.filter((t) => t.offline);

  const load = useCallback(async () => {
    const [b, s] = await Promise.all([
      adminApi.listOfflineBatches(token, eventSlug),
      adminApi.getOfflineSummary(token, eventSlug),
    ]);
    setBatches(b);
    setSummary(s);
  }, [token, eventSlug]);

  useEffect(() => { load().catch(() => setMsg('โหลดข้อมูลล็อตไม่สำเร็จ')); }, [load]);

  const openRun = async (batch: OfflineBatchDto) => {
    if (openId === batch.id) { setOpenId(null); return; }
    setOpenId(batch.id);
    setPicked(new Set());
    setFilter('all');
    setCardsLoading(true);
    try {
      setCards(await adminApi.listOfflineCards(token, batch.id, eventSlug));
    } catch (err) {
      setMsg(err instanceof ApiError ? err.message : 'โหลดเลขบัตรไม่สำเร็จ');
      setCards([]);
    } finally {
      setCardsLoading(false);
    }
  };

  const reloadCards = async () => {
    if (!openId) return;
    setCards(await adminApi.listOfflineCards(token, openId, eventSlug));
    setPicked(new Set());
  };

  const create = async (input: { ticketId: string; label: string; quantity: number }) => {
    setBusy(true);
    setMsg('');
    try {
      await adminApi.createOfflineBatch(token, input, eventSlug);
      setPrinting(false);
      await load();
      // The run has taken capacity out of the storefront, so the ticket list is now stale.
      onChanged();
    } catch (err) {
      setMsg(err instanceof ApiError ? err.message : 'สร้างล็อตไม่สำเร็จ');
    } finally {
      setBusy(false);
    }
  };

  const download = async (batch: OfflineBatchDto) => {
    setBusy(true);
    setMsg('');
    try {
      const cards = await adminApi.listOfflineCards(token, batch.id, eventSlug);
      const { zipSync, strToU8 } = await import('fflate');
      const files: Record<string, Uint8Array> = {};

      for (const card of cards) {
        const canvas = document.createElement('canvas');
        await QRCode.toCanvas(canvas, card.payload, { width: 600, margin: 2 });
        const withSerial = document.createElement('canvas');
        withSerial.width = canvas.width;
        withSerial.height = canvas.height + 90;
        const ctx = withSerial.getContext('2d')!;
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, withSerial.width, withSerial.height);
        ctx.drawImage(canvas, 0, 0);
        ctx.fillStyle = '#000000';
        ctx.font = 'bold 52px ui-monospace, monospace';
        ctx.textAlign = 'center';
        ctx.fillText(card.serial, withSerial.width / 2, canvas.height + 60);

        const blob: Blob = await new Promise((resolve) => withSerial.toBlob((b) => resolve(b!), 'image/png'));
        files[`${card.serial}.png`] = new Uint8Array(await blob.arrayBuffer());
      }
      files['README.txt'] = strToU8(
        `${batch.label} · ${batch.serial_prefix} · ${cards.length} ใบ\n`
        + 'บัตรทุกใบยังไม่เปิดใช้งาน — ต้องขายที่จุดจำหน่ายก่อนจึงจะสแกนเข้างานได้\n',
      );

      // Stored, not deflated: PNG is already compressed, so the CPU buys nothing.
      const zipped = zipSync(files, { level: 0 });
      const url = URL.createObjectURL(new Blob([zipped], { type: 'application/zip' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${batch.serial_prefix}-${cards.length}.zip`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setMsg(err instanceof ApiError ? err.message : 'สร้างไฟล์ไม่สำเร็จ');
    } finally {
      setBusy(false);
    }
  };

  /**
   * Sell a whole run.
   *
   * The loop lives here rather than in the Worker: a run holds up to 2000 cards and each one becomes
   * an order, which is the shape of work measured being cancelled at about two seconds. Each call
   * takes a slice and says what is left, so the progress the owner watches is real rather than a
   * spinner over one long request — and an interrupted run is simply a run with cards still in it.
   */
  const activateBatch = async (batch: OfflineBatchDto) => {
    if (!window.confirm(
      `เปิดใช้งานทั้งล็อต "${batch.label}" — บัตรที่ยังไม่ขาย ${batch.unsold} ใบจะกลายเป็นคำสั่งซื้อที่ชำระแล้วทันที และนับเป็นยอดขาย\n\nยืนยันหรือไม่?`,
    )) return;
    setBusy(true);
    setMsg('');
    let done = 0;
    try {
      for (;;) {
        const { activated, remaining } = await adminApi.activateOfflineBatch(token, batch.id, eventSlug);
        done += activated;
        setActivating({ id: batch.id, done, left: remaining });
        if (remaining === 0) break;
        // A slice that activates nothing while cards remain means every one was taken by a counter
        // mid-loop. Stopping beats spinning on a set this pass cannot move.
        if (activated === 0) break;
      }
      setMsg(`เปิดใช้งานแล้ว ${done} ใบ ในล็อต "${batch.label}"`);
      await load();
      if (openId === batch.id) await reloadCards();
      onChanged();
    } catch (err) {
      setMsg(err instanceof ApiError ? err.message : 'เปิดใช้งานทั้งล็อตไม่สำเร็จ');
    } finally {
      setActivating(null);
      setBusy(false);
    }
  };

  const voidBatch = async (batch: OfflineBatchDto) => {
    if (!window.confirm(`ยกเลิกล็อต "${batch.label}"? บัตรที่ยังไม่ขายจะใช้ไม่ได้ และโควตาจะคืนกลับ`)) return;
    setBusy(true);
    setMsg('');
    try {
      const { voided } = await adminApi.voidOfflineBatch(token, batch.id, eventSlug);
      setMsg(`ยกเลิกแล้ว ${voided} ใบ`);
      await load();
      if (openId === batch.id) await reloadCards();
      onChanged();
    } catch (err) {
      setMsg(err instanceof ApiError ? err.message : 'ยกเลิกล็อตไม่สำเร็จ');
    } finally {
      setBusy(false);
    }
  };

  const activateOne = async () => {
    setBusy(true);
    setMsg('');
    try {
      const res = await adminApi.activateOfflineTicket(token, serial.trim(), eventSlug);
      setMsg(res.alreadySold
        ? `${res.serial} ขายไปแล้วก่อนหน้านี้ (${res.order.code})`
        : `${res.serial} เปิดใช้งานแล้ว · ${res.order.code}`);
      setSerial('');
      await load();
      if (openId) await reloadCards();
      onChanged();
    } catch (err) {
      setMsg(err instanceof ApiError ? err.message : 'เปิดใช้งานไม่สำเร็จ');
    } finally {
      setBusy(false);
    }
  };

  /** The ticked cards, in either direction. Each button acts only on the half it can act on. */
  const moveSelected = async (dir: 'on' | 'off') => {
    const ids = [...picked];
    if (!ids.length) return;
    if (dir === 'off' && !window.confirm(
      `ปิดใช้งาน ${ids.length} ใบ — คำสั่งซื้อที่ผูกอยู่จะถูกยกเลิก และบัตรกลับเป็นสต็อก\n\nบัตรที่เช็คอินไปแล้วจะถูกข้าม\n\nยืนยันหรือไม่?`,
    )) return;
    setBusy(true);
    setMsg('');
    try {
      const res = dir === 'on'
        ? await adminApi.activateOfflineSelected(token, ids, eventSlug)
        : await adminApi.deactivateOfflineSelected(token, ids, eventSlug);
      const moved = 'activated' in res ? res.activated : res.deactivated;
      const verb = dir === 'on' ? 'เปิดใช้งาน' : 'ปิดใช้งาน';
      const skipped = skipSummary(res.skipped);
      setMsg(`${verb}แล้ว ${moved} ใบ${skipped ? ` · ข้าม ${skipped}` : ''}`);
      await load();
      await reloadCards();
      onChanged();
    } catch (err) {
      setMsg(err instanceof ApiError ? err.message : `${dir === 'on' ? 'เปิด' : 'ปิด'}ใช้งานไม่สำเร็จ`);
    } finally {
      setBusy(false);
    }
  };

  const shown = cards.filter((c) => filter === 'all' || c.status === filter);
  const pickedCards = cards.filter((c) => picked.has(c.id));
  const canOpen = pickedCards.filter((c) => c.status === 'unsold').length;
  const canClose = pickedCards.filter((c) => c.status === 'sold').length;
  const nameOf = (id: string) => {
    const t = tickets.find((x) => x.id === id);
    return t ? (t.nameTh || t.nameEn) : id;
  };

  if (!offlineTypes.length) {
    return (
      <div className="panel" style={{ padding: 16 }}>
        <h3 style={{ marginTop: 0 }}>บัตรขายหน้างาน</h3>
        <div style={{ color: 'var(--muted)', fontSize: 13 }}>
          ยังไม่มีประเภทตั๋วที่ตั้งเป็น "บัตรขายหน้างาน" — ไปที่แท็บ <strong>สร้างประเภทตั๋ว</strong> ติ๊กช่องนั้น แล้วตั้งรหัสนำหน้า 4 หลัก
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'grid', gap: 20 }}>
      <div>
        <h3 style={{ margin: '0 0 10px' }}>โควตาแยกตามประเภทบัตร</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(320px,1fr))', gap: 14 }}>
          {summary.map((s) => <QuotaCard key={s.ticketId} s={s} />)}
        </div>
      </div>

      <div className="panel" style={{ padding: 16 }}>
        <h3 style={{ marginTop: 0 }}>ขายบัตรทีละใบ</h3>
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <label style={{ display: 'grid', gap: 4, fontSize: 12 }}>
            เลขบัตร
            <input value={serial} onChange={(e) => setSerial(e.target.value.toUpperCase())} placeholder="MPAS-0001" style={{ width: 170, fontFamily: 'var(--mono)' }} />
          </label>
          <button type="button" className="btn-primary" disabled={busy || !serial.trim()} onClick={activateOne}>เปิดใช้งาน</button>
        </div>
        <div style={{ marginTop: 10, fontSize: 12, color: 'var(--muted)' }}>
          หน้าจุดจำหน่ายสำหรับพนักงานอยู่ที่ <code>/staff/pos</code> — ออก Token ประเภท "จุดจำหน่าย" ได้ที่หน้า ผู้ใช้ &amp; Token
        </div>
      </div>

      {msg && <div className="panel" style={{ padding: 12, fontSize: 13 }}>{msg}</div>}

      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 14, flexWrap: 'wrap', marginBottom: 10 }}>
          <h3 style={{ margin: 0 }}>ล็อตที่พิมพ์แล้ว</h3>
          <button type="button" className="btn-primary" onClick={() => setPrinting(true)}>+ สั่งพิมพ์ล็อตใหม่</button>
        </div>

        <div className="panel" style={{ padding: 0, overflow: 'hidden' }}>
          {batches.length === 0 ? (
            <div style={{ color: 'var(--muted)', fontSize: 13, padding: 16 }}>ยังไม่มีล็อต</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table className="admin-table" style={{ minWidth: 700 }}>
                <thead>
                  <tr>
                    <th>ล็อต</th><th>สถานะ</th>
                    <th style={{ textAlign: 'right' }}>ยังไม่ขาย</th>
                    <th style={{ textAlign: 'right' }}>ขายแล้ว</th>
                    <th style={{ textAlign: 'right' }}>ยกเลิก</th>
                    <th style={{ textAlign: 'right' }}>จัดการ</th>
                  </tr>
                </thead>
                <tbody>
                  {batches.map((b) => {
                    const dead = Boolean(b.voided_at);
                    return [
                      <tr key={b.id} style={dead ? { color: 'var(--dim)' } : undefined}>
                        <td>
                          <button type="button" className="btn-link" onClick={() => openRun(b)} style={{ textAlign: 'left', padding: 0 }}
                            aria-expanded={openId === b.id} aria-label={`ดูเลขบัตรของ ${nameOf(b.ticket_id)} พิมพ์ครั้งที่ ${b.run_no}`}>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                              <span style={{ fontFamily: 'var(--mono)', fontSize: 12, fontWeight: 600, width: 28, height: 28, display: 'grid', placeItems: 'center', borderRadius: 7, border: '1px solid var(--border)', opacity: dead ? .55 : 1 }}>
                                {b.run_no}
                              </span>
                              <span>
                                <span style={{ fontWeight: dead ? 400 : 500 }}>{nameOf(b.ticket_id)} · พิมพ์ครั้งที่ {b.run_no}</span>
                                <span style={{ display: 'block', fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--dim)' }}>
                                  {b.label} · {b.serial_prefix} · {b.quantity} ใบ
                                </span>
                              </span>
                            </span>
                          </button>
                        </td>
                        <td style={{ fontSize: 12 }}>
                          {dead ? `ยกเลิกแล้ว · คืนโควตา ${b.voided}` : b.sold > 0 ? 'ขายไปบางส่วน' : 'สต็อกเต็มล็อต'}
                        </td>
                        <td style={{ textAlign: 'right', fontFamily: 'var(--mono)' }}>{b.unsold}</td>
                        <td style={{ textAlign: 'right', fontFamily: 'var(--mono)' }}>{b.sold}</td>
                        <td style={{ textAlign: 'right', fontFamily: 'var(--mono)' }}>{b.voided}</td>
                        <td style={{ textAlign: 'right' }}>
                          <span style={{ display: 'inline-flex', gap: 5 }}>
                            <RunTool kind="zip" title="ดาวน์โหลด ZIP" disabled={busy} onClick={() => download(b)} />
                            <RunTool kind="activate" title={dead ? 'ล็อตนี้ถูกยกเลิกแล้ว' : b.unsold === 0 ? 'ไม่มีบัตรที่ยังไม่ขาย' : 'เปิดใช้งานทั้งล็อต'}
                              disabled={busy || dead || b.unsold === 0} onClick={() => activateBatch(b)} />
                            <RunTool kind="void" title={dead ? 'ล็อตนี้ถูกยกเลิกแล้ว' : 'ยกเลิกล็อต'}
                              disabled={busy || dead} onClick={() => voidBatch(b)} />
                          </span>
                          {activating?.id === b.id && (
                            <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>
                              {activating.done} ใบ · เหลือ {activating.left}
                            </div>
                          )}
                        </td>
                      </tr>,
                      openId === b.id ? (
                        <tr key={`${b.id}-cards`}>
                          <td colSpan={6} style={{ background: 'var(--bg)' }}>
                            {cardsLoading ? (
                              <div style={{ fontSize: 13, color: 'var(--muted)' }}>กำลังโหลดเลขบัตร…</div>
                            ) : (
                              <div style={{ display: 'grid', gap: 12 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
                                  <strong style={{ fontSize: 13 }}>เลขบัตรในล็อตนี้ <span style={{ color: 'var(--dim)', fontWeight: 400 }}>{cards.length} ใบ</span></strong>
                                  <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                                    {([
                                      ['all', `ทั้งหมด ${cards.length}`],
                                      ['sold', `เปิดใช้งานแล้ว ${cards.filter((c) => c.status === 'sold').length}`],
                                      ['unsold', `ยังไม่เปิด ${cards.filter((c) => c.status === 'unsold').length}`],
                                      ['void', `ยกเลิก ${cards.filter((c) => c.status === 'void').length}`],
                                    ] as const).map(([k, lbl]) => (
                                      <button key={k} type="button" className={filter === k ? 'btn-primary' : 'btn-ghost'}
                                        style={{ fontSize: 11.5, padding: '4px 10px' }} onClick={() => setFilter(k)}>{lbl}</button>
                                    ))}
                                  </div>
                                </div>

                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(160px,1fr))', gap: 6, maxHeight: 320, overflowY: 'auto' }}>
                                  {shown.map((c) => {
                                    const colour = c.status === 'sold' ? '#6fbf8e' : c.status === 'void' ? 'var(--dim)' : '#d9b36a';
                                    return (
                                      <label key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontFamily: 'var(--mono)', fontSize: 12, padding: '7px 9px', border: '1px solid var(--border)', borderRadius: 6, color: colour, cursor: c.status === 'void' ? 'not-allowed' : 'pointer' }}>
                                        <input type="checkbox" checked={picked.has(c.id)} disabled={c.status === 'void'}
                                          onChange={(e) => setPicked((prev) => {
                                            const next = new Set(prev);
                                            if (e.target.checked) next.add(c.id); else next.delete(c.id);
                                            return next;
                                          })} />
                                        <span style={{ textDecoration: c.status === 'void' ? 'line-through' : undefined }}>{c.serial}</span>
                                      </label>
                                    );
                                  })}
                                </div>

                                {picked.size > 0 && (
                                  <div className="panel" style={{ padding: '11px 13px', display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                                    <span style={{ fontSize: 13, fontWeight: 600 }}>เลือกไว้ {picked.size} ใบ</span>
                                    <button type="button" className="btn-link" onClick={() => setPicked(new Set(shown.filter((c) => c.status !== 'void').map((c) => c.id)))}>เลือกทั้งหมด</button>
                                    <button type="button" className="btn-link" onClick={() => setPicked(new Set())}>ล้างที่เลือก</button>
                                    <span style={{ flex: 1 }} />
                                    <button type="button" className="btn-primary" disabled={busy || canOpen === 0} onClick={() => moveSelected('on')}>
                                      เปิดใช้งาน{canOpen ? ` (${canOpen})` : ''}
                                    </button>
                                    <button type="button" className="btn-ghost" disabled={busy || canClose === 0} onClick={() => moveSelected('off')}>
                                      ปิดใช้งาน{canClose ? ` (${canClose})` : ''}
                                    </button>
                                    <div style={{ width: '100%', fontSize: 11.5, color: 'var(--dim)' }}>
                                      {canOpen > 0 && canClose > 0
                                        ? 'เลือกไว้ทั้งที่เปิดแล้วและยังไม่เปิด — แต่ละปุ่มจะทำเฉพาะใบที่ทำได้'
                                        : canClose > 0
                                          ? 'ปิดใช้งานจะยกเลิกคำสั่งซื้อที่ผูกกับบัตรใบนั้น และคืนบัตรกลับเป็นสต็อก · บัตรที่เช็คอินแล้วปิดไม่ได้'
                                          : ''}
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                          </td>
                        </tr>
                      ) : null,
                    ];
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {printing && (
        <PrintRunModal types={offlineTypes} batches={batches} busy={busy}
          onClose={() => setPrinting(false)} onPrint={create} />
      )}
    </div>
  );
}
